/**
 * CLOSE OUT v4 — Phase A (CLOSEOUT-REDESIGN.md, approved mockup
 * mockups/closeout-workflow-v4.html — deviations from the mockup are bugs).
 *
 * Parallel route: the existing /office-review page is untouched; this page
 * reads the new /api/close-out endpoints. Phase A shipped the queue (blocking-
 * station filters), the job shell (context bar + five-station strip), and
 * stations 1–2; Phases B–C have since shipped stations 3–5 for real (estimate
 * triangle + Corbin gate, payment, send-off) — all five stations are live.
 *
 * Screen-size contract (decision 8): columns collapse, never clip — queue is
 * 2-col, 3-col at 2xl (1536px+), 1-col under md; ultra-wide docks the queue as a
 * sticky rail beside the job. No positioned elements that can fall
 * off-canvas.
 */
import { RemovedChoicesWarning } from "@/components/removed-choices-warning";
import { LaborCrewStepper, type LaborShape } from "@/components/labor-crew-stepper";
import { JobCustomerCheck, MoveJobLink } from "@/components/move-job-customer";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BudgetImpactLine } from "@/pages/customers/budget/impact-line";
import { api, ApiError, attachmentDownloadUrl } from "@/lib/api";
import { photoCaption } from "@/lib/photo-label";
import { RELEASE_LABELS, releaseCategoryLabel } from "@/pages/field/release-proof";
import { DeclinedCardNextSteps, type CardGuard } from "@/components/declined-card-next-steps";
import {
    CardHoldHowItWorks,
    HoldChip,
    fromHoldMessage,
    holdChargeWords,
    type ChargeHold,
    type FromHold,
} from "@/components/card-hold/card-hold";
import { isWorkOrderLooking } from "@/lib/signed-work-order";
import { useWoRegenerate } from "@/lib/use-wo-regenerate";
import { WoRegenerateStatusLine } from "./wo-regenerate-status-line";
import { reviewPromptFromBody, type ReviewPrompt } from "./reviewPrompt";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ReportProblemsBanner } from "./report-problems-banner";
import {
    NotPricedYet,
    RepairRuleStrip,
    TechAddedNeedsOkBanner,
    notPricedLabel,
    ruleWorthShowing,
    type CloseoutRuleView,
    type TechAddedNeedsOk,
} from "./repair-rule-strip";
import { CityEmailPrompt } from "./city-email-prompt";
import { FormVerdictBanner, type FormVerdict as FormMismatch } from "./form-verdict-banner";
import { Dialog } from "@/components/ui/dialog";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { DocumentPreviewDialog } from "@/components/document-preview-dialog";
import { ReportInputFlag } from "@/components/report-input-flag";
import { CloseoutPdfMarkup } from "@/components/closeout-pdf-markup";
import { CloseoutDocViewer } from "@/components/closeout-doc-viewer";
import { CloseoutLearningDigest } from "@/components/closeout-learning-digest";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import CardOnFilePanel from "@/components/card-on-file-panel";
import { PricebookLinePicker } from "@/components/pricebook-line-picker";
import { DroppedAttachmentsWarning, type DroppedAttachment } from "@/components/dropped-attachments-warning";
import { PacketSizeNotice, type LeftOutFile, type PacketSizePlan } from "@/components/packet-size-notice";
import {
    StaffOnlySendGate,
    noCustomerEmailRefusal,
    type NoCustomerEmailRefusal,
} from "@/components/staff-only-send-gate";
import { SentAnotherWayDialog } from "@/components/sent-another-way-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    SendRecipientEditor,
    summarizeSendPlan,
    docsSummary,
    type PlanRecipient,
    type PacketDoc,
} from "@/components/send-recipient-editor";
import {
    AlertTriangle,
    CalendarPlus,
    Camera,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Download,
    FileCheck2,
    GitCompare,
    Loader2,
    Lock,
    Mail,
    Maximize2,
    Minimize2,
    Pencil,
    Plus,
    Printer,
    RefreshCw,
    Sparkles,
    Upload,
    Eye,
    Trash2,
    UserPlus,
    UserRound,
    X,
} from "lucide-react";
import { usePageContextReporter } from "@/lib/page-context";
import { stationForTopic, useBallSwitch, useWhoseTurn, type Ball, type BallHandoff } from "@/lib/closeout-ball";
import { HandToButton, HandToDialog, HandoffBanner } from "@/components/closeout/handoff";
import {
    ClaimStrip,
    DoneSendBack,
    TurnChip,
    TurnLanes,
    TurnLine,
    WhoseTurnSwitch,
    useClaim,
} from "@/components/closeout/whose-turn";
import {
    BallChip,
    BallLine,
    BallRail,
    BallSwitch,
    ChangeToCouldNotTest,
    JobNoteStar,
    JobNotesDialog,
    NoteLine,
    WhyStrip,
    NeedsNewQuote,
    NoReturnReplacementQuoted,
    QuoteSendChip,
    QuoteTrail,
    quoteTrail,
    type CantTestDevice,
    type QuoteAutoSend,
} from "./ball";
import { CloseOutList, StarButton, StarDialog, type QueueJob, type QueueResponse, type Staff } from "./queue-list";
import { TeachIntro } from "@/teach/teach-card";
import { deriveBandModel } from "./bandModel";
import { CLOSE_OUT_QUEUE_KEY, refreshCloseOutQueue } from "./queueRefresh";
import { deriveSendGate } from "./sendGate";
import { useAuth } from "@/lib/auth";
import { PermitCard } from "@/pages/jobs/detail/permit-card";
import { useMakeOfficial, type ReportDeviceCandidate } from "@/components/report-device-picker";
import { testStateLabel, testStateTone } from "@/lib/test-state";
import { QuoteCheckStrip } from "./quote-check-strip";
import { WaitingOnApproverText } from "@/components/waiting-on-approver-text";
import { EmptyQuoteBanner } from "./empty-quote-banner";
import { ApprovedEditWarning, EditApprovedConfirm, SecondLookBar } from "@/components/quote-second-look";
import { WoWaitingChip } from "./wo-waiting";
import { ReportsWithoutTestsGuard, type ReportsWithoutTests } from "./reports-without-tests";

/** One look per result (lib/test-state.ts). Couldn't test / not finished /
 *  not known are grey, never a green PASS. */
const RESULT_PILL: Record<"pass" | "fail" | "neutral", string> = {
    pass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
    fail: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
    neutral: "bg-muted text-foreground/70",
};

/**
 * Measure an element's top edge to the viewport bottom and return that as a
 * pixel height, so the document-review pane can fill the screen exactly —
 * regardless of the shell chrome above it (header, breadcrumbs) or whether
 * the stepper is collapsed. Recomputes on resize and when `collapsed` flips.
 */
function useFillHeight(collapsed: boolean, active: boolean) {
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);
    useLayoutEffect(() => {
        if (!active) {
            setHeight(undefined);
            return;
        }
        const el = ref.current;
        if (!el) return;
        const compute = () => {
            const top = el.getBoundingClientRect().top;
            setHeight(Math.max(360, Math.round(window.innerHeight - top - 12)));
        };
        compute();
        const raf = requestAnimationFrame(compute);
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("resize", compute);
            cancelAnimationFrame(raf);
        };
    }, [collapsed, active]);
    return { ref, height };
}

// ─── Types (mirror closeOutWorkspace.ts) ────────────────────────────────

// Who or what completed a station — a named human (reviewer, biller, sender)
// or the right process (agency report, Stripe). Mirrors closeoutStepCredit.ts.
interface StepCredit {
    by: string;
    kind: "person" | "process";
}
type StepCreditMap = Partial<Record<1 | 2 | 4 | 5, StepCredit | null>>;

interface CloseOutDocument {
    id: string;
    file_name: string | null;
    /** Bytes — used to spot a duplicate attachment (same name + size = same file). */
    file_size: number | null;
    category: string;
    mime_type: string | null;
    asset_id: string | null;
    notes: string | null;
    reviewed_at: string | null;
    reviewed_by_name: string | null;
    // Official reports pulled from an online reporting agency and matched to a
    // tested device don't need office review — the server auto-credits them so
    // the office only eyeballs forms mapped in this program.
    autoReviewed?: boolean;
    serial_number: string | null;
    assembly_type: string | null;
    size_label: string | null;
}

interface CloseOutPhoto {
    id: string;
    file_name: string | null;
    asset_id?: string | null;
    photo_tag?: string | null;
    mime_type?: string | null;
    /** Which device it shows: `2" Double Check · SN 40613G · Tag photo` (server-built). */
    photo_label?: string | null;
}

// An open "Corbin fix" flag on this job (raised from "Something's wrong…").
// While one exists, station 1 blocks as "Report fix — with Corbin"; the
// Documents station surfaces it with a resolve-in-place control.
interface CloseOutDocFix {
    id: string;
    title: string;
    notes: string | null;
    created_at: string;
}

// Per-device tech remarks surfaced on Charges/Estimate so the office can verify
// what the tech reported is covered by what's being invoiced.
interface TechRemarkGroup {
    asset_id: string | null;
    device: string;
    overall_result: string | null;
    remarks: string[];
}

interface CityInfo {
    id: string;
    name: string;
    submission_email: string | null;
    portal_only: boolean;
}

// The report was generated on a jurisdiction form that no longer matches the
// site's water provider (e.g. provider corrected to City of McKinney after the
// report was built on the TCEQ default). Drives the "reformat" banner on the
// Documents station; the reformat reruns generation from the structured data.
interface ChargesWorkRow {
    asset_id: string;
    overall_result: string | null;
    serial_number: string | null;
    assembly_type: string | null;
    size_label: string | null;
    manufacturer: string | null;
    model: string | null;
    report_reviewed: boolean;
    // Tech-added-device state (job_assets). A device the tech found that
    // wasn't on the original work order is `added_by: 'tech'` and starts with
    // no decision (null) until the office rules — billing it approves, "don't
    // bill" declines. Office-scheduled devices carry added_by !== 'tech'.
    link_id: string | null;
    added_by: string | null;
    office_review_decision: "approved" | "denied" | "pending" | null;
}

interface ChargesInvoice {
    id: string;
    status: string;
    invoice_number: string | null;
    total_amount: string;
    title: string | null;
    notes: string | null;
    amount_paid: string;
}

interface ChargesLine {
    id: string;
    invoice_id: string;
    description: string | null;
    qty: number;
    unit_price: number;
    unit_cost: number | null;
    asset_id: string | null;
    /** Labor line hours × techs × rate (#1823 S3); null on other lines. */
    labor?: LaborShape | null;
}

// ─── Scanner lanes: three SEPARATE color-coded viewers (decision 5) ─────
// photos gray / reports indigo / WO orange — never blended.
type ScannerLane = "reports" | "wo" | "photos";
const LANE_STYLE: Record<ScannerLane, { bar: string; title: string }> = {
    reports: { bar: "bg-indigo-700", title: "TEST REPORTS" },
    wo: { bar: "bg-orange-900", title: "WORK ORDER" },
    photos: { bar: "bg-zinc-600", title: "PHOTOS" },
};

const STATION_META: Array<{ n: 1 | 2 | 3 | 4 | 5; title: string; phase: "A" | "B" | "C" }> = [
    { n: 1, title: "Documents", phase: "A" },
    { n: 2, title: "Charges", phase: "A" },
    { n: 3, title: "Estimate", phase: "B" },
    { n: 4, title: "Payment", phase: "C" },
    { n: 5, title: "Customer", phase: "C" },
];

function deviceLabel(d: { assembly_type?: string | null; size_label?: string | null; serial_number?: string | null }) {
    return (
        [d.assembly_type ? String(d.assembly_type).toUpperCase() : null, d.size_label, d.serial_number]
            .filter(Boolean)
            .join(" · ") || "device"
    );
}

export default function CloseOutPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const jobId = searchParams.get("job");
    const stationParam = Number(searchParams.get("station") || 0);

    const queue = useQuery({
        queryKey: CLOSE_OUT_QUEUE_KEY,
        queryFn: () => api.get<QueueResponse>("/api/close-out/queue"),
    });
    const jobs = queue.data?.jobs ?? [];
    const selected = jobs.find((j) => j.id === jobId) ?? null;
    // #1703 — tell the Help Desk what's on screen (the 2026-10-01 test drive:
    // on a Closeout quote it asked "do you have the estimate number?").
    const stationNames = ["", "Documents", "Charges", "Estimate", "Payment", "Customer"];
    usePageContextReporter(
        jobId
            ? {
                  label: selected
                      ? `Close Out — Job #${selected.display_number ?? "?"}${selected.company_name ? ` (${selected.company_name})` : ""}`
                      : "Close Out — a job",
                  entity: { type: "jobs", id: jobId },
                  activeTab: stationNames[stationParam] || undefined,
                  values: selected ? { customer: selected.company_name, job_title: selected.title } : undefined,
              }
            : null,
    );

    const setJob = (id: string | null, station?: number) =>
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (id) next.set("job", id);
                else next.delete("job");
                if (id && station) next.set("station", String(station));
                else next.delete("station");
                return next;
            },
            { replace: true },
        );

    return (
        <div className={cn("mx-auto w-full max-w-[1560px] px-3", jobId ? "pb-2" : "pb-16")}>
            {/* Title + subtitle are redundant while a job is open — reclaim the
                height for the document viewer. */}
            {!jobId && (
                <>
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-lg font-bold tracking-tight text-shell-foreground">Closeout</h1>
                        <ListBallSwitch />
                        {/* WO rework slice 2: every work order not yet signed,
                            checked or sent, sorted by who has the ball. */}
                        <WoWaitingChip dark />
                    </div>
                    <p className="mb-1 text-[12.5px] text-shell-foreground/80">
                        Calm by default, loud on a problem &mdash; a card&apos;s spine and pills say what needs a hand
                        before you open it.
                    </p>
                    {/* #832 C3: each queue states its scope — this page, Office
                        Review, and the Jobs "Needs Review" stage showed four
                        different numbers for close-out work with no explanation. */}
                    <p className="mb-3 text-[11px] text-shell-foreground/60" data-testid="queue-scope-line">
                        This queue: jobs with paperwork or billing still to finish. A job leaves once its packet is sent
                        and the money is settled. The toolbar&apos;s Closeout chip counts exactly this queue; Office
                        Review (checks &amp; flags) overlaps it.
                        {jobs.length >= 200 && (
                            <span className="font-semibold text-amber-500">
                                {" "}
                                Showing the oldest 200 — there are more.
                            </span>
                        )}
                    </p>
                </>
            )}
            {jobId ? (
                <JobView
                    jobId={jobId}
                    queueJob={selected}
                    jobs={jobs}
                    staff={queue.data?.staff ?? []}
                    currentUserId={queue.data?.currentUserId ?? null}
                    stationParam={stationParam}
                    onBack={() => setJob(null)}
                    onJump={(id) => setJob(id)}
                    onStation={(n) => setJob(jobId, n)}
                />
            ) : (
                <CloseOutList
                    data={queue.data}
                    isLoading={queue.isLoading}
                    onOpen={(id, station) => setJob(id, station)}
                />
            )}
        </div>
    );
}

/** The per-person "Try who has the ball" switch on the queue page. */
function ListBallSwitch() {
    const sw = useBallSwitch();
    return (
        <>
            <BallSwitch on={sw.on} onChange={sw.set} forEveryone={sw.forEveryone} dark />
            <WhoseTurnSwitch dark />
            {/* One-time intro, 10/16 (teach rules: office only, once, one per page load). */}
            <TeachIntro featureKey="closeout.notes-shared" when={sw.on} />
        </>
    );
}

// ═══ JOB VIEW ═══════════════════════════════════════════════════════════

function JobView({
    jobId,
    queueJob,
    jobs,
    staff,
    currentUserId,
    stationParam,
    onBack,
    onJump,
    onStation,
}: {
    jobId: string;
    queueJob: QueueJob | null;
    jobs: QueueJob[];
    staff: Staff[];
    currentUserId: string | null;
    stationParam: number;
    onBack: () => void;
    onJump: (id: string) => void;
    onStation: (n: number) => void;
}) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    // Flag / hand-off can be opened from the context bar at any point in the
    // review — you no longer have to bounce back to the queue to star a job.
    const [starOpen, setStarOpen] = useState(false);

    // Open at the first incomplete station unless the URL pins one.
    const station = stationParam >= 1 && stationParam <= 5 ? stationParam : (queueJob?.blocking.station ?? 1);

    // #770: the context band is ALWAYS visible — the v4 mockup contract shows
    // identity + stations at all times, and the collapsed-by-default stepper
    // (old `closeout_stepper_collapsed` preference, now retired) was hiding
    // exactly the "what needs me / where's the money" answers the audit said
    // were missing. The band is slim enough that the Documents PDF still gets
    // the majority of the viewport (useFillHeight measures below it).
    const isDocs = station === 1;
    const { ref: fillRef, height: fillHeight } = useFillHeight(false, isDocs);
    const band = deriveBandModel(queueJob);

    // The Estimate station only applies when something failed or an estimate
    // exists — a clean pass has nothing to estimate, so hide the pill. Count
    // (not total) is the reliable signal: the office can add a blank estimate
    // from Charges whose total is $0, and that must still reveal the station.
    // (Server never blocks on station 3 without a draft estimate, so this is
    // purely presentational.)
    const showEstimate = queueJob
        ? queueJob.failed_devices > 0 ||
          (queueJob.draft_estimate_count ?? 0) > 0 ||
          Number(queueJob.draft_estimate_total ?? 0) > 0
        : true;
    const stations = STATION_META.filter((s) => s.n !== 3 || showEstimate);

    // Who/what completed each step — real credit in place of a flat "done".
    // Isolated per-job read (see /close-out/jobs/:id/step-credit); if it fails
    // the strip simply falls back to "done", never blocking the workspace.
    const stepCredit = useQuery({
        queryKey: ["close-out-step-credit", jobId],
        queryFn: () => api.get<{ credits: StepCreditMap }>(`/api/close-out/jobs/${jobId}/step-credit`),
    });
    const credits = stepCredit.data?.credits;

    // "Next" jumps to the station that actually needs work next (the server's
    // blocking station), skipping any already done or not applicable to this
    // job (e.g. Estimate on a clean pass). Falls back to the first station
    // after Documents when the job is already all-clear.
    const firstAfterDocs = stations.find((s) => s.n > 1)?.n ?? 2;
    const nextNeededStation = queueJob && queueJob.blocking.station > 1 ? queueJob.blocking.station : firstAfterDocs;
    const nextNeededTitle = STATION_META.find((s) => s.n === nextNeededStation)?.title ?? "Charges";
    const ballSwitch = useBallSwitch();
    const ballOn = ballSwitch.on;
    const turnOn = useWhoseTurn().on && ballOn;
    // Whose turn (pick 4): opening a job from the shared pile claims it.
    const claimedBy = useClaim(jobId, queueJob?.turn, turnOn);
    const { isAdmin } = useAuth();
    // Who has the ball, 10/16: tell the server this job was opened, once per
    // job per visit, so "New — not looked at yet" clears for everyone.
    // Hand to…: opening the job marks an answer you got back as seen. The
    // server hands it back once so the header keeps showing it this visit.
    const [seenReply, setSeenReply] = useState<BallHandoff | null>(null);
    useEffect(() => {
        setSeenReply(null);
        if (!ballOn || !jobId) return;
        api.post<{ seenReplies?: BallHandoff[] }>(`/api/close-out/jobs/${jobId}/opened`, {})
            .then((r) => {
                setSeenReply(r?.seenReplies?.[0] ?? null);
                refreshCloseOutQueue(qc, { jobId });
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ballOn, jobId]);
    // Rail card ⋯ menu → Hand to… (threads/hand-it-off).
    const [railMenuFor, setRailMenuFor] = useState<string | null>(null);
    const [railHandFor, setRailHandFor] = useState<QueueJob | null>(null);
    const jobNameOf = (j: QueueJob | null) => j?.site_name || j?.company_name || j?.title || "this job";
    const headerHandoff = queueJob?.ball?.handoff ?? seenReply;

    // One rail card. With "Try who has the ball" on it carries one extra
    // line: who holds it and for how long (amber dot after 2 work days).
    const railCard = (j: QueueJob) => (
        <div key={j.id} className="relative">
            <button
                onClick={() => onJump(j.id)}
                className={cn(
                    "mb-1.5 block w-full rounded-lg border bg-card px-2.5 py-2 pr-7 text-left text-xs",
                    j.id === jobId
                        ? "border-teal-700 shadow-[0_0_0_2px_rgba(15,118,110,.14)]"
                        : "border-border hover:border-teal-700",
                )}
            >
                {/* M10 (#834): lead with the site, not the billing account —
                            five "Cintas Fire National Account" entries are identical
                            without the site/address line. Same rule as QueueCard. */}
                <div className="truncate font-bold">{j.site_name || j.company_name || j.title || "Job"}</div>
                {(j.service_address || (j.company_name && j.company_name !== j.site_name && j.site_name)) && (
                    <div className="mt-0.5 truncate text-[10.5px] text-muted-foreground">
                        {j.service_address || j.company_name}
                    </div>
                )}
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-foreground/60">
                    <span
                        className={cn(
                            "flex h-[15px] w-[15px] flex-none items-center justify-center rounded-full text-[9px] font-extrabold",
                            j.blocking.group === "send"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
                        )}
                    >
                        {j.blocking.station}
                    </span>
                    <span className="truncate">
                        {j.blocking.label}
                        {j.site_city ? ` · ${j.site_city}` : ""}
                    </span>
                </div>
                {turnOn && j.turn ? <TurnLine turn={j.turn} /> : ballSwitch.on && j.ball && <BallLine ball={j.ball} />}
                {ballSwitch.on && <NoteLine notes={j.notes} />}
            </button>
            {ballSwitch.on && (
                <DoneSendBack
                    jobId={j.id}
                    handoff={j.ball?.handoff}
                    viewerId={currentUserId}
                    className="-mt-1 mb-1.5 w-full"
                />
            )}
            {ballSwitch.on && (
                <>
                    <button
                        type="button"
                        aria-label="More for this job"
                        onClick={() => setRailMenuFor((v) => (v === j.id ? null : j.id))}
                        className="absolute right-1 top-1 rounded px-1.5 text-[15px] leading-none text-foreground/50 hover:bg-muted"
                        data-testid="rail-card-menu"
                    >
                        ⋯
                    </button>
                    {railMenuFor === j.id && (
                        <div className="absolute right-1 top-6 z-20 rounded-lg border border-border bg-card p-1 text-xs shadow-lg">
                            <button
                                type="button"
                                disabled={j.ball?.handoff?.state === "open"}
                                onClick={() => {
                                    setRailMenuFor(null);
                                    setRailHandFor(j);
                                }}
                                className="block w-full whitespace-nowrap rounded px-2.5 py-1.5 text-left font-bold text-pink-800 hover:bg-pink-50 disabled:opacity-50 dark:text-pink-200 dark:hover:bg-pink-950/40"
                            >
                                {j.ball?.handoff?.state === "open"
                                    ? `Already with ${j.ball.handoff.toName}`
                                    : "Hand to…"}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className="grid grid-cols-1 items-start gap-2.5 2xl:grid-cols-[250px_minmax(0,1fr)]">
            {/* Ultra-wide: queue rides along as a sticky rail (decision 8). */}
            <aside className="hidden 2xl:block sticky top-2.5">
                <div className="mb-1.5 flex items-center justify-between gap-2 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-shell-foreground/80">
                    <span>Queue — rides along when wide</span>
                </div>
                <div className="mb-2">
                    <BallSwitch
                        on={ballSwitch.on}
                        onChange={ballSwitch.set}
                        forEveryone={ballSwitch.forEveryone}
                        dark
                    />
                    <WhoseTurnSwitch dark />
                </div>
                {turnOn ? (
                    <TurnLanes jobs={jobs} renderCard={(j) => railCard(j)} variant="rail" selectedId={jobId} />
                ) : ballSwitch.on ? (
                    <BallRail
                        jobs={jobs}
                        selectedId={jobId}
                        renderCard={(j) => railCard(j)}
                        isApprover={isAdmin}
                        onOpen={onJump}
                    />
                ) : (
                    jobs.slice(0, 12).map((j) => railCard(j))
                )}
                <div className="mt-2 text-[10.5px] font-semibold text-shell-foreground/60">
                    Work one job start-to-finish, then take the next — jumping between jobs is how payments and packets
                    get crossed.
                </div>
            </aside>

            <div
                ref={fillRef}
                className={cn("min-w-0", isDocs && fillHeight != null && "flex flex-col")}
                style={isDocs && fillHeight != null ? { height: fillHeight } : undefined}
            >
                {/* #770 context band — always visible, every breakpoint. Row 1:
                    identity. Row 2: THE STORY (why the dollar amounts exist).
                    Row 3: NEEDED TO FINISH + money trio. Row 4: station strip. */}
                <div
                    className={cn(
                        // eslint-disable-next-line no-restricted-syntax -- D1: floats over scrolling content; the blur is load-bearing
                        "z-10 rounded-xl border border-border bg-card/95 shadow-sm backdrop-blur-sm",
                        isDocs ? "shrink-0 sticky top-0 mb-2" : "mb-2.5",
                    )}
                >
                    {
                        <>
                            <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-3.5 py-1.5">
                                <button onClick={onBack} className="whitespace-nowrap text-xs text-foreground/60">
                                    ← Queue ({jobs.length})
                                </button>
                                {ballOn ? (
                                    <JobNoteStar notes={queueJob?.notes} onClick={() => setStarOpen(true)} />
                                ) : (
                                    <StarButton star={queueJob?.star ?? null} onClick={() => setStarOpen(true)} />
                                )}
                                <span className="text-sm font-bold">
                                    {queueJob?.company_name || queueJob?.title || "Job"}
                                </span>
                                {/* #1897 — fix a job built under the wrong customer, right here. */}
                                <MoveJobLink jobId={jobId} />
                                {(queueJob?.service_address || queueJob?.site_name) && (
                                    <span className="hidden min-w-0 truncate text-[11.5px] text-foreground/60 md:inline">
                                        📍 {queueJob?.service_address || queueJob?.site_name}
                                    </span>
                                )}
                                {queueJob?.site_city && (
                                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10.5px] font-bold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                                        {queueJob.site_city}
                                        {queueJob.portal_name ? ` · ${queueJob.portal_name}` : ""}
                                    </span>
                                )}
                                <div className="ml-auto flex items-center gap-1.5">
                                    {ballOn && queueJob && (
                                        <HandToButton
                                            jobId={jobId}
                                            jobName={jobNameOf(queueJob)}
                                            disabled={queueJob.ball?.handoff?.state === "open"}
                                            asks={queueJob.asks}
                                        />
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => navigate(`/jobs/${jobId}`)}>
                                        View job
                                    </Button>
                                </div>
                            </div>
                            {/* Hand to…: the ask (or the answer) sits right under the job name. */}
                            {ballOn && headerHandoff && (
                                <HandoffBanner
                                    jobId={jobId}
                                    handoff={headerHandoff}
                                    viewerId={currentUserId}
                                    onGotIt={
                                        headerHandoff.state === "answered" && headerHandoff.fromUserId === currentUserId
                                            ? () => setSeenReply(null)
                                            : undefined
                                    }
                                />
                            )}
                            {/* "Why isn't this out yet?" — the specific blocker and
                                the newest shared note, in one line (10/16). */}
                            {turnOn && <ClaimStrip name={claimedBy} />}
                            {ballOn && queueJob && <WhyStrip job={queueJob} onNotes={() => setStarOpen(true)} />}
                            {/* #1897 — the paper names another vendor / moved today, Undo. */}
                            <div className="border-b border-border px-3.5 py-2 empty:hidden">
                                <JobCustomerCheck jobId={jobId} compact />
                            </div>
                            {band && band.story && (
                                <div className="flex items-baseline gap-2 border-b border-border bg-muted/30 px-3.5 py-1.5 text-[12px]">
                                    <span className="flex-none text-[9.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
                                        The story
                                    </span>
                                    <span className="min-w-0 text-foreground/80">{band.story}</span>
                                </div>
                            )}
                            {band && (
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border px-3.5 py-1.5">
                                    <span className="flex flex-wrap items-center gap-1.5">
                                        <span className="flex-none text-[9.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
                                            Needed to finish
                                        </span>
                                        {/* Who has the ball: the holder in plain words leads. */}
                                        {ballOn && queueJob?.ball && turnOn && queueJob.turn ? (
                                            <TurnChip
                                                turn={queueJob.turn}
                                                onClick={() => onStation(stationForTopic(queueJob.ball!.topic))}
                                            />
                                        ) : (
                                            ballOn &&
                                            queueJob?.ball && (
                                                <BallChip
                                                    ball={queueJob.ball}
                                                    onClick={() => onStation(stationForTopic(queueJob.ball!.topic))}
                                                />
                                            )
                                        )}
                                        {band.needs.map((n) => (
                                            <button
                                                key={n.station}
                                                onClick={() => onStation(n.station)}
                                                className={cn(
                                                    "rounded-full px-2 py-0.5 text-[10.5px] font-bold",
                                                    n.state === "current"
                                                        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
                                                        : n.state === "done"
                                                          ? "bg-emerald-50 text-emerald-700 line-through decoration-1 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                          : "bg-muted text-foreground/55",
                                                )}
                                            >
                                                {n.label}
                                            </button>
                                        ))}
                                    </span>
                                    {(band.money.invoice || band.money.estimate || band.money.due) && (
                                        <span className="ml-auto flex flex-none items-center gap-3 text-[11.5px] font-semibold tabular-nums">
                                            <span className="text-[9.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
                                                Money
                                            </span>
                                            <span>
                                                Invoice <b>{band.money.invoice ?? "—"}</b>
                                            </span>
                                            <span>
                                                Estimate <b>{band.money.estimate ?? "—"}</b>
                                            </span>
                                            <span>
                                                Due <b>{band.money.due ?? "—"}</b>
                                            </span>
                                        </span>
                                    )}
                                </div>
                            )}
                            {/* Station strip — stations that apply to this job.
                                #757: the badge shows the FIXED station number
                                (1–5) so the strip, the queue cards' circled
                                numbers, and the workspace all speak one scheme;
                                a not-applicable station renders as a muted
                                "not needed" row rather than renumbering the
                                rest. */}
                            <div className="flex overflow-x-auto px-1.5">
                                {STATION_META.map((s) => {
                                    // #757: station numbers are STABLE (1–5 everywhere —
                                    // strip, cards, queue). A station that doesn't apply
                                    // (Estimate on a clean pass) renders as a muted
                                    // "not needed" placeholder instead of vanishing, so
                                    // the numbering never appears to skip.
                                    const notNeeded = s.n === 3 && !showEstimate;
                                    if (notNeeded) {
                                        return (
                                            <div
                                                key={s.n}
                                                className="flex min-w-[126px] flex-1 items-center gap-2 border-b-[3px] border-transparent px-2 py-2 text-left opacity-50"
                                            >
                                                <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-muted text-[11px] font-extrabold text-foreground/60">
                                                    {s.n}
                                                </span>
                                                <span>
                                                    <span className="block text-[12.5px] font-bold leading-tight line-through decoration-1">
                                                        {s.title}
                                                    </span>
                                                    <span className="hidden text-[10.5px] text-foreground/60 sm:block">
                                                        not needed for this job
                                                    </span>
                                                </span>
                                            </div>
                                        );
                                    }
                                    // Phases A–C are all interactive now; nothing is locked.
                                    const locked = false;
                                    const now = station === s.n;
                                    const stepNumber = s.n;
                                    const done = queueJob ? queueJob.blocking.station > s.n : false;
                                    const needs = queueJob ? queueJob.blocking.station === s.n : false;
                                    return (
                                        <button
                                            key={s.n}
                                            onClick={() => onStation(s.n)}
                                            className={cn(
                                                "flex min-w-[126px] flex-1 items-center gap-2 border-b-[3px] px-2 py-2 text-left",
                                                now ? "border-teal-700" : "border-transparent",
                                                locked && "opacity-55",
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    "flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-extrabold",
                                                    now
                                                        ? "bg-teal-700 text-white"
                                                        : done
                                                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                                                          : needs
                                                            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                                                            : "bg-muted text-foreground/60",
                                                )}
                                            >
                                                {stepNumber}
                                            </span>
                                            <span>
                                                <span className="block text-[12.5px] font-bold leading-tight">
                                                    {s.title}
                                                </span>
                                                <span className="hidden text-[10.5px] text-foreground/60 sm:block">
                                                    {locked
                                                        ? `Phase ${s.phase}`
                                                        : needs
                                                          ? queueJob?.blocking.label
                                                          : done
                                                            ? `✓ ${credits?.[s.n as 1 | 2 | 4 | 5]?.by ?? "done"}`
                                                            : ""}
                                                </span>
                                            </span>
                                            {locked && (
                                                <Lock className="ml-auto h-3 w-3 flex-none text-foreground/40" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    }
                </div>

                {/* Couldn't-test summary + one-click return trip. Sits under the
                    context bar, above every station. shrink-0 so it never steals
                    the Documents PDF pane's height. Renders nothing unless a
                    device on this job couldn't be tested. */}
                <CouldNotTestBanner
                    jobId={jobId}
                    onDone={onBack}
                    offerNoReturn={ballOn && queueJob?.has_quote === true}
                    showChangeToCantTest={ballOn && station === 1}
                />

                {/* #770: every station opens with its one-line question, tied to
                    the story — what this step is FOR on this particular job. */}
                {queueJob && (
                    <p className="mb-1.5 shrink-0 px-1 text-[12px] font-semibold italic text-foreground/55">
                        {station === 1 &&
                            ((queueJob.docs_unreviewed ?? queueJob.report_total - queueJob.report_reviewed) > 0
                                ? "Do the field documents say what we're about to bill for?"
                                : "Documents reviewed ✓ — these reports are the proof behind everything billed on this job.")}
                        {station === 2 &&
                            (queueJob.failed_devices > 0 && queueJob.draft_estimate_count > 0
                                ? "You're invoicing the test visit. The failed device's repair is NOT on this invoice — it's quoted on the estimate."
                                : "Is the invoice right — and is anything billable from the visit missing?")}
                        {station === 3 &&
                            (queueJob.failed_devices > 0
                                ? "This quote exists because a device failed. It needs owner approval before it can ride any send."
                                : "The estimate needs owner approval before it can ride any send.")}
                        {station === 4 &&
                            (queueJob.draft_estimate_count > 0
                                ? "Collect what's owed today. The repair estimate is collected only after the customer approves it — it is not owed now."
                                : "Collect what's owed today.")}
                        {station === 5 &&
                            `One packet tells the customer the whole story${queueJob.site_city ? `; ${queueJob.site_city} gets its reports too` : ""}.`}
                    </p>
                )}

                {isDocs ? (
                    /* Documents station fills the remaining height so the PDF is
                       as tall as the viewport allows. */
                    <div className="min-h-0 flex-1">
                        <StationDocuments
                            jobId={jobId}
                            onStation={onStation}
                            nextStation={nextNeededStation}
                            nextStationTitle={nextNeededTitle}
                        />
                    </div>
                ) : (
                    <>
                        {station === 2 && <StationCharges jobId={jobId} onStation={onStation} />}
                        {station === 3 && (
                            <StationEstimate jobId={jobId} ball={ballOn ? (queueJob?.ball ?? null) : undefined} />
                        )}
                        {station === 4 && (
                            <StationPayment
                                jobId={jobId}
                                onPaid={() => onStation(5)}
                                onGoEstimate={() => onStation(3)}
                            />
                        )}
                        {station === 5 && <StationSendoff jobId={jobId} onSent={onBack} />}
                    </>
                )}
            </div>

            {railHandFor && (
                <HandToDialog
                    jobId={railHandFor.id}
                    jobName={jobNameOf(railHandFor)}
                    asks={railHandFor.asks}
                    onClose={() => setRailHandFor(null)}
                />
            )}
            {starOpen && queueJob && ballOn && (
                <JobNotesDialog
                    jobId={jobId}
                    jobName={queueJob.site_name || queueJob.company_name || queueJob.title || "this job"}
                    onClose={() => setStarOpen(false)}
                />
            )}
            {starOpen && queueJob && !ballOn && (
                <StarDialog
                    job={queueJob}
                    staff={staff}
                    currentUserId={currentUserId}
                    onClose={() => setStarOpen(false)}
                    onSaved={() => refreshCloseOutQueue(qc, { jobId })}
                />
            )}
        </div>
    );
}

// ═══ Couldn't-test summary + "Add another appointment" ══════════════════════
//
// When the tech couldn't test some devices, closeout tells the office how many,
// shows the tech's reasons, and offers a one-click return trip for JUST those
// devices — re-opening them onto another appointment on the same job (the rest
// of the job stays done). It reuses the office-review add-appointment endpoint
// the closeout screen already calls for approve/send. Renders nothing unless a
// device on this job couldn't be tested (could_not_test / not_located; a
// device_gone release is removal review, not a return trip).
// Forward-only guard (Issue #315). The banner + "Add another appointment" show
// ONLY for devices marked can't-test on/after this feature shipped — a device
// released before this date is historical, so the feature never surfaces (or
// re-opens) an already-completed job.
// NOTE: keep in sync with ADD_APPOINTMENT_SINCE in src/server/services/jobVisitService.ts.
const ADD_APPOINTMENT_SINCE = new Date("2026-07-23T00:00:00Z");

interface CloseoutDeviceRow {
    id: string;
    name: string | null;
    serial_number: string | null;
    disposition: "skipped" | "not_due" | "not_located" | "could_not_test" | null;
    disposition_reason: string | null;
    disposition_category: string | null;
    disposition_set_at?: string | null;
    is_in_scope?: boolean | null;
    /** A recorded "no return trip" decision (migration 662). */
    no_return_reason?: string | null;
    /** 10/28: the tech's pictures for Can't find it / Couldn't get to it. */
    release_photo_ids?: string[] | null;
}

function couldNotTestCategoryLabel(c: string | null): string | null {
    return releaseCategoryLabel(c);
}

/** The tech's pictures for a release (10/28), small, each opening full size. */
function ReleasePhotoStrip({ ids }: { ids: string[] }) {
    if (ids.length === 0) {
        return <p className="mt-1 text-[11px] font-semibold text-destructive">No pictures from the tech.</p>;
    }
    return (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="release-photo-strip">
            {ids.slice(0, 6).map((id) => (
                <a
                    key={id}
                    href={attachmentDownloadUrl(id, { disposition: "inline" })}
                    target="_blank"
                    rel="noreferrer"
                    className="block h-14 w-14 overflow-hidden rounded border border-amber-300"
                    title="Open the tech's picture"
                >
                    <img
                        src={attachmentDownloadUrl(id, { variant: "thumb" })}
                        alt="Tech's picture of the device spot"
                        className="h-full w-full object-cover"
                    />
                </a>
            ))}
        </div>
    );
}

function CouldNotTestBanner({
    jobId,
    onDone,
    offerNoReturn = false,
    showChangeToCantTest = false,
}: {
    jobId: string;
    onDone: () => void;
    /** Who has the ball: a repair with a replacement quote needs no return trip. */
    offerNoReturn?: boolean;
    /** Who has the ball: Documents offers "Change to Couldn't test". */
    showChangeToCantTest?: boolean;
}) {
    const qc = useQueryClient();
    const devicesQuery = useQuery({
        queryKey: ["backflow", "job", jobId, "devices"],
        queryFn: () => api.get<{ devices: CloseoutDeviceRow[] }>(`/api/backflow/job/${jobId}/devices`),
        enabled: !!jobId,
        // A non-backflow job has no such devices; don't hammer it on a miss.
        retry: false,
    });
    const rows = devicesQuery.data?.devices ?? [];
    const inScope = rows.filter((r) => r.is_in_scope !== false);
    // Devices the field couldn't test that need a RETURN TRIP: could_not_test /
    // not_located, minus device_gone (that's removal review, not a re-test).
    // Forward-only: skip anything marked before this feature shipped.
    const needsReturn = inScope.filter(
        (r) =>
            (r.disposition === "could_not_test" || r.disposition === "not_located") &&
            r.disposition_category !== "device_gone" &&
            r.disposition_set_at != null &&
            new Date(r.disposition_set_at) >= ADD_APPOINTMENT_SINCE &&
            // A recorded "no return" decision (replacement quoted, or the
            // office said no trip is needed) settles the device.
            !r.no_return_reason,
    );

    const addAppointment = useMutation({
        mutationFn: () =>
            api.post<{ remainingCount: number; jobStatus: string }>(
                `/api/office-review/jobs/${jobId}/add-appointment`,
                {},
            ),
        onSuccess: (r) => {
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["backflow", "job", jobId, "devices"] });
            const n = r?.remainingCount ?? 0;
            toast.success(`Return trip added — ${n} device${n === 1 ? "" : "s"} back on the schedule board`);
            onDone();
        },
        onError: (e: any) =>
            toast.error(`Couldn't add appointment: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });

    const changeToCantTest = showChangeToCantTest ? (
        <ChangeToCouldNotTest jobId={jobId} devices={inScope as unknown as CantTestDevice[]} />
    ) : null;
    if (needsReturn.length === 0) return changeToCantTest;

    return (
        <>
            {changeToCantTest}
            <div className="mb-3 shrink-0 rounded-lg border border-amber-300/80 bg-amber-50/90 p-3 dark:border-amber-600/50 dark:bg-amber-950/30">
                <div className="flex items-start gap-2">
                    <CalendarPlus className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                            {needsReturn.length} of {inScope.length} device{inScope.length === 1 ? "" : "s"}{" "}
                            weren&apos;t tested this visit
                        </p>
                        <p className="mt-0.5 text-xs leading-relaxed text-amber-800/90 dark:text-amber-200/90">
                            Add another appointment to send a return trip for just{" "}
                            {needsReturn.length === 1 ? "this device" : "these devices"} — the rest of the job is done.
                        </p>
                    </div>
                </div>
                <ul className="mt-2 space-y-1.5">
                    {needsReturn.map((r) => {
                        const cat = couldNotTestCategoryLabel(r.disposition_category);
                        return (
                            <li
                                key={r.id}
                                className="rounded-md border border-amber-200/70 bg-white/60 px-2.5 py-1.5 text-xs dark:border-amber-700/40 dark:bg-black/10"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="min-w-0 truncate font-medium text-foreground">
                                        {r.name ?? "Unnamed device"}
                                        {r.serial_number ? ` · S/N ${r.serial_number}` : ""}
                                    </span>
                                    <span className="shrink-0 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200">
                                        {r.disposition === "not_located"
                                            ? RELEASE_LABELS.not_located
                                            : RELEASE_LABELS.could_not_test}
                                    </span>
                                </div>
                                {cat && <p className="mt-0.5 font-medium text-foreground/80">{cat}</p>}
                                {r.disposition_reason && (
                                    <p className="mt-0.5 italic text-foreground/85">
                                        &ldquo;{r.disposition_reason}&rdquo;
                                    </p>
                                )}
                                <ReleasePhotoStrip ids={r.release_photo_ids ?? []} />
                            </li>
                        );
                    })}
                </ul>
                <Button
                    size="sm"
                    className="mt-2.5 h-8 w-full text-xs sm:w-auto"
                    disabled={addAppointment.isPending}
                    onClick={() => addAppointment.mutate()}
                >
                    <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Add another appointment
                </Button>
                {offerNoReturn && <NoReturnReplacementQuoted jobId={jobId} onDone={() => undefined} />}
            </div>
        </>
    );
}

// ═══ STATION 4 — PAYMENT (Phase C) ══════════════════════════════════════
// Payment gates EVERYTHING outbound (decision 1). Non-net + unpaid: nothing
// sends until collected. Net terms pass through. The gate reads the customer
// record — the clicker never decides.

interface PaymentData {
    /** Card hold (#2037, step 5): Charge takes from a live hold; "expired" = it ran out. */
    cardHold?: ChargeHold | null;
    /** The last card decline on this job that is still open (Charge card step). */
    lastDecline?: { reason: string; at: string; amount: number | null } | null;
    companyName: string | null;
    customerId: string | null;
    paymentProfile: string | null;
    isCod: boolean;
    netTermsDays: number | null;
    hasCardOnFile: boolean;
    cardBrand: string | null;
    cardLast4: string | null;
    canViewMoney: boolean;
    canManageCustomer: boolean;
    // Net-terms grant governance. Office may REQUEST a COD→NET switch; only an
    // Admin approves (Manager/Admin apply instantly). Removing NET stays instant.
    termsChangeNeedsApproval: boolean;
    canApproveTerms: boolean;
    currentUserId: string | null;
    pendingTermsRequest: {
        id: string;
        requestedProfile: string;
        requestedNetTermsDays: number | null;
        fromProfile: string | null;
        note: string | null;
        requestedBy: string | null;
        requestedByName: string | null;
        requestedAt: string;
        jobId: string | null;
    } | null;
    gate: { unlocked: boolean; mode: "net_terms" | "paid" | "card_on_file" | "blocked"; reason: string };
    invoices: Array<{
        id: string;
        number: string | null;
        status: string;
        total: number | null;
        paid: number | null;
        balance: number | null;
        /** #1984: may the card on file be run for this invoice? */
        cardGuard?: CardGuard | null;
    }>;
    /** #756: open (draft/sent, priced) estimates — money NOT in any invoice above. */
    openEstimates: Array<{
        id: string;
        number: string | null;
        status: string;
        total: number | null;
        approvalState: "pending" | "approved" | null;
    }>;
}

function StationPayment({
    jobId,
    onPaid,
    onGoEstimate,
}: {
    jobId: string;
    onPaid: () => void;
    onGoEstimate?: () => void;
}) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["close-out-payment", jobId],
        queryFn: () => api.get<PaymentData>(`/api/close-out/jobs/${jobId}/payment`),
    });
    const data = q.data;
    const [recordFor, setRecordFor] = useState<string | null>(null);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["close-out-payment", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-send-off", jobId] });
        refreshCloseOutQueue(qc, { jobId });
    };
    const openInvoice = data?.invoices.find((i) => i.status !== "paid") ?? data?.invoices[0] ?? null;

    // The Charge card step (Book My Test, 10/17): nothing charges a card
    // without this button. A decline is said plainly on the job, with what to
    // do next, and stays there until the card is settled.
    const charge = useMutation({
        mutationFn: (invoiceId: string) =>
            api.post<{ fromHold?: FromHold | null }>(`/api/invoices/${invoiceId}/charge-card`, {}),
        onSuccess: (r) => {
            // Card hold (#2037): say what came from the hold and what didn't.
            const f = r?.fromHold ? fromHoldMessage(r.fromHold) : null;
            if (f && !f.ok) toast.error(f.text);
            else toast.success(f ? f.text : "Card charged. The packet can go as a paid receipt.");
            refresh();
        },
        onError: (e) => {
            // #1984: a refusal (the card was already declined) is said in
            // plain words; the red box then shows the next step.
            const body = e instanceof ApiError ? (e.body as { blocked?: boolean; message?: string } | null) : null;
            if (e instanceof ApiError && e.status === 409 && body?.blocked && body.message) toast.error(body.message);
            refresh();
        },
    });
    const payLink = useMutation({
        // The server route is GET (generates the pay_token on first read).
        mutationFn: (invoiceId: string) => api.get<{ url: string }>(`/api/invoices/${invoiceId}/pay-link`),
        onSuccess: (r) => {
            if (r?.url) navigator.clipboard?.writeText(r.url).catch(() => {});
            toast.success("Payment link ready — copied to your clipboard.");
        },
        onError: () => toast.error("Couldn't make a payment link."),
    });

    // COD ↔ NET terms — changes the CUSTOMER'S DEFAULT terms (permanent, on the
    // customer record), gated on crm.manage. NET on → net_terms + a term length;
    // NET off → back to pay-on-invoice (COD). Re-derives the send gate on success.
    const [showTerms, setShowTerms] = useState(false);
    const setTerms = useMutation({
        mutationFn: (vars: { profile: string; netTermsDays: number | null }) =>
            api.patch<{ termsChangeRequest?: { status: string } }>(`/api/customers/${data?.customerId}`, {
                paymentProfile: vars.profile,
                ...(vars.profile === "net_terms" ? { netTermsDays: vars.netTermsDays ?? 30 } : {}),
                // Lets an Admin deep-link back to this job when approving.
                jobId,
            }),
        onSuccess: (res) => {
            // A throttled net grant comes back as a pending request, not applied.
            if (res?.termsChangeRequest?.status === "pending") {
                toast.success("NET terms requested — Corbin will approve it.");
            } else {
                toast.success("Customer terms updated.");
            }
            setShowTerms(false);
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't update the customer's terms."),
    });
    // Admin approves / declines a pending grant; the requester may cancel theirs.
    const approveTerms = useMutation({
        mutationFn: () => api.post(`/api/customers/${data?.customerId}/terms-request/approve`, {}),
        onSuccess: () => {
            toast.success("NET terms approved — applied to the customer.");
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't approve the request."),
    });
    const declineTerms = useMutation({
        mutationFn: (_vars: { cancel: boolean }) =>
            api.post(`/api/customers/${data?.customerId}/terms-request/decline`, {}),
        onSuccess: (_r, vars) => {
            toast.success(vars.cancel ? "Request cancelled." : "Request declined — terms unchanged.");
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't update the request."),
    });

    if (q.isLoading) {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading payment…
            </div>
        );
    }
    if (!data) return null;
    const g = data.gate;
    const netTerms = data.paymentProfile === "net_terms";
    const money = (n: number | null) =>
        n == null ? "—" : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    return (
        <div className="space-y-3">
            {/* #756: the money this station used to hide. "Collect $80" is only
                the test invoice — an open repair estimate is a separate, larger
                number that is NOT part of any invoice above, and it was far too
                easy to read a job as fully paid while it sat unlinked. */}
            {data.openEstimates.length > 0 && (
                <div className="rounded-xl border-l-[3px] border-amber-600 bg-amber-50/60 p-3.5 text-[13px] dark:bg-amber-900/15">
                    {data.openEstimates.map((e) => (
                        <div key={e.id} className="flex flex-wrap items-center gap-2">
                            <span className="min-w-0 flex-1">
                                <b>
                                    Unpaid repair estimate{e.number ? ` #${e.number}` : ""}
                                    {data.canViewMoney && e.total != null ? ` — ${money(e.total)}` : ""}
                                </b>{" "}
                                — NOT part of this invoice.{" "}
                                {e.approvalState === "approved"
                                    ? "Approved; it rides the packet as a quote and is collected only after the customer accepts it."
                                    : e.approvalState === "pending"
                                      ? "Awaiting owner approval before it can ride the packet."
                                      : "Still a draft — it needs approval before it can ride the packet."}
                            </span>
                            {onGoEstimate && (
                                <Button size="sm" variant="outline" onClick={onGoEstimate}>
                                    Open Estimate station
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {netTerms ? (
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-bold">Net-terms customer — passes through</p>
                    <p className="mt-1.5 text-[12.5px] text-foreground/70">
                        The invoice rides showing the balance due. The gate reads the customer record; the clicker never
                        decides.
                    </p>
                    <Button className="mt-3 bg-teal-700 text-white hover:bg-teal-800" onClick={onPaid}>
                        Continue to send-off →
                    </Button>
                </div>
            ) : (
                <div className="rounded-xl border border-border bg-card p-4">
                    <p className="text-sm font-bold">
                        {openInvoice && data.canViewMoney && openInvoice.balance != null && openInvoice.balance > 0
                            ? `Collect ${money(openInvoice.balance)} — ${data.companyName ?? "customer"}`
                            : g.mode === "paid"
                              ? "Paid in full"
                              : `Collect payment — ${data.companyName ?? "customer"}`}
                    </p>
                    {g.mode !== "paid" && (data.hasCardOnFile || data.cardHold?.state === "held") && openInvoice && (
                        <ChargeCardStep
                            last4={data.cardLast4}
                            amount={data.canViewMoney ? openInvoice.balance : null}
                            invoiceStatus={openInvoice.status}
                            pending={charge.isPending}
                            decline={data.lastDecline ?? null}
                            customerName={data.companyName}
                            customerId={data.customerId}
                            guard={openInvoice.cardGuard ?? null}
                            hold={data.cardHold ?? null}
                            onChanged={refresh}
                            onCharge={() => charge.mutate(openInvoice.id)}
                        />
                    )}
                    {g.mode !== "paid" && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {openInvoice && (
                                <Button
                                    variant="outline"
                                    disabled={payLink.isPending}
                                    onClick={() => payLink.mutate(openInvoice.id)}
                                >
                                    Email payment link
                                </Button>
                            )}
                            {openInvoice && (
                                <Button variant="outline" onClick={() => setRecordFor(openInvoice.id)}>
                                    Record check / cash
                                </Button>
                            )}
                        </div>
                    )}
                    {g.mode === "paid" && (
                        <Button className="mt-3 bg-teal-700 text-white hover:bg-teal-800" onClick={onPaid}>
                            Continue to send-off →
                        </Button>
                    )}
                    {/* Add a new card — inline over-the-phone entry OR a secure
                        link the customer fills. Reuses the shared card panel. */}
                    {g.mode !== "paid" && data.customerId && (
                        <div className="mt-3">
                            <CardOnFilePanel
                                customerId={data.customerId}
                                hasCard={data.hasCardOnFile}
                                brand={data.cardBrand}
                                last4={data.cardLast4}
                                onCardSaved={refresh}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* #770 (C6): the dollar leads; the gate status explains second. */}
            <div
                className={cn(
                    "rounded-xl border-l-[3px] p-3.5 text-[13px]",
                    g.unlocked
                        ? "border-emerald-600 bg-emerald-50/60 dark:bg-emerald-900/15"
                        : "border-red-600 bg-red-50/60 dark:bg-red-900/15",
                )}
            >
                <b>{g.unlocked ? "✓ Cleared to send" : "⛔ Nothing sends until paid"}</b> — {g.reason} Payment clears →
                the city filing goes out from the prepared Documents preview, and the customer packet unlocks on the
                Customer step.
            </div>

            {/* COD ↔ NET — change the customer's DEFAULT terms from here.
                Granting NET is a credit decision: Office REQUESTS it and an Admin
                approves; Manager/Admin apply instantly. Removing NET is instant. */}
            {data.canManageCustomer && data.customerId && (
                <div className="rounded-xl border border-border bg-card p-3.5">
                    {data.pendingTermsRequest ? (
                        /* A grant is awaiting Corbin's approval — everyone sees the
                           same pending banner; the Admin can act on it right here. */
                        <div className="rounded-lg border-l-[3px] border-amber-500 bg-amber-50/60 p-3 dark:bg-amber-900/15">
                            <p className="text-[13px] font-bold text-amber-800 dark:text-amber-200">
                                NET {data.pendingTermsRequest.requestedNetTermsDays ?? 30} requested — waiting on admin
                                approval
                            </p>
                            <p className="mt-0.5 text-[11.5px] text-foreground/70">
                                {data.pendingTermsRequest.requestedByName
                                    ? `Requested by ${data.pendingTermsRequest.requestedByName}. `
                                    : ""}
                                The customer stays on{" "}
                                {data.pendingTermsRequest.fromProfile === "net_terms" ? "NET" : "COD"} until an admin
                                approves.
                            </p>
                            <div className="mt-2.5 flex flex-wrap gap-2">
                                {data.canApproveTerms ? (
                                    <>
                                        <Button
                                            size="sm"
                                            className="bg-teal-700 text-white hover:bg-teal-800"
                                            disabled={approveTerms.isPending || declineTerms.isPending}
                                            onClick={() => approveTerms.mutate()}
                                        >
                                            {approveTerms.isPending && (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            )}
                                            Approve NET {data.pendingTermsRequest.requestedNetTermsDays ?? 30}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={approveTerms.isPending || declineTerms.isPending}
                                            onClick={() => declineTerms.mutate({ cancel: false })}
                                        >
                                            Decline
                                        </Button>
                                    </>
                                ) : data.pendingTermsRequest.requestedBy != null &&
                                  data.pendingTermsRequest.requestedBy === data.currentUserId ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={declineTerms.isPending}
                                        onClick={() => declineTerms.mutate({ cancel: true })}
                                    >
                                        Cancel request
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <p className="text-[13px] font-bold">
                                        Terms —{" "}
                                        {netTerms ? `NET ${data.netTermsDays ?? 30}` : "COD (due on/before send)"}
                                    </p>
                                    <p className="mt-0.5 text-[11.5px] text-foreground/60">
                                        {!netTerms && data.termsChangeNeedsApproval
                                            ? "Granting NET terms extends credit — an admin has to approve your request before it takes effect."
                                            : "Changing this updates the customer's default terms permanently, on their record."}
                                    </p>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => setShowTerms((s) => !s)}>
                                    {netTerms
                                        ? "Switch to COD"
                                        : data.termsChangeNeedsApproval
                                          ? "Request NET terms"
                                          : "Switch to NET terms"}
                                </Button>
                            </div>
                            {showTerms && (
                                <TermsSwitcher
                                    netTerms={netTerms}
                                    currentDays={data.netTermsDays ?? 30}
                                    needsApproval={data.termsChangeNeedsApproval}
                                    pending={setTerms.isPending}
                                    onCancel={() => setShowTerms(false)}
                                    onConfirm={(profile, days) => setTerms.mutate({ profile, netTermsDays: days })}
                                />
                            )}
                        </>
                    )}
                </div>
            )}

            {recordFor && (
                <RecordPaymentDialog
                    invoiceId={recordFor}
                    defaultAmount={openInvoice?.balance ?? null}
                    onClose={() => setRecordFor(null)}
                    onRecorded={() => {
                        setRecordFor(null);
                        refresh();
                    }}
                />
            )}
        </div>
    );
}

function RecordPaymentDialog({
    invoiceId,
    defaultAmount,
    onClose,
    onRecorded,
}: {
    invoiceId: string;
    defaultAmount: number | null;
    onClose: () => void;
    onRecorded: () => void;
}) {
    const [amount, setAmount] = useState(defaultAmount != null ? String(defaultAmount) : "");
    const [method, setMethod] = useState("check");
    const [reference, setReference] = useState("");
    const save = useMutation({
        mutationFn: () =>
            api.post(`/api/invoices/${invoiceId}/payments`, {
                amount: Number(amount),
                method,
                reference: reference.trim() || null,
            }),
        onSuccess: () => {
            toast.success("Payment recorded.");
            onRecorded();
        },
        onError: () => toast.error("Couldn't record the payment."),
    });
    return (
        <Dialog open onClose={onClose} title="Record check / cash">
            <div className="space-y-2.5 p-1">
                <label className="block text-[12px] font-semibold">
                    Amount
                    <input
                        className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
                        value={amount}
                        inputMode="decimal"
                        onChange={(e) => setAmount(e.target.value)}
                    />
                </label>
                <label className="block text-[12px] font-semibold">
                    Method
                    <select
                        className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                    >
                        <option value="check">Check</option>
                        <option value="cash">Cash</option>
                        <option value="ach">ACH / transfer</option>
                        <option value="other">Other</option>
                    </select>
                </label>
                <label className="block text-[12px] font-semibold">
                    Reference (check #, memo)
                    <input
                        className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                    />
                </label>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        className="bg-teal-700 text-white hover:bg-teal-800"
                        disabled={!Number(amount) || save.isPending}
                        onClick={() => save.mutate()}
                    >
                        {save.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                        Record payment
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

/** Inline confirm for a COD↔NET terms switch on the customer's default record. */
function TermsSwitcher({
    netTerms,
    currentDays,
    needsApproval,
    pending,
    onCancel,
    onConfirm,
}: {
    netTerms: boolean;
    currentDays: number;
    // When true, going NET is a REQUEST that an admin must approve — the confirm
    // button says "Request …" rather than "Set …".
    needsApproval: boolean;
    pending: boolean;
    onCancel: () => void;
    onConfirm: (profile: string, days: number | null) => void;
}) {
    const [days, setDays] = useState(String(currentDays || 30));
    // Switching TO net terms → net_terms + a term length. Switching OFF net →
    // pay_on_invoice (plain COD, due on/before send).
    const goingNet = !netTerms;
    return (
        <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3">
            {goingNet ? (
                <label className="block text-[12px] font-semibold">
                    Net terms (days)
                    <select
                        className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                    >
                        <option value="15">NET 15</option>
                        <option value="30">NET 30</option>
                        <option value="45">NET 45</option>
                        <option value="60">NET 60</option>
                    </select>
                </label>
            ) : (
                <p className="text-[12.5px] text-foreground/70">
                    Switch this customer to <b>COD</b> — invoices are due on or before the packet goes out.
                </p>
            )}
            {goingNet && needsApproval && (
                <p className="mt-2 text-[11.5px] text-amber-700 dark:text-amber-300">
                    This sends a request to an admin — the customer stays on COD until it&apos;s approved.
                </p>
            )}
            <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
                    Cancel
                </Button>
                <Button
                    size="sm"
                    className="bg-teal-700 text-white hover:bg-teal-800"
                    disabled={pending}
                    onClick={() => onConfirm(goingNet ? "net_terms" : "pay_on_invoice", goingNet ? Number(days) : null)}
                >
                    {pending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    {goingNet ? (needsApproval ? `Request NET ${days}` : `Set NET ${days}`) : "Set COD"}
                </Button>
            </div>
        </div>
    );
}

// ═══ STATION 5 — SEND-OFF (Phase C) ═════════════════════════════════════
// The ONLY place a customer send starts (decision 3). Locked until payment
// clears. Manifest: check = goes, uncheck = held (held pieces re-send later
// only from their own pages). One "Send packet" rides the sanctioned
// packet-hold path.

interface SendOffData {
    unlocked: boolean;
    /** Close Out guard (11/2): reports on file but 0 tests — never send. Older servers omit it. */
    reportsWithoutTests?: ReportsWithoutTests;
    gate: { mode: string; reason: string };
    customerId: string | null;
    recipient: { email: string; name: string | null } | null;
    manifest: {
        invoice: { id: string | null; paid: boolean; balance: number | null };
        reports: number;
        woState: "paper" | "generated" | null;
        /** vendor_work_order job — a completed WO belongs on the packet. */
        woApplicable?: boolean;
        estimate: { id: string; label: string; total: number | null } | null;
        estimateHeld: boolean;
        // #756: failure reasons no estimate line appears to cover — must be
        // acknowledged in the confirm dialog before the send unlocks.
        estimateGaps: string[];
        // Reason a quote was deliberately skipped, when the office chose to tell
        // the customer (Issue #517). Pre-fills a line in the email below.
        noQuoteNote: string | null;
        photos: number;
        // Office-uploaded supplemental files (Issue #311 rip cord 3). Each rides
        // the packet when included, via the CE-2 explicit-attachment path.
        uploads: Array<{
            id: string;
            file_name: string | null;
            mime_type: string | null;
            /** The upload checker read it as a work order (saved verdict). */
            wo_verdict_reason?: string | null;
        }>;
        // Every uploaded paper WO (Issue #295) so the office can handpick which
        // ride. `appointment` is null for a single-WO job (reads as before).
        workOrders: Array<{ id: string; file_name: string | null; appointment: number | null }>;
        /** Blank forms / city copies that never go to the customer. Older servers omit it. */
        leftOut?: LeftOutFile[];
        documents: Array<{
            id: string;
            file_name: string | null;
            category: string;
            mime_type: string | null;
            autoReviewed: boolean;
        }>;
    };
    photoList: Array<{ id: string; file_name: string | null; photo_tag: string | null; photo_label?: string | null }>;
    /** Add from the field: tech photos/files on the job, kept out of the packet. */
    insideOnlyCount?: number;
    companyName: string | null;
    siteName: string | null;
    woRef: string | null;
    canViewMoney: boolean;
    // A saved customer-email draft (Issue #517). When present it's loaded verbatim
    // instead of re-seeding, so edits persist and the office sees what was added.
    emailDraft: { subject: string; body: string; updatedByName: string | null; updatedAt: string } | null;
}

interface EmailCoachResult {
    available: boolean;
    reason?: string;
    tone?: string;
    issues?: string[];
    rewrite?: { subject: string; message: string } | null;
}

function StationSendoff({ jobId, onSent }: { jobId: string; onSent: () => void }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["close-out-send-off", jobId],
        queryFn: () => api.get<SendOffData>(`/api/close-out/jobs/${jobId}/send-off`),
    });
    const data = q.data;
    // Manifest checkboxes — check = goes, uncheck = held. Photos off by default.
    const [inc, setInc] = useState({ invoice: true, reports: true, estimate: true, photos: false });
    // Recipient routing (who gets which documents) from the shared editor.
    const [plan, setPlan] = useState<PlanRecipient[]>([]);
    const [missingDocs, setMissingDocs] = useState<PacketDoc[]>([]);
    const handlePlanChange = useCallback((p: PlanRecipient[]) => setPlan(p), []);
    const handleMissing = useCallback((mds: PacketDoc[]) => setMissingDocs(mds), []);
    // "Resend to who missed it": when a prior send left recipients failed or
    // dropped, the office can flip the editor to seed ONLY those — a resend that
    // reaches just the misses, never re-blasting everyone who already got it.
    const [resendMode, setResendMode] = useState<"all" | "missed">("all");
    const [missedCount, setMissedCount] = useState(0);
    const handleMissedCount = useCallback((n: number) => setMissedCount(n), []);
    // Which photos ride — start with all, uncheck to leave home (excluded ids).
    const [excludedPhotos, setExcludedPhotos] = useState<Set<string>>(() => new Set());
    // Which uploaded work orders ride (Issue #295) — start with all checked;
    // uncheck to leave one home. Merged into excludedAttachmentIds at send.
    const [excludedWorkOrders, setExcludedWorkOrders] = useState<Set<string>>(() => new Set());
    // Which office-uploaded supplemental files ride (Issue #311) — all checked
    // by default; the included ids go out as attachmentIds (CE-2 explicit path).
    const [excludedUploads, setExcludedUploads] = useState<Set<string>>(() => new Set());
    // Which per-device reports ride (both / one / none). The send path already
    // honors excludedAttachmentIds for report attachments — this simply exposes
    // it. All ride by default; unchecked ids merge into excludedAttachmentIds at
    // send. Scoped to the customer-facing per-device reports: the merged
    // city-filing `report` never emails and work orders have their own picker.
    const [excludedReports, setExcludedReports] = useState<Set<string>>(() => new Set());
    // A blank form uploaded by mistake, or a city copy: never offered as a
    // customer report ("Left out: blank form" below instead).
    const leftOutIds = new Set((data?.manifest.leftOut ?? []).map((l) => l.id));
    const reportFiles = (data?.manifest.documents ?? []).filter(
        (d) => (d.category === "test_report" || d.category === "test_report_official") && !leftOutIds.has(d.id),
    );
    // Only surface the per-report checklist when there are 2+ to choose between;
    // a single report reads exactly as before (the one "N test reports" row).
    const usePerReportPicker = reportFiles.length > 1;
    const reportOn = (id: string) => !excludedReports.has(id);
    const toggleReport = (id: string) =>
        setExcludedReports((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const reportsSending = reportFiles.filter((d) => reportOn(d.id));
    // The merged jurisdiction PDF (category 'report', e.g. `tceq.pdf`) is the
    // city-filing copy — it is NEVER emailed to the customer
    // (selectCustomerReportRows drops it), so it must not be counted or previewed
    // as a customer attachment. Surfaced separately so the office sees where it
    // went instead of mistaking it for a second report the customer receives.
    const cityFilingReport =
        (data?.manifest.documents ?? []).find((d) => d.category === "report" && !leftOutIds.has(d.id)) ?? null;
    // How many reports the CUSTOMER actually receives — the per-device reports,
    // not the city copy. Falls back to the server count only when there are no
    // per-device attachments (portal/legacy jobs) so nothing regresses there.
    const customerReportCount = reportFiles.length > 0 ? reportFiles.length : (data?.manifest.reports ?? 0);
    // The office-edited email — what you preview is what sends.
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [seeded, setSeeded] = useState(false);
    // The last saved version (Issue #517) — its subject/body drive the "unsaved
    // edits" indicator; byName/at drive the "Saved by … " line.
    const [savedMeta, setSavedMeta] = useState<{
        byName: string | null;
        at: string;
        subject: string;
        body: string;
    } | null>(null);
    // Preview-before-send: which attached piece the office is eyeballing.
    const [previewAttachment, setPreviewAttachment] = useState<{ id: string; name: string | null } | null>(null);
    const [previewDoc, setPreviewDoc] = useState<{ kind: "invoice" | "estimate"; id: string } | null>(null);
    const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
    // Last-look confirmation — "Send packet" opens this; the email only goes out
    // after the office confirms here. Guards against a misclick emailing the
    // customer with a wrong recipient / missing attachment / bad subject.
    const [showConfirm, setShowConfirm] = useState(false);
    // #756: failed-device work no estimate line covers must be acknowledged
    // deliberately before the packet can go — a soft gate (the matching is
    // fuzzy), but never a silent one.
    const estimateGaps = data?.manifest.estimateGaps ?? [];
    const [gapAck, setGapAck] = useState(false);
    // Send-path failsafes the reused office-review approve-and-send can raise —
    // closeout must resolve them here instead of dead-ending in a toast. Helper
    // Mode: "Was {helper} on this job?" (422 HELPER_PRESENCE_REQUIRED) must be
    // answered before approval; CE-1: selected files that won't ride (409
    // DROPPED_ATTACHMENTS_UNCONFIRMED) need an explicit "Send anyway".
    const [helperPresencePrompt, setHelperPresencePrompt] = useState<{
        helpers: Array<{ id: string; name: string }>;
    } | null>(null);
    const [dropGate, setDropGate] = useState<{ dropped: DroppedAttachment[] } | null>(null);
    // Every recipient is one of ours (422 NO_CUSTOMER_EMAIL): add the
    // customer's email, or tick "Send to staff only (internal copy)".
    const [staffOnlyGate, setStaffOnlyGate] = useState<NoCustomerEmailRefusal | null>(null);
    // "Already sent another way" (2026-10-07): portal / mail / in person.
    const [otherWayOpen, setOtherWayOpen] = useState(false);
    /** #1250 — a permit answer is outstanding; answered inline, not elsewhere. */
    const [permitGate, setPermitGate] = useState<{ message: string } | null>(null);
    // Never hand edit controls to someone the server will 403. The card is
    // read-only for them, which is honest, rather than inputs that fail on save.
    const { can: canDo } = useAuth();
    const canManageJobs = canDo("jobs.manage");

    // Seed the editable email once — personal, and adaptive to what's actually
    // in the packet (a WO job leads with the location + WO number; the body names
    // exactly what's attached). The office can tweak from there; the server also
    // appends its own summary line + the review button beneath.
    useEffect(() => {
        if (!data || seeded) return;
        // A saved draft wins over the generated seed — load it verbatim so edits
        // persist and the office sees exactly what was written (Issue #517).
        if (data.emailDraft) {
            setSubject(data.emailDraft.subject);
            setMessage(data.emailDraft.body);
            setSavedMeta({
                byName: data.emailDraft.updatedByName,
                at: data.emailDraft.updatedAt,
                subject: data.emailDraft.subject,
                body: data.emailDraft.body,
            });
            setSeeded(true);
            return;
        }
        const co = data.companyName ?? "Your service";
        const hasEstimate = !!data.manifest.estimate;
        const loc = data.siteName?.trim() || null;
        const wo = data.woRef?.trim() || null;
        const oxford = (arr: string[]) => arr.join(", ").replace(/, ([^,]*)$/, arr.length > 2 ? ", and $1" : " and $1");

        // Subject — WO jobs lead with location + WO; otherwise company + docs.
        const docsShort = ["test reports"];
        if (data.manifest.invoice) docsShort.push("invoice");
        if (hasEstimate) docsShort.push("estimate");
        const docsSubj = docsShort.join(", ").replace(/, ([^,]*)$/, " & $1");
        setSubject(wo ? `${loc ? `${loc} — ` : ""}WO ${wo} · ${docsSubj}` : `${co} — your ${docsSubj}`);

        // Body — name exactly what's attached, reference WO/location. NO greeting
        // line here: the email renderer always prepends its own per-recipient
        // "Hi {name}," so a seeded greeting produced a double "Hi Preston,".
        const bits = ["your test reports"];
        if (data.manifest.invoice) bits.push(data.manifest.invoice.paid ? "your paid invoice" : "your invoice");
        if (hasEstimate) bits.push("a repair estimate for your review");
        const where = wo ? ` for work order ${wo}${loc ? ` at ${loc}` : ""}` : loc ? ` at ${loc}` : "";
        const lines: string[] = [];
        // "Here are", not "Attached are": a big packet's reports go as a link.
        lines.push(`Thank you for your business. Here ${bits.length > 1 ? "are" : "is"} ${oxford(bits)}${where}.`);
        // Why there's no quote (Issue #517) — only when the office ticked "tell
        // the customer" on the skip. Editable like the rest of the draft.
        const noQuote = data.manifest.noQuoteNote?.trim();
        if (noQuote) {
            lines.push("");
            lines.push(`Regarding a quote: ${noQuote}`);
        }
        lines.push("");
        lines.push("Please let us know if you have any questions — we're always glad to help.");
        setMessage(lines.join("\n"));
        setSeeded(true);
    }, [data, seeded]);

    // Save the email draft (Issue #517) so edits persist and the office sees
    // what was written — instead of a blank re-seed on the next visit.
    const saveDraft = useMutation({
        mutationFn: () =>
            api.put<{ updatedByName: string | null; updatedAt: string }>(`/api/close-out/jobs/${jobId}/email-draft`, {
                subject,
                body: message,
            }),
        onSuccess: (res) => {
            setSavedMeta({
                byName: res?.updatedByName ?? null,
                at: res?.updatedAt ?? "",
                subject,
                body: message,
            });
            toast.success("Email draft saved — the office picks up right here.");
            qc.invalidateQueries({ queryKey: ["close-out-send-off", jobId] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save the draft."),
    });
    const draftDirty = !savedMeta || savedMeta.subject !== subject || savedMeta.body !== message;

    // AI email coach — reviews the drafted subject/message and offers a rewrite.
    // Pure suggestion; the office stays in control (never auto-applies, never sends).
    const [coach, setCoach] = useState<EmailCoachResult | null>(null);
    const reviewEmail = useMutation({
        mutationFn: () =>
            api.post<EmailCoachResult>(`/api/close-out/email-coach`, {
                subject,
                message,
                customerId: data?.customerId ?? null,
                context: data
                    ? {
                          companyName: data.companyName,
                          recipientName: data.recipient?.name ?? null,
                          includesInvoice: inc.invoice,
                          invoicePaid: data.manifest.invoice.paid,
                          reportCount: usePerReportPicker
                              ? reportsSending.length
                              : inc.reports
                                ? customerReportCount
                                : 0,
                          includesEstimate: inc.estimate && !!data.manifest.estimate,
                          photoCount: inc.photos ? data.photoList.filter((p) => !excludedPhotos.has(p.id)).length : 0,
                      }
                    : {},
            }),
        onSuccess: (r) => {
            setCoach(r);
            if (!r.available) toast.error(r.reason ?? "AI review isn't available right now.");
        },
        onError: () => toast.error("AI review failed — send as-is or try again."),
    });
    const applyRewrite = () => {
        if (coach?.rewrite) {
            setSubject(coach.rewrite.subject);
            setMessage(coach.rewrite.message);
            setCoach(null);
            toast.success("Applied the suggested wording — edit freely before you send.");
        }
    };

    const send = useMutation<
        unknown,
        any,
        { confirmDroppedAttachments?: boolean; staffOnlyConfirmed?: boolean } | void
    >({
        mutationFn: (opts) => {
            // Office-uploaded supplemental files that are still ticked ride as
            // explicit attachments (CE-2) — they reach every recipient.
            const uploadAttachmentIds = (data?.manifest.uploads ?? [])
                .filter((u) => !excludedUploads.has(u.id))
                .map((u) => u.id);
            // Rides the sanctioned packet path (charge-at-approve + park + ONE
            // email; the Phase B estimate gate holds any unapproved estimate).
            // Now carries the office's recipient routing, photo picks, uploaded
            // files, and the edited subject/message.
            return api.post(`/api/office-review/jobs/${jobId}/approve-and-send`, {
                invoices: inc.invoice,
                // With the per-report picker, "reports" is on when at least one
                // report is still ticked; the unchecked ones ride
                // excludedAttachmentIds below. Single-report jobs keep the old
                // all-or-nothing checkbox.
                reports: usePerReportPicker ? reportsSending.length > 0 : inc.reports && customerReportCount > 0,
                estimates: inc.estimate,
                photos: inc.photos,
                ...(plan.length > 0 ? { plan } : {}),
                ...(uploadAttachmentIds.length > 0 ? { attachmentIds: uploadAttachmentIds } : {}),
                excludedAttachmentIds: [...excludedPhotos, ...excludedWorkOrders, ...excludedReports],
                emailSubject: subject.trim(),
                emailMessage: message.trim(),
                // CE-1: only set after the office confirms the drop warning
                // ("Send anyway"); without it the server 409s and nothing is lost.
                ...(opts?.confirmDroppedAttachments ? { confirmDroppedAttachments: true } : {}),
                // Only after the office ticked "Send to staff only (internal copy)".
                ...(opts?.staffOnlyConfirmed ? { staffOnlyConfirmed: true } : {}),
                // #756: the acknowledged not-on-estimate failure list — audit
                // trail for "who saw the gap and sent anyway."
                ...(estimateGaps.length > 0 && gapAck ? { estimateGapsAcknowledged: estimateGaps } : {}),
            });
        },
        onSuccess: (res: any) => {
            // Honest partial-delivery signal: the send succeeded for some but
            // not all. Don't claim a clean "sent". Two distinct misses:
            //   · failed        — the email was attempted but delivery failed
            //                     (resend to just those).
            //   · dropped       — the recipient (often a one-time address the
            //                     office typed in) got NO email because none of
            //                     their documents were available at send time
            //                     (e.g. a held estimate). This is the miss that
            //                     used to be silent.
            const failed: string[] = Array.isArray(res?.failed_recipients) ? res.failed_recipients : [];
            const dropped: Array<{ recipient: string }> = Array.isArray(res?.dropped_recipients)
                ? res.dropped_recipients
                : [];
            if (res?.partial || failed.length > 0 || dropped.length > 0) {
                const parts: string[] = [];
                if (failed.length) {
                    parts.push(`${failed.length} delivery failed: ${failed.join(", ")}`);
                }
                if (dropped.length) {
                    parts.push(
                        `${dropped.length} received nothing (their documents weren't ready): ` +
                            dropped.map((d) => d.recipient).join(", "),
                    );
                }
                toast.error(`Sent, but not to everyone — ${parts.join("; ")}. Follow up from the customer record.`, {
                    duration: 14000,
                });
                setShowConfirm(false);
                refreshCloseOutQueue(qc, { now: true });
                // Refresh the "who missed" set so a return visit is accurate.
                qc.invalidateQueries({ queryKey: ["send-preview", jobId] });
                onSent();
                return;
            }
            toast.success(
                res?.internal_copy
                    ? "Test copy sent to staff only. The customer got nothing, and nothing is marked delivered."
                    : resendMode === "missed"
                      ? "Resent to who missed it."
                      : "Packet sent — one email per recipient.",
            );
            setShowConfirm(false);
            refreshCloseOutQueue(qc, { now: true });
            qc.invalidateQueries({ queryKey: ["send-preview", jobId] });
            onSent();
        },
        // The reused office-review approve-and-send enforces the same gates it
        // always did. Resolve the two that block mid-send right here instead of
        // dead-ending in a toast (the bug: closeout never ported these).
        onError: (e: any) => {
            // Helper Mode close-out failsafe: a paired helper had no clock-in on
            // this job, so "Was {helper} on this job?" must be answered before
            // approval. Open the Yes/No modal below.
            if (e?.status === 422 && e?.body?.error === "HELPER_PRESENCE_REQUIRED") {
                setHelperPresencePrompt({
                    helpers: Array.isArray(e.body.helpers) ? e.body.helpers : [],
                });
                return;
            }
            // CE-1: a selected attachment won't ride (privacy/size). Show the
            // "Send anyway" gate rather than silently dropping or dead-ending.
            const staffOnly = noCustomerEmailRefusal(e);
            if (staffOnly) {
                setStaffOnlyGate(staffOnly);
                return;
            }
            if (e?.status === 409 && e?.body?.error === "DROPPED_ATTACHMENTS_UNCONFIRMED") {
                setDropGate({
                    dropped: Array.isArray(e.body.droppedAttachments) ? e.body.droppedAttachments : [],
                });
                return;
            }
            // #1250: a permit answer is outstanding. Resolve it RIGHT HERE —
            // the toast this replaces told the office to go to "the job's
            // Permit card", which lives on a different page, which is exactly
            // the dead-end the comment above this handler warns about.
            if (e?.status === 422 && e?.body?.reason === "permit_not_answered") {
                setPermitGate({ message: e?.body?.message ?? "This job still needs a permit answer." });
                return;
            }
            toast.error(e?.body?.message ?? "Send failed — check the recipients and try again.");
        },
    });

    // Helper Mode close-out failsafe answer. "Yes" writes an office-attested
    // helper session (crediting the helper's device points); "No" records they
    // weren't on site. Either clears the gate — then re-fire the send, which is
    // still staged in the open confirm modal, so the office finishes in one flow.
    const answerHelperPresence = useMutation<unknown, any, "yes" | "no">({
        mutationFn: (answer) => api.post(`/api/office-review/jobs/${jobId}/helper-presence`, { answer }),
        onSuccess: (_r, answer) => {
            setHelperPresencePrompt(null);
            refreshCloseOutQueue(qc, { jobId });
            toast.success(
                answer === "yes"
                    ? "Recorded — helper credited on this job. Sending…"
                    : "Recorded — helper wasn't on this job. Sending…",
            );
            send.mutate();
        },
        onError: (e: any) =>
            toast.error(`Couldn't record the answer: ${e?.body?.message ?? e?.body?.error ?? "unknown"}`),
    });

    // "Send a test copy to me first" — emails the exact report PDF(s) + photos +
    // the ticked additional files to the signed-in operator ONLY (the customer is
    // never emailed), so they can eyeball everything that will ship before the
    // real send. Non-destructive. The additional-file ids mirror the real send's
    // explicit-attachment path so the test copy shows what actually rides.
    const testSend = useMutation({
        mutationFn: () =>
            api.post<{
                sentTo?: string;
                attachments: { filename: string; bytes: number }[];
                duplicatesRemoved: number;
            }>(`/api/office-review/jobs/${jobId}/send-test-to-me`, {
                photos: inc.photos,
                ...(subject.trim() ? { emailSubject: subject.trim() } : {}),
                attachmentIds: (data?.manifest.uploads ?? [])
                    .filter((u) => !excludedUploads.has(u.id))
                    .map((u) => u.id),
            }),
        onSuccess: (r) =>
            toast.success(`Test copy sent to ${r.sentTo ?? "you"} — check your inbox, then send for real.`),
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't send the test copy — is a report attached yet?"),
    });

    // Will the reports attach, or go as a link? Measured on the server the way
    // the mail server measures it (packetSizePlan.ts); one plain line below.
    const sizeReports = usePerReportPicker ? reportsSending.length > 0 : inc.reports && customerReportCount > 0;
    const sizeExcluded = [...excludedWorkOrders, ...excludedReports].sort();
    const sizeUploads = (data?.manifest.uploads ?? []).filter((u) => !excludedUploads.has(u.id)).map((u) => u.id);
    const sizePlan = useQuery({
        queryKey: [
            "close-out-send-size",
            jobId,
            inc.invoice,
            sizeReports,
            inc.estimate,
            sizeExcluded.join(","),
            sizeUploads.join(","),
        ],
        queryFn: () => {
            const qs = new URLSearchParams({
                invoices: inc.invoice ? "1" : "0",
                reports: sizeReports ? "1" : "0",
                estimates: inc.estimate ? "1" : "0",
            });
            if (sizeExcluded.length > 0) qs.set("excluded", sizeExcluded.join(","));
            if (sizeUploads.length > 0) qs.set("attachmentIds", sizeUploads.join(","));
            return api.get<PacketSizePlan>(`/api/close-out/jobs/${jobId}/send-size?${qs.toString()}`);
        },
        enabled: !!data?.unlocked,
        staleTime: 60_000,
    });
    const sizeMessage = sizePlan.data?.message ?? null;
    const leftOutFiles = data?.manifest.leftOut ?? sizePlan.data?.leftOut ?? [];

    if (q.isLoading) {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading send-off…
            </div>
        );
    }
    if (!data) return null;
    if (!data.unlocked) {
        return (
            <div className="rounded-xl border border-border bg-card p-4">
                <p className="flex items-center gap-2 text-sm font-bold">
                    <Lock className="h-4 w-4 text-foreground/50" /> Locked — collect payment first
                </p>
                <p className="mt-1.5 text-[12.5px] text-foreground/70">{data.gate.reason}</p>
            </div>
        );
    }
    const m = data.manifest;
    const money = (n: number | null) =>
        n == null ? "" : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const photoOn = (id: string) => !excludedPhotos.has(id);
    const togglePhoto = (id: string) =>
        setExcludedPhotos((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const photosSending = data.photoList.filter((p) => photoOn(p.id)).length;
    // Uploaded work orders (Issue #295): all ride by default; uncheck to hold
    // one back. Only shown as a checklist when there are 2+.
    const workOrders = data.manifest.workOrders ?? [];
    const woOn = (id: string) => !excludedWorkOrders.has(id);
    const toggleWorkOrder = (id: string) =>
        setExcludedWorkOrders((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    // Office-uploaded supplemental files (rip cord 3). Included ids ride as
    // explicit attachments (attachmentIds) so they reach every recipient.
    const uploads = data.manifest.uploads ?? [];
    const uploadOn = (id: string) => !excludedUploads.has(id);
    const toggleUpload = (id: string) =>
        setExcludedUploads((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const includedUploads = uploads.filter((u) => uploadOn(u.id));
    // Two gates, not one (Issue #802): opening the last-look dialog vs.
    // actually sending. The #756 gap acknowledgment gates only the send —
    // its checkbox lives inside the dialog, so gating the dialog-opening
    // button on it left the office with a dead button and no way to clear it.
    const { canOpenConfirm, canSend } = deriveSendGate({
        planCount: plan.length,
        missingDocCount: missingDocs.length,
        subject,
        sending: send.isPending,
        estimateGapCount: estimateGaps.length,
        gapAck,
    });

    const Row = ({
        on,
        set,
        children,
        note,
    }: {
        on: boolean;
        set: (v: boolean) => void;
        children: React.ReactNode;
        note?: string;
    }) => (
        <label className="flex items-center gap-2.5 border-b border-dashed border-border py-2 text-[13px] last:border-0">
            <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} className="h-3.5 w-3.5" />
            <span className="flex-1">{children}</span>
            {note && <span className="text-[11px] text-muted-foreground">{note}</span>}
        </label>
    );

    return (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="space-y-3">
                {/* What goes in the packet */}
                <div className="rounded-xl border border-border bg-card p-3.5">
                    <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                        What goes in the packet
                    </div>
                    <div className="mt-1.5">
                        <Row
                            on={inc.invoice}
                            set={(v) => setInc((s) => ({ ...s, invoice: v }))}
                            note={data.canViewMoney && m.invoice.balance != null ? money(m.invoice.balance) : undefined}
                        >
                            Invoice {m.invoice.paid ? "— PAID" : ""}
                        </Row>
                        {/* Reports — a single report keeps the simple on/off row;
                            two or more get a checklist so the office handpicks
                            which ride (both / one / none). Unchecked ids ride
                            excludedAttachmentIds, which the send path honors. */}
                        {usePerReportPicker ? (
                            <div className="border-b border-dashed border-border py-2">
                                <div className="flex items-center gap-2 text-[13px]">
                                    <span className="flex-1">
                                        Test reports — pick which ride ({reportsSending.length} of {reportFiles.length})
                                    </span>
                                    <span className="text-[11px] text-muted-foreground">black print</span>
                                </div>
                                <div className="mt-1 space-y-1">
                                    {reportFiles.map((d) => (
                                        <label
                                            key={d.id}
                                            className="flex items-center gap-2 text-[12.5px] text-foreground/80"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={reportOn(d.id)}
                                                onChange={() => toggleReport(d.id)}
                                                className="h-3.5 w-3.5 accent-teal-600"
                                            />
                                            <span className="min-w-0 flex-1 truncate">
                                                {d.file_name || "Test report"}
                                            </span>
                                            {d.category === "test_report_official" && (
                                                <span className="flex-none rounded bg-emerald-100 px-1 py-px text-[9px] font-bold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                                                    official
                                                </span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                                {reportsSending.length === 0 && (
                                    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                                        No reports selected — the packet will go without a test report.
                                    </p>
                                )}
                            </div>
                        ) : customerReportCount === 0 ? (
                            /* #757 (audit): a job with NO test reports (device
                               couldn't be tested, portal-filed, etc.) used to
                               offer a pre-checked "0 test reports" attachment —
                               a checked box for a document that doesn't exist.
                               Say it plainly instead; nothing rides. */
                            <div className="flex items-center gap-2 border-b border-dashed border-border py-2 text-[12px] text-amber-700 dark:text-amber-300">
                                No test reports on this job — the packet goes without one.
                            </div>
                        ) : (
                            <Row on={inc.reports} set={(v) => setInc((s) => ({ ...s, reports: v }))} note="black print">
                                {reportFiles.length === 1 ? (
                                    <span className="flex min-w-0 items-baseline gap-1.5">
                                        1 test report
                                        <span className="min-w-0 truncate font-normal text-foreground/55">
                                            — {reportFiles[0]!.file_name || "test report"}
                                        </span>
                                    </span>
                                ) : (
                                    <>
                                        {customerReportCount} test report{customerReportCount === 1 ? "" : "s"}
                                    </>
                                )}
                            </Row>
                        )}
                        {/* The merged jurisdiction PDF (e.g. tceq.pdf) is the city's
                            filing copy — filed to the city, NEVER emailed to the
                            customer. Show it plainly so "N test reports" can't read
                            as N customer attachments (the "it sends both" confusion). */}
                        {cityFilingReport && (
                            <div className="flex items-center gap-2 border-b border-dashed border-border py-2 text-[12px] text-foreground/60">
                                <span className="flex-none rounded bg-muted px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-foreground/60">
                                    City copy
                                </span>
                                <span className="min-w-0 flex-1 truncate">
                                    {cityFilingReport.file_name || "jurisdiction form"} — filed with the city, not
                                    emailed to the customer
                                </span>
                            </div>
                        )}
                        {(sizeMessage || leftOutFiles.length > 0) && (
                            <div className="border-b border-dashed border-border py-2">
                                <PacketSizeNotice message={sizeMessage} leftOut={leftOutFiles} />
                            </div>
                        )}
                        {/* One WO (or a generated fill): the simple "rides with
                            the packet" row, exactly as before. Two or more
                            uploaded WOs (Issue #295): a checklist so the office
                            handpicks which visits' paperwork goes to the
                            customer. All checked by default. */}
                        {workOrders.length > 1 ? (
                            <div className="border-b border-dashed border-border py-2">
                                <div className="mb-1 text-[13px]">
                                    Work orders — pick which visits ride ({workOrders.length - excludedWorkOrders.size}{" "}
                                    of {workOrders.length})
                                </div>
                                <div className="space-y-1">
                                    {workOrders.map((w) => (
                                        <label
                                            key={w.id}
                                            className="flex items-center gap-2 text-[12.5px] text-foreground/80"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={woOn(w.id)}
                                                onChange={() => toggleWorkOrder(w.id)}
                                                className="h-3.5 w-3.5 accent-teal-600"
                                            />
                                            <span>{w.appointment ? `Appointment ${w.appointment}` : "Work order"}</span>
                                            {w.file_name && (
                                                <span className="truncate text-[11px] text-muted-foreground">
                                                    {w.file_name}
                                                </span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            m.woState && (
                                <div className="flex items-center gap-2.5 border-b border-dashed border-border py-2 text-[13px]">
                                    <span className="text-emerald-700 dark:text-emerald-300">✓</span>
                                    <span className="flex-1">
                                        Work order — {m.woState === "paper" ? "paper copy" : "generated"}
                                    </span>
                                    <span className="text-[11px] text-muted-foreground">rides with the packet</span>
                                </div>
                            )
                        )}
                        {m.estimate && (
                            <Row
                                on={inc.estimate}
                                set={(v) => setInc((s) => ({ ...s, estimate: v }))}
                                note="Corbin-approved ✓"
                            >
                                {m.estimate.label}
                            </Row>
                        )}
                        {m.estimateHeld && (
                            <div className="py-2 text-[12px] text-amber-700 dark:text-amber-300">
                                An estimate is on this job but isn&apos;t Corbin-approved — it&apos;s held out of the
                                packet (station 3).
                            </div>
                        )}
                        <Row on={inc.photos} set={(v) => setInc((s) => ({ ...s, photos: v }))} note="off by default">
                            Photos ({m.photos})
                        </Row>
                    </div>
                </div>

                {/* Additional files (rip cord 3) — drop zone + upload button so
                    the office can tack extra files onto the packet at closeout.
                    Ticked files ride via the CE-2 explicit-attachment path. */}
                <AdditionalFilesCard
                    jobId={jobId}
                    woApplicable={m.woApplicable === true}
                    woSlotEmpty={workOrders.length === 0}
                    woNumber={data.woRef ?? null}
                    uploads={uploads}
                    uploadOn={uploadOn}
                    toggleUpload={toggleUpload}
                    onPreview={(id, name) => setPreviewAttachment({ id, name })}
                    onUploaded={() => qc.invalidateQueries({ queryKey: ["close-out-send-off", jobId] })}
                />

                {/* Preview before sending — open each attached piece to eyeball
                    exactly what the customer will receive. Last line of defense. */}
                {(m.invoice.id || m.documents.length > 0 || m.estimate) && (
                    <div className="rounded-xl border border-border bg-card p-3.5">
                        <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                            <Eye className="h-3 w-3" /> Preview before sending
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {inc.invoice && m.invoice.id && (
                                <button
                                    type="button"
                                    onClick={() => setPreviewDoc({ kind: "invoice", id: m.invoice.id! })}
                                    className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-xs font-semibold hover:border-foreground/40 hover:bg-muted"
                                >
                                    <Eye className="h-3.5 w-3.5 text-foreground/50" /> Invoice
                                </button>
                            )}
                            {inc.reports &&
                                // Only what the customer actually receives: drop the
                                // merged city-filing copy (category 'report') and any
                                // report the office unchecked in the picker above.
                                m.documents
                                    .filter((d) => d.category !== "report" && !excludedReports.has(d.id))
                                    .map((d) => (
                                        <button
                                            key={d.id}
                                            type="button"
                                            onClick={() =>
                                                setPreviewAttachment({ id: d.id, name: d.file_name ?? null })
                                            }
                                            title={d.file_name || docCategoryLabel(d.category)}
                                            className="flex max-w-[220px] items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-xs font-semibold hover:border-foreground/40 hover:bg-muted"
                                        >
                                            <Eye className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
                                            <span className="truncate">
                                                {d.file_name || docCategoryLabel(d.category)}
                                            </span>
                                        </button>
                                    ))}
                            {inc.estimate && m.estimate && (
                                <button
                                    type="button"
                                    onClick={() => setPreviewDoc({ kind: "estimate", id: m.estimate!.id })}
                                    className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-xs font-semibold hover:border-foreground/40 hover:bg-muted"
                                >
                                    <Eye className="h-3.5 w-3.5 text-foreground/50" /> {m.estimate.label}
                                </button>
                            )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-muted-foreground">
                            Opens the exact file the customer receives. Photos preview in the picker below.
                        </p>
                    </div>
                )}

                {/* Add from the field: tech adds are inside only and are never offered. */}
                {inc.photos && (data.insideOnlyCount ?? 0) > 0 && (
                    <div
                        className="flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-2 text-[13px] font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200"
                        data-testid="send-off-inside-only"
                    >
                        <Lock className="h-4 w-4 shrink-0" aria-hidden />
                        {data.insideOnlyCount === 1
                            ? "1 photo or file the tech added is inside only. It is not in this packet."
                            : `${data.insideOnlyCount} photos or files the tech added are inside only. They are not in this packet.`}
                    </div>
                )}
                {/* Photo picker — choose which photos ride. */}
                {inc.photos && data.photoList.length > 0 && (
                    <div className="rounded-xl border border-border bg-card p-3.5">
                        <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                            <Camera className="h-3 w-3" /> Which photos to send ({photosSending} of{" "}
                            {data.photoList.length})
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {data.photoList.map((p) => {
                                const on = photoOn(p.id);
                                return (
                                    <div key={p.id} className="w-24">
                                        <div
                                            className={cn(
                                                "relative h-16 w-24 overflow-hidden rounded-lg border-2",
                                                on ? "border-teal-600" : "border-border opacity-45",
                                            )}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => togglePhoto(p.id)}
                                                aria-pressed={on}
                                                title={photoCaption(p)}
                                                className="block h-full w-full"
                                            >
                                                <img
                                                    src={attachmentDownloadUrl(p.id, { variant: "thumb" })}
                                                    alt={photoCaption(p)}
                                                    loading="lazy"
                                                    className="h-full w-full object-cover"
                                                />
                                            </button>
                                            <span
                                                className={cn(
                                                    "pointer-events-none absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-extrabold",
                                                    on ? "bg-teal-700 text-white" : "bg-black/50 text-white/80",
                                                )}
                                            >
                                                {on ? "✓" : ""}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setPreviewPhoto(p.id)}
                                                title="Preview photo"
                                                className="absolute bottom-0.5 left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/80"
                                            >
                                                <Eye className="h-2.5 w-2.5" />
                                            </button>
                                        </div>
                                        <p
                                            className={cn(
                                                "mt-0.5 line-clamp-2 text-[9.5px] leading-tight",
                                                on ? "text-foreground/80" : "text-muted-foreground",
                                            )}
                                        >
                                            {photoCaption(p)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="mt-1.5 text-[11px] text-muted-foreground">
                            Tap a photo to include or leave it out; the eye previews it. Only the ticked photos are
                            attached.
                        </p>
                    </div>
                )}

                {/* Recipients — who gets which pieces. */}
                <div className="rounded-xl border border-border bg-card p-3.5">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                            Who gets what
                        </div>
                        {/* Only when a prior send left someone out. Flips the editor
                            to just-the-misses; flips back to the full account. */}
                        {missedCount > 0 &&
                            (resendMode === "all" ? (
                                <button
                                    type="button"
                                    onClick={() => setResendMode("missed")}
                                    className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                                >
                                    ↻ Resend to who missed it ({missedCount})
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setResendMode("all")}
                                    className="rounded-md border border-border bg-muted px-2 py-1 text-[11px] font-semibold text-foreground/70 hover:bg-muted/70"
                                >
                                    ← Back to everyone
                                </button>
                            ))}
                    </div>
                    {resendMode === "missed" && (
                        <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                            Showing only the {missedCount} recipient{missedCount === 1 ? "" : "s"} who didn&apos;t get
                            the last send. Sending now reaches just them — nobody who already received it is emailed
                            again.
                        </p>
                    )}
                    {data.customerId ? (
                        <SendRecipientEditor
                            jobId={jobId}
                            customerId={data.customerId}
                            include={{
                                invoices: inc.invoice,
                                estimates: inc.estimate,
                                reports: inc.reports,
                                photos: inc.photos,
                            }}
                            onChange={handlePlanChange}
                            onMissingDocsChange={handleMissing}
                            onlyMissed={resendMode === "missed"}
                            onMissedCountChange={handleMissedCount}
                        />
                    ) : (
                        <p className="mt-1.5 text-[12.5px] text-amber-700 dark:text-amber-300">
                            No customer on this job — add one before sending.
                        </p>
                    )}
                </div>
            </div>

            {/* The email + review + send */}
            <div className="space-y-3 lg:sticky lg:top-2 lg:self-start">
                <div className="rounded-xl border border-border bg-card p-3.5">
                    <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                        <Mail className="h-3 w-3" /> The email (edit before it goes)
                    </div>
                    <label className="mt-2 block text-[11px] font-semibold text-foreground/60">Subject</label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-8 text-sm" />
                    <label className="mt-2 block text-[11px] font-semibold text-foreground/60">Message</label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={8}
                        className="mt-1 text-sm leading-relaxed"
                    />
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                        {sizeMessage
                            ? "This is exactly what the customer reads. The invoice and estimate are attached, the reports go as one \u201cView your test reports\u201d button, and a \u201cLeave us a Google review\u201d button is added at the bottom."
                            : "This is exactly what the customer reads. The reports, invoice, estimate and photos are attached automatically underneath, and a \u201cLeave us a Google review\u201d button is added at the bottom."}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Button
                            size="sm"
                            variant={draftDirty ? "default" : "outline"}
                            className="h-7 gap-1 px-2 text-[11px]"
                            disabled={saveDraft.isPending || !draftDirty}
                            onClick={() => saveDraft.mutate()}
                        >
                            {saveDraft.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Check className="h-3.5 w-3.5" />
                            )}
                            {draftDirty ? "Save draft" : "Saved"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 px-2 text-[11px]"
                            disabled={reviewEmail.isPending || !message.trim()}
                            onClick={() => reviewEmail.mutate()}
                        >
                            {reviewEmail.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Sparkles className="h-3.5 w-3.5" />
                            )}
                            Review with AI
                        </Button>
                        <span className="text-[10.5px] text-muted-foreground">
                            {savedMeta
                                ? draftDirty
                                    ? `Unsaved edits${savedMeta.byName ? ` — last saved by ${savedMeta.byName}` : ""}`
                                    : `Saved${savedMeta.byName ? ` by ${savedMeta.byName}` : ""} — the office sees this`
                                : "Save so the office picks up your wording"}
                        </span>
                    </div>
                    {coach?.available && (
                        <div className="mt-2 rounded-lg border border-violet-300 bg-violet-50/70 p-2.5 text-[12px] dark:border-violet-500/40 dark:bg-violet-900/20">
                            {coach.tone && (
                                <p className="font-semibold text-violet-800 dark:text-violet-200">{coach.tone}</p>
                            )}
                            {coach.issues && coach.issues.length > 0 ? (
                                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-foreground/75">
                                    {coach.issues.map((it, i) => (
                                        <li key={i}>{it}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mt-1 text-foreground/70">Reads well — nothing to fix.</p>
                            )}
                            {coach.rewrite && (
                                <div className="mt-2 rounded border border-border bg-background/70 p-2">
                                    <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Suggested wording
                                    </div>
                                    <p className="mt-1 font-semibold">{coach.rewrite.subject}</p>
                                    <p className="mt-0.5 whitespace-pre-wrap text-foreground/75">
                                        {coach.rewrite.message}
                                    </p>
                                    <div className="mt-2 flex gap-2">
                                        <Button
                                            size="sm"
                                            className="h-7 bg-violet-600 px-2 text-[11px] text-white hover:bg-violet-700"
                                            onClick={applyRewrite}
                                        >
                                            Use this wording
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-[11px]"
                                            onClick={() => setCoach(null)}
                                        >
                                            Keep mine
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {!coach.rewrite && (
                                <button
                                    className="mt-1.5 text-[11px] font-semibold text-foreground/55 hover:text-foreground"
                                    onClick={() => setCoach(null)}
                                >
                                    Dismiss
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Review & send */}
                <div className="rounded-xl border border-border bg-card p-3.5">
                    <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                        Review &amp; send
                    </div>
                    <ul className="mt-1.5 space-y-0.5 text-[12px] text-foreground/75">
                        {inc.invoice && (
                            <li>
                                • Invoice {m.invoice.paid ? "(paid)" : ""}
                                {data.canViewMoney && m.invoice.balance != null ? ` — ${money(m.invoice.balance)}` : ""}
                            </li>
                        )}
                        {(usePerReportPicker ? reportsSending.length > 0 : inc.reports && customerReportCount > 0) && (
                            <li>
                                • {usePerReportPicker ? reportsSending.length : customerReportCount} test report
                                {(usePerReportPicker ? reportsSending.length : customerReportCount) === 1 ? "" : "s"}
                                {sizeMessage ? " — as a link (too big to attach)" : ""}
                            </li>
                        )}
                        {cityFilingReport && (
                            <li className="text-muted-foreground">
                                • City-filing copy ({cityFilingReport.file_name || "form"}) — to the city, not the
                                customer
                            </li>
                        )}
                        {inc.estimate && m.estimate && <li>• {m.estimate.label}</li>}
                        {m.woState && <li>• Work order ({m.woState === "paper" ? "paper" : "generated"})</li>}
                        {inc.photos && (
                            <li>
                                • {photosSending} photo{photosSending === 1 ? "" : "s"}
                            </li>
                        )}
                        {includedUploads.map((u) => (
                            <li key={u.id}>• {u.file_name || "Additional file"}</li>
                        ))}
                    </ul>
                    {missingDocs.length === 0 && plan.length > 0 ? (
                        // #833: this caption and the recipients panel above must
                        // never disagree — both read summarizeSendPlan. The
                        // audit caught "3 emails — one per recipient" here while
                        // the panel truthfully said "1 email — 1 shared thread".
                        <p className="mt-1.5 text-[12px] font-semibold text-foreground/80" data-testid="send-caption">
                            {(() => {
                                const s = summarizeSendPlan(plan);
                                return `${s.emailCount} email${s.emailCount === 1 ? "" : "s"} — ${s.modeLabel}.`;
                            })()}
                        </p>
                    ) : (
                        <p className="mt-1.5 text-[12px] text-amber-700 dark:text-amber-300">
                            {plan.length === 0
                                ? "Add at least one recipient before sending."
                                : `Assign every document to a recipient first (${missingDocs.join(", ")}).`}
                        </p>
                    )}
                    {data.reportsWithoutTests?.blocked ? (
                        <div className="mt-3">
                            <ReportsWithoutTestsGuard jobId={jobId} reports={data.reportsWithoutTests.reports} />
                        </div>
                    ) : (
                        <>
                            <Button
                                className="mt-3 w-full bg-teal-700 text-white hover:bg-teal-800"
                                disabled={!canOpenConfirm}
                                onClick={() => setShowConfirm(true)}
                            >
                                {send.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                                Send packet
                            </Button>
                            <p className="mt-2 text-[11px] text-muted-foreground">
                                This is the last step before the customer hears from us — one more review pops up before
                                anything is emailed.
                            </p>
                            {/* 2026-10-07: delivered by portal, mail or by hand — record it
                        instead of emailing staff. */}
                            <Button
                                variant="outline"
                                className="mt-2 w-full border-primary/50 text-primary hover:bg-primary/10"
                                disabled={send.isPending}
                                onClick={() => setOtherWayOpen(true)}
                                data-testid="closeout-sent-another-way"
                            >
                                Already sent another way
                            </Button>
                        </>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground">
                        The customer already has it (portal, mail, by hand)? Record it. Nobody gets an email.
                    </p>
                </div>
            </div>
            {/* Preview-before-send dialogs — reports/WO by attachment id, invoice/
                estimate by doc id, photos inline. What you preview is what sends. */}
            <AttachmentPreviewDialog
                open={!!previewAttachment}
                onClose={() => setPreviewAttachment(null)}
                attachmentId={previewAttachment?.id ?? null}
                fileName={previewAttachment?.name ?? null}
            />
            <DocumentPreviewDialog
                open={!!previewDoc}
                onClose={() => setPreviewDoc(null)}
                kind={previewDoc?.kind ?? "invoice"}
                documentId={previewDoc?.id ?? null}
            />
            <Dialog
                open={!!previewPhoto}
                onClose={() => setPreviewPhoto(null)}
                title={photoCaption(data.photoList.find((p) => p.id === previewPhoto) ?? {})}
            >
                {previewPhoto && (
                    <PhotoWithSave
                        id={previewPhoto}
                        caption={photoCaption(data.photoList.find((p) => p.id === previewPhoto) ?? {})}
                    />
                )}
            </Dialog>

            {/* Last-look confirmation — the real send only fires from inside here.
                Reviews exactly who gets emailed, what's attached, and the subject
                so a misclick can't email the customer before the office is ready. */}
            <Dialog
                open={showConfirm}
                // While the send is in flight, don't let Esc / backdrop / the X
                // dismiss into an ambiguous state — the button drives it.
                onClose={() => {
                    if (!send.isPending) setShowConfirm(false);
                }}
                title="Send this packet?"
                className="max-w-xl"
            >
                <div className="space-y-4 text-[13px]">
                    {(() => {
                        const s = summarizeSendPlan(plan);
                        return (
                            <p className="text-foreground/70" data-testid="confirm-send-summary">
                                This emails the recipient{plan.length === 1 ? "" : "s"} below —{" "}
                                <span className="font-semibold text-foreground">
                                    {s.emailCount} email{s.emailCount === 1 ? "" : "s"}
                                </span>
                                , {s.modeLabel}. Give it one last look, then confirm or go back.
                            </p>
                        );
                    })()}

                    {/* Who it's going to — every recipient + exactly their documents. */}
                    <div>
                        <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                            <Mail className="h-3 w-3" /> Going to
                        </div>
                        <ul className="mt-1.5 space-y-1.5">
                            {plan.map((r) => (
                                <li key={r.value} className="rounded-lg border border-border bg-muted/30 px-2.5 py-1.5">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                        <span className="break-all font-semibold">{r.value}</span>
                                        {r.contactName && (
                                            <span className="text-[11px] text-foreground/55">({r.contactName})</span>
                                        )}
                                        {r.source === "one_time" && (
                                            <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-semibold text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                                                one-time
                                            </span>
                                        )}
                                        {r.tier === "legacy" && (
                                            <span className="flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                                                <AlertTriangle className="h-2.5 w-2.5" /> unverified
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-0.5 text-[11.5px] text-foreground/65">
                                        gets {docsSummary(r.docs) || "—"}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {/* #757 (audit): "unverified" used to be a bare chip you had
                            to hover to understand — right before real money went to
                            that address. Say what it means, inline. */}
                        {plan.some((r) => r.tier === "legacy") && (
                            <p className="mt-1.5 text-[11.5px] text-amber-800/90 dark:text-amber-300/90">
                                <b>Unverified</b> = carried over from the legacy customer record and never confirmed.
                                Double-check the address before relying on it — fix it under Manage contacts if it's
                                wrong.
                            </p>
                        )}
                    </div>

                    {/* What's attached — the full packet manifest. */}
                    <div>
                        <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                            What&apos;s attached
                        </div>
                        <ul className="mt-1.5 space-y-0.5 text-foreground/80">
                            {inc.invoice && (
                                <li>
                                    • Invoice {m.invoice.paid ? "(paid)" : ""}
                                    {data.canViewMoney && m.invoice.balance != null
                                        ? ` — ${money(m.invoice.balance)}`
                                        : ""}
                                </li>
                            )}
                            {(usePerReportPicker
                                ? reportsSending.length > 0
                                : inc.reports && customerReportCount > 0) && (
                                <li>
                                    • {usePerReportPicker ? reportsSending.length : customerReportCount} test report
                                    {(usePerReportPicker ? reportsSending.length : customerReportCount) === 1
                                        ? ""
                                        : "s"}
                                    {sizeMessage ? " — as a link (too big to attach)" : ""}
                                </li>
                            )}
                            {cityFilingReport && (
                                <li className="text-muted-foreground">
                                    • City-filing copy ({cityFilingReport.file_name || "form"}) — to the city, not the
                                    customer
                                </li>
                            )}
                            {m.woState && <li>• Work order ({m.woState === "paper" ? "paper" : "generated"})</li>}
                            {inc.estimate && m.estimate && <li>• {m.estimate.label}</li>}
                            {inc.photos && photosSending > 0 && (
                                <li>
                                    • {photosSending} photo{photosSending === 1 ? "" : "s"}
                                </li>
                            )}
                            {includedUploads.map((u) => (
                                <li key={u.id}>• {u.file_name || "Additional file"}</li>
                            ))}
                        </ul>
                    </div>

                    {/* #756: money that could slip — failed-device work no
                        estimate line covers. Requires a deliberate tick; the
                        acknowledgment rides the send's audit trail. */}
                    {estimateGaps.length > 0 && (
                        <div className="rounded-lg border-l-[3px] border-amber-600 bg-amber-50/60 px-3 py-2.5 dark:bg-amber-900/15">
                            <p className="font-semibold text-amber-900 dark:text-amber-200">
                                ⚠ Failed-device work not covered by any estimate
                            </p>
                            <ul className="mt-1 space-y-0.5 text-[12.5px] text-amber-900/90 dark:text-amber-200/90">
                                {estimateGaps.map((gap) => (
                                    <li key={gap}>• {gap}</li>
                                ))}
                            </ul>
                            <p className="mt-1 text-[11.5px] text-amber-800/80 dark:text-amber-300/80">
                                These failure reasons don&apos;t match any estimate line on this job. If they should be
                                quoted, go back to the Estimate station first — nothing here charges the customer for
                                them.
                            </p>
                            <label className="mt-2 flex items-start gap-2 text-[12.5px] font-semibold text-amber-900 dark:text-amber-200">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 rounded"
                                    checked={gapAck}
                                    onChange={(e) => setGapAck(e.target.checked)}
                                />
                                I see this — send the packet anyway
                            </label>
                        </div>
                    )}

                    {/* The subject — catch a wrong subject line before it goes. */}
                    <div>
                        <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                            Email subject
                        </div>
                        <p className="mt-1 break-words rounded-lg border border-border bg-muted/30 px-2.5 py-1.5 font-medium">
                            {subject.trim() || <span className="text-amber-700 dark:text-amber-300">(empty)</span>}
                        </p>
                    </div>

                    {/* Test-to-me: see the real report in your own inbox before
                        the customer gets it. Non-destructive — customer unaffected. */}
                    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-2.5 py-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-[12px] text-foreground/70">
                                Want to see it first? Send the report to your own inbox — the customer won&apos;t get
                                anything.
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={testSend.isPending || send.isPending}
                                onClick={() => testSend.mutate()}
                            >
                                {testSend.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                                Send a test copy to me
                            </Button>
                        </div>
                        {testSend.data && (
                            <div className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[12px] text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
                                <p className="font-semibold">
                                    ✓ Test sent to {testSend.data.sentTo ?? "you"} — {testSend.data.attachments.length}{" "}
                                    file{testSend.data.attachments.length === 1 ? "" : "s"}
                                    {testSend.data.duplicatesRemoved > 0
                                        ? ` (collapsed ${testSend.data.duplicatesRemoved} duplicate${testSend.data.duplicatesRemoved === 1 ? "" : "s"})`
                                        : ", no duplicates"}
                                    .
                                </p>
                                <ul className="mt-0.5 space-y-0.5">
                                    {testSend.data.attachments.map((a, i) => (
                                        <li key={i}>
                                            • {a.filename} ({Math.round(a.bytes / 1024)} KB)
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* What this packet does to the customer's budget — informs, never blocks. */}
                    {showConfirm && data.canViewMoney && (
                        <>
                            {inc.invoice && m.invoice.id && <BudgetImpactLine invoiceId={m.invoice.id} />}
                            {inc.estimate && m.estimate && <BudgetImpactLine estimateId={m.estimate.id} />}
                        </>
                    )}

                    <div className="flex flex-col-reverse gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
                        <Button
                            variant="outline"
                            className="sm:min-w-[110px]"
                            disabled={send.isPending}
                            onClick={() => setShowConfirm(false)}
                        >
                            Go back
                        </Button>
                        <Button
                            className="bg-teal-700 text-white hover:bg-teal-800 sm:min-w-[150px]"
                            disabled={!canSend}
                            onClick={() => send.mutate()}
                        >
                            {send.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Yes, send it now
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Helper Mode close-out failsafe — the Yes/No office-review had and
                closeout was missing, so a paired-helper job dead-ended on send.
                Answering clears the gate and re-fires the send from the still-open
                confirm modal, so the office finishes in one flow. */}
            <Dialog
                open={!!helperPresencePrompt}
                onClose={() => {
                    if (!answerHelperPresence.isPending) setHelperPresencePrompt(null);
                }}
                title="Was the helper on this job?"
            >
                <div className="space-y-3 text-[13px]">
                    <p className="text-foreground/80">
                        {(() => {
                            const names = (helperPresencePrompt?.helpers ?? [])
                                .map((h) => h.name)
                                .filter(Boolean)
                                .join(" and ");
                            return `${names || "This tech's paired helper"} was paired with this tech, but no helper clock-in was recorded on this job. Approval is blocked until you answer.`;
                        })()}
                    </p>
                    <p className="text-xs text-foreground/60">
                        Yes credits the helper with this job&apos;s device points; No records they weren&apos;t on site.
                        Either answer unblocks the send.
                    </p>
                </div>
                <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button
                        variant="outline"
                        disabled={answerHelperPresence.isPending}
                        onClick={() => answerHelperPresence.mutate("no")}
                    >
                        No — not on this job
                    </Button>
                    <Button
                        disabled={answerHelperPresence.isPending}
                        onClick={() => answerHelperPresence.mutate("yes")}
                    >
                        {answerHelperPresence.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            "Yes — they were here"
                        )}
                    </Button>
                </div>
            </Dialog>

            {/* #1250: the job can't close until every device that needs a permit
                is answered. The card is rendered here rather than linked to, so
                the office answers and re-sends without leaving the flow. */}
            <Dialog
                open={!!permitGate}
                onClose={() => {
                    if (!send.isPending) setPermitGate(null);
                }}
                title="This job still needs a permit answer"
            >
                {permitGate && jobId ? (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{permitGate.message}</p>
                        <PermitCard jobId={jobId} canManage={canManageJobs} />
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="outline" onClick={() => setPermitGate(null)}>
                                Close
                            </Button>
                            <Button
                                disabled={send.isPending}
                                onClick={() => {
                                    setPermitGate(null);
                                    send.mutate({});
                                }}
                            >
                                Answered — send now
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Dialog>

            {/* CE-1: some selected files won't ride (privacy/size). "Send anyway"
                replays the send with the confirm flag — same shared warning the
                office-review send used, so nothing drops silently. */}
            <Dialog
                open={!!dropGate}
                onClose={() => {
                    if (!send.isPending) setDropGate(null);
                }}
                title="Some files won't be attached"
            >
                {dropGate ? (
                    <DroppedAttachmentsWarning
                        dropped={dropGate.dropped}
                        isPending={send.isPending}
                        onSendAnyway={() => {
                            setDropGate(null);
                            // Keep any confirmation already given (staff-only).
                            send.mutate({ ...(send.variables ?? {}), confirmDroppedAttachments: true });
                        }}
                        onCancel={() => setDropGate(null)}
                    />
                ) : null}
            </Dialog>
            <Dialog
                open={!!staffOnlyGate}
                onClose={() => {
                    if (!send.isPending) setStaffOnlyGate(null);
                }}
                title="No customer email on file"
            >
                {staffOnlyGate ? (
                    <StaffOnlySendGate
                        refusal={staffOnlyGate}
                        isPending={send.isPending}
                        onSendStaffOnly={() => {
                            setStaffOnlyGate(null);
                            send.mutate({ ...(send.variables ?? {}), staffOnlyConfirmed: true });
                        }}
                        onSentAnotherWay={() => {
                            setStaffOnlyGate(null);
                            setOtherWayOpen(true);
                        }}
                        onCancel={() => setStaffOnlyGate(null)}
                    />
                ) : null}
            </Dialog>
            <SentAnotherWayDialog
                jobId={jobId}
                open={otherWayOpen}
                onClose={() => setOtherWayOpen(false)}
                onSaved={() => {
                    setShowConfirm(false);
                    refreshCloseOutQueue(qc, { now: true });
                    onSent();
                }}
            />
        </div>
    );
}

// Additional-files uploader for Send-off (rip cord 3). A drag-and-drop zone +
// Upload button that posts multipart to /close-out/jobs/:jobId/uploads. The
// uploaded-files list lets the office pick which ride the packet (all on by
// default) and preview each. Uploaded files ride via the CE-2 attachmentIds
// path — this never touches the per-device report selection.
function AdditionalFilesCard({
    jobId,
    woApplicable = false,
    woSlotEmpty = false,
    woNumber = null,
    uploads,
    uploadOn,
    toggleUpload,
    onPreview,
    onUploaded,
}: {
    jobId: string;
    /** vendor_work_order job — asks about WO-looking uploads (threads/wo-in-wrong-slot). */
    woApplicable?: boolean;
    /** No paper WO in the Work Order slot yet. */
    woSlotEmpty?: boolean;
    woNumber?: string | null;
    uploads: Array<{ id: string; file_name: string | null; mime_type: string | null }>;
    uploadOn: (id: string) => boolean;
    toggleUpload: (id: string) => void;
    onPreview: (id: string, name: string | null) => void;
    onUploaded: () => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    // "No, keep it as an additional file" — remembered for this screen only.
    const [notWo, setNotWo] = useState<Set<string>>(new Set());
    // A signed WO dropped HERE never satisfied the packet hold (15 held packets,
    // 2026-09-30). While the slot is empty, ask about anything that looks like one.
    const askAbout =
        woApplicable && woSlotEmpty ? uploads.filter((u) => !notWo.has(u.id) && isWorkOrderLooking(u, woNumber)) : [];
    const useAsWo = useMutation({
        mutationFn: (id: string) =>
            api.post<{ release: "released" | "not_parked" | "waiting" | "failed" }>(
                `/api/attachments/${id}/use-as-work-order`,
                {},
            ),
        onSuccess: (r) => {
            if (r.release === "failed") toast.error("It's in the Work Order slot, but the packet didn't reach anyone.");
            else if (r.release === "released") toast.success("In the Work Order slot — the held packet went out.");
            else toast.success("Moved to the Work Order slot.");
            onUploaded();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't use that file as the work order."),
    });
    // A city report dropped here files as the official report on THIS job;
    // one that names no single device asks "Which device?" (2026-10-06).
    const makeOfficial = useMakeOfficial(jobId, { onDone: onUploaded });
    const upload = useMutation({
        mutationFn: (files: File[]) => {
            const fd = new FormData();
            for (const f of files) fd.append("files", f);
            return api.postForm<{
                uploaded: number;
                duplicates: number;
                filedReports?: Array<{ serial: string; duplicate: boolean }>;
                needsDevice?: Array<{ attachmentId: string; fileName: string; candidates: ReportDeviceCandidate[] }>;
                releasedJobIds?: string[];
            }>(`/api/close-out/jobs/${jobId}/uploads`, fd);
        },
        onSuccess: (r) => {
            const parts: string[] = [];
            const filed = (r.filedReports ?? []).filter((f) => !f.duplicate);
            if (filed.length > 0)
                parts.push(
                    filed.length === 1
                        ? `Filed as the official report for S/N ${filed[0].serial}`
                        : `${filed.length} official reports filed`,
                );
            if (r.uploaded > 0) parts.push(`${r.uploaded} file${r.uploaded === 1 ? "" : "s"} added`);
            if (r.duplicates > 0) parts.push(`${r.duplicates} already on the job`);
            if ((r.releasedJobIds ?? []).includes(jobId)) parts.push("packet sent");
            toast.success(parts.join(" · ") || "Uploaded.");
            const ask = r.needsDevice?.[0];
            if (ask) makeOfficial.askFor(ask.attachmentId, ask.fileName, ask.candidates);
            onUploaded();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Upload failed."),
    });
    const pick = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        upload.mutate(Array.from(files));
        if (inputRef.current) inputRef.current.value = "";
    };
    return (
        <div className="rounded-xl border border-border bg-card p-3.5">
            {makeOfficial.element}
            <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-foreground/60">
                <Upload className="h-3 w-3" /> Additional files
            </div>
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    pick(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
                }}
                className={cn(
                    "mt-2 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-5 text-center transition-colors",
                    dragOver
                        ? "border-teal-600 bg-teal-50/60 dark:bg-teal-900/20"
                        : "border-border hover:border-teal-600/60 hover:bg-muted/40",
                )}
            >
                {upload.isPending ? (
                    <span className="flex items-center gap-2 text-[12.5px] text-foreground/70">
                        <Loader2 className="h-4 w-4 animate-spin" /> Uploading&hellip;
                    </span>
                ) : (
                    <>
                        <Upload className="h-5 w-5 text-foreground/40" />
                        <span className="text-[12.5px] font-semibold text-foreground/80">
                            Drop files here, or click to browse
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                            PDFs, photos, signed authorizations &mdash; up to 25&nbsp;MB each
                        </span>
                        {woApplicable && (
                            <span className="text-[11px] font-semibold text-orange-800 dark:text-orange-300">
                                Signed work orders go in the Work Order slot &mdash; files here don&apos;t release the
                                packet.
                            </span>
                        )}
                    </>
                )}
                <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => pick(e.target.files)} />
            </div>
            <div className="mt-2 flex justify-end">
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-[11px]"
                    disabled={upload.isPending}
                    onClick={() => inputRef.current?.click()}
                >
                    {upload.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <Upload className="mr-1 h-3.5 w-3.5" />
                    )}
                    Upload files
                </Button>
            </div>
            {askAbout.map((u) => (
                <div
                    key={`ask-${u.id}`}
                    data-testid="wo-ask"
                    className="mt-2 rounded-lg border border-orange-300/70 bg-orange-50 px-3 py-2 text-[12.5px] text-orange-900 dark:border-orange-500/40 dark:bg-orange-950/40 dark:text-orange-200"
                >
                    <span className="font-semibold">{u.file_name}</span> looks like the signed work order. Is it?
                    <div className="mt-1.5 flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            className="h-7 bg-orange-700 px-2.5 text-[11px] text-white hover:bg-orange-800"
                            disabled={useAsWo.isPending}
                            onClick={() => useAsWo.mutate(u.id)}
                        >
                            Yes &mdash; put it in the Work Order slot
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-[11px]"
                            onClick={() => setNotWo((prev) => new Set(prev).add(u.id))}
                        >
                            No, keep it as an additional file
                        </Button>
                    </div>
                </div>
            ))}
            {uploads.length > 0 && (
                <div className="mt-2 space-y-1 border-t border-dashed border-border pt-2">
                    <div className="text-[11px] font-semibold text-foreground/55">
                        Uploaded &mdash; ticked files ride the packet
                    </div>
                    {uploads.map((u) => (
                        <div key={u.id} className="flex items-center gap-2 text-[12.5px]">
                            <input
                                type="checkbox"
                                checked={uploadOn(u.id)}
                                onChange={() => toggleUpload(u.id)}
                                className="h-3.5 w-3.5 accent-teal-600"
                            />
                            <span className="min-w-0 flex-1 truncate">{u.file_name || "file"}</span>
                            <button
                                type="button"
                                onClick={() => onPreview(u.id, u.file_name)}
                                title="Preview file"
                                className="flex h-5 w-5 flex-none items-center justify-center rounded-full text-foreground/50 hover:bg-muted hover:text-foreground"
                            >
                                <Eye className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ═══ STATION 3 — ESTIMATE (Phase B) ═════════════════════════════════════
// The triangle (what failed / what the tech said / what we're asking, per-line
// mismatch flags) → "Send to Corbin" → he approves from his queue. Only an
// approved-and-current estimate rides any send (CLOSEOUT-REDESIGN.md 2 & 4b).

interface EstimateAskLine {
    id: string;
    description: string | null;
    qty: number;
    unit_price: number;
    // Cost basis for the cost × multiplier = price editor. null = no cost on
    // file (the editor treats it as cost = price, multiplier = 1).
    unit_cost: number | null;
    // Comparative quote (#380): which option this line belongs to (null = base).
    optionId?: string | null;
    // Per-device quote (#1120): the device this line is for. A line with an
    // assetId and NO optionId is external component work on an optioned
    // device — added to whichever tier they pick, never a choice of its own.
    assetId?: string | null;
    /** Labor line hours × techs × rate (#1823 S3); null on other lines. */
    labor?: LaborShape | null;
    ask: boolean;
}

/**
 * One failed device's repair menu (#1120, Station 3 per #1348).
 *
 * Distinct from `EstimateOption` above, which is #380's estimate-level
 * Repair-vs-Replacement pair. The money rule differs too: a device option's
 * total is its OWN lines only, because folding in the estimate's shared base
 * lines would show that shared cost inside every device's price.
 */
interface EstimateDeviceOption {
    id: string;
    optionKey: string;
    label: string;
    sortOrder: number;
    subtotal: number;
    taxAmount: number;
    total: number;
    isSelected: boolean;
    isDecline: boolean;
    lineIds: string[];
}
interface EstimateDeviceGroup {
    assetId: string;
    device: string;
    serial: string | null;
    location: string | null;
    selectedOptionId: string | null;
    options: EstimateDeviceOption[];
    shape: {
        realOptionCount: number;
        onlyOption: boolean;
        notGuaranteed: boolean;
        tiersShort: boolean;
    };
    externalLineIds: string[];
}
// Comparative Repair-vs-Replacement quote option (#380).
interface EstimateOption {
    id: string;
    option_key: string;
    label: string;
    sort_order: number;
    subtotal: number;
    taxAmount: number;
    total: number;
    lineIds: string[];
}
interface StationEstimateData {
    failed: Array<{
        assetId: string;
        device: string;
        serial: string | null;
        reasons: string[];
        techSaid: string[];
        reportReviewed: boolean;
        /** #1958: the ONE repair-or-replace rule, as the auto-draft read it. */
        rule?: CloseoutRuleView;
    }>;
    /** #1958: tech-added devices on the drafts that the office hasn't OK'd. */
    techAddedNeedsOk?: TechAddedNeedsOk | null;
    estimates: Array<{
        id: string;
        displayNumber: string | null;
        title: string | null;
        // The customer-facing SCOPE OF WORK on the quote PDF. Close-out is
        // where the office decides what to say, so it is read and written
        // here rather than in another tab.
        notes: string | null;
        subtotal: number;
        taxAmount: number;
        total: number;
        status: string;
        // Comparative quote (#380): 'single' (flat) or 'choose_one' (two options).
        selectionMode: string;
        selectedOptionId: string | null;
        options: EstimateOption[];
        // Per-device repair options (#1120). null for the flat and comparative
        // shapes — which is every estimate today, and renders exactly as before.
        deviceGroups: EstimateDeviceGroup[] | null;
        lines: EstimateAskLine[];
        gaps: string[];
        approvalState: "none" | "pending" | "approved" | "delta";
        snapshotTotal: number | null;
        sentAt: string | null;
        approvedAt: string | null;
        approvedByName: string | null;
        // Make It Professional #1: when the quote reached the customer, and
        // its own same-day send when one is queued.
        customerSentAt?: string | null;
        autoSend?: QuoteAutoSend | null;
        // Choices the office dropped from this quote; Rebuild brings them back.
        removedChoices?: string[];
        // Handoff (Issue #517): who this estimate was passed off to build.
        assignedEstimator: { userId: string; name: string | null } | null;
        /** A draft with no lines on a job with a failed device: why it's empty
         *  (FWISD Meadowbrook #102678833). Null otherwise. */
        emptyWhy?: string | null;
    }>;
    photos: CloseOutPhoto[];
    documents?: CloseOutDocument[];
    canViewMoney: boolean;
    // When the customer is on a negotiated billing profile the markup
    // multiplier is locked in the editor (the agreed rate governs).
    rateCardActive: boolean;
    // Which repair-options world this job is in (#1348). Two states that look
    // identical on the estimate and must not be confused: the feature being off
    // (the default), and this customer being set to one recommended repair.
    repairOptions?: { featureEnabled: boolean; customerMode: "three" | "single" | "hourly" };
    // A recorded "no estimate needed" decision (Issue #517) — present even when
    // the estimate itself is cancelled/absent, so the station reads as resolved.
    noQuoteDecision: {
        reason: string;
        shareWithCustomer: boolean;
        byName: string | null;
        at: string | null;
    } | null;
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The per-device menu for this estimate, or null for the flat and comparative
 *  shapes. A quote that seeded no groups is not "per-device with zero devices" —
 *  it is an ordinary quote, and must render as one. */
const perDeviceGroupsOf = (est: StationEstimateData["estimates"][number]): EstimateDeviceGroup[] | null =>
    est.deviceGroups && est.deviceGroups.length > 0 ? est.deviceGroups : null;

/**
 * Station 3 rendering a per-device repair quote (#1120, built per #1348).
 *
 * Until now the office had no screen for this shape at all. `closeOutWorkspace`
 * only ever loaded options for #380's estimate-level comparative quote, so a
 * per-device estimate — which never sets `selection_mode` and stays 'single' —
 * fell into the flat branch and rendered every tier of every device as one
 * undifferentiated pile of lines, under a total that counted only the selected
 * ones. The office could not see what the customer was being offered.
 *
 * Three things the drawing (round 4, `threads/repair-options-per-device/`)
 * established and this honours:
 *
 *  1. **No estimate total on this screen.** The customer's choices decide it.
 *     A number here would be a number nobody has agreed to.
 *  2. **A decline is a dash, never `$0.00`.** `$0.00` reads as a free repair,
 *     and it is a refusal, not a product.
 *  3. **The only-option device is never badged "recommended".** The
 *     recommendation falls back to the most thorough tier we can price; where
 *     that is Clean & Flush, badging it recommends the one tier the business
 *     explicitly does not stand behind (interview Q8: *"Could work. Not
 *     guaranteed — it only helps if the problem is debris."*). It stays
 *     pre-selected, because a group with no selection prices at zero, but it is
 *     labelled for what it is.
 *
 * Option lines stay collapsed to one all-in price per tier until "Edit inline"
 * is on — four devices at four tiers is sixteen priced options, and the round-2
 * proof of that at full detail is why the compact row exists.
 */
export function PerDeviceQuote({
    est,
    groups,
    failed,
    editing,
    canViewMoney,
    canViewMarkup,
    rateCardActive,
    onSaved,
}: {
    est: { id: string; lines: EstimateAskLine[] };
    groups: EstimateDeviceGroup[];
    failed: StationEstimateData["failed"];
    editing: boolean;
    canViewMoney: boolean;
    canViewMarkup: boolean;
    rateCardActive: boolean;
    onSaved: () => void;
}) {
    const lineById = new Map(est.lines.map((l) => [l.id, l]));
    // #1417 — the last-line refusal, and the way out of it. The office is never
    // told only "no": the two moves that are actually meant are replacing the
    // line (add first, then delete) and dropping the repair altogether. A flat
    // refusal would just route them the long way round to the same broken quote.
    const [emptyGuard, setEmptyGuard] = useState<{
        optionId: string;
        label: string;
        message: string;
    } | null>(null);
    const removeOption = useMutation({
        mutationFn: (optionId: string) => api.delete(`/api/estimates/${est.id}/options/${optionId}`),
        onSuccess: (r: any) => {
            toast.success(
                r?.removedDecline
                    ? "Repair removed — that was the last one, so the device is off the menu."
                    : "Repair removed from this device.",
            );
            setEmptyGuard(null);
            onSaved();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't remove the option."),
    });
    return (
        <div className="mt-2 space-y-2.5">
            <Dialog open={!!emptyGuard} onClose={() => setEmptyGuard(null)} title="That's the last line on this repair">
                <div className="space-y-3 p-1">
                    <p className="text-[12.5px]">{emptyGuard?.message}</p>
                    <div className="rounded-md border border-border bg-muted/30 p-2.5 text-[12px]">
                        <p>
                            <b>Replace it instead.</b> Add the new line first, then delete this one — the option is
                            never empty and the quote is never wrong.
                        </p>
                        <p className="mt-1.5">
                            <b>Or remove the whole repair.</b> Takes this line with it. Use it when the repair
                            isn&apos;t being offered at all; if it&apos;s the last one, the device comes off the menu.
                        </p>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEmptyGuard(null)}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            className="bg-red-600 text-white hover:bg-red-700"
                            disabled={removeOption.isPending}
                            onClick={() => emptyGuard && removeOption.mutate(emptyGuard.optionId)}
                        >
                            {removeOption.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Remove &ldquo;{emptyGuard?.label}&rdquo;
                        </Button>
                    </div>
                </div>
            </Dialog>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 dark:border-emerald-800 dark:bg-emerald-900/20">
                <span className="rounded bg-emerald-700 px-1.5 py-0.5 text-[9.5px] font-extrabold uppercase tracking-wide text-white">
                    Customer picks one per device
                </span>
                <span className="text-[11px] font-medium text-emerald-900 dark:text-emerald-200">
                    Pre-selected below is what they&apos;ll see chosen. Every price is all-in — the repair, its re-test
                    and the city fee.
                </span>
            </div>

            {groups.map((g) => {
                const fail = failed.find((f) => f.assetId === g.assetId);
                const externals = g.externalLineIds.map((id) => lineById.get(id)).filter(Boolean) as EstimateAskLine[];
                const externalTotal = externals.reduce((n, l) => n + l.qty * l.unit_price, 0);
                // #1958: a needed piece with no price is never hidden behind
                // "only option" — it is named, in amber.
                const unpriced = g.options
                    .flatMap((o) => o.lineIds.map((id) => lineById.get(id)?.description))
                    .map(notPricedLabel)
                    .filter((x): x is string => !!x);
                const shapeOk = unpriced.length === 0;
                return (
                    <div
                        key={g.assetId}
                        className="grid gap-2 border-t border-border pt-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]"
                    >
                        {/* Left: the device and what failed. Named by size, make,
                            model and serial — never the asset id (AGENTS.md). */}
                        <div className="min-w-0">
                            <div className="text-[12.5px] font-bold">{g.device}</div>
                            <div className="text-[11px] text-foreground/60">
                                {[g.serial ? `S/N ${g.serial}` : null, g.location].filter(Boolean).join(" · ") ||
                                    "No serial on file"}
                            </div>
                            {(fail?.reasons.length ?? 0) > 0 && (
                                <div className="mt-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
                                    {fail?.reasons.join(", ")}
                                </div>
                            )}
                        </div>

                        {/* Right: the menu. */}
                        <div className="min-w-0 space-y-1">
                            {g.options.map((o) => {
                                const optLines = o.lineIds
                                    .map((id) => lineById.get(id))
                                    .filter(Boolean) as EstimateAskLine[];
                                return (
                                    <div key={o.id}>
                                        <div
                                            className={cn(
                                                // Wrapping, not truncating. The 390px proof
                                                // truncated "Clean & Flush" to "Clean…" on the
                                                // one device where the label carries the most
                                                // weight — the only tier we can offer. Badges
                                                // drop to their own line on a phone instead of
                                                // competing with the name for width.
                                                "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2 py-1.5",
                                                o.isDecline
                                                    ? "border-dashed border-border bg-transparent"
                                                    : o.isSelected
                                                      ? "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/20"
                                                      : "border-border bg-card",
                                            )}
                                        >
                                            <span
                                                aria-hidden
                                                className={cn(
                                                    "h-3 w-3 flex-none rounded-full border-2",
                                                    o.isSelected
                                                        ? "border-emerald-600 bg-emerald-600"
                                                        : "border-foreground/25",
                                                )}
                                            />
                                            <span
                                                className={cn(
                                                    "min-w-0 flex-1 text-[12px]",
                                                    o.isDecline ? "text-foreground/60" : "font-semibold",
                                                )}
                                            >
                                                {o.isDecline
                                                    ? "Not this one — declines the whole service for this device"
                                                    : o.label}
                                            </span>
                                            {/* The badge rules, and the reason this
                                                component exists rather than a count. */}
                                            {shapeOk && g.shape.notGuaranteed && !o.isDecline && (
                                                <span className="order-last w-fit basis-full rounded-full bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-800 sm:order-none sm:basis-auto dark:bg-amber-900/50 dark:text-amber-200">
                                                    only option — not guaranteed
                                                </span>
                                            )}
                                            {shapeOk &&
                                                g.shape.onlyOption &&
                                                !g.shape.notGuaranteed &&
                                                !o.isDecline && (
                                                    <span className="order-last w-fit basis-full rounded-full bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-bold text-slate-700 sm:order-none sm:basis-auto dark:bg-slate-800 dark:text-slate-300">
                                                        only option
                                                    </span>
                                                )}
                                            {o.isSelected && !o.isDecline && !g.shape.onlyOption && (
                                                <span className="order-last w-fit basis-full rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-bold text-emerald-700 sm:order-none sm:basis-auto dark:bg-emerald-900/50 dark:text-emerald-300">
                                                    recommended
                                                </span>
                                            )}
                                            {canViewMoney && (
                                                <span
                                                    className={cn(
                                                        "flex-none text-[12.5px] tabular-nums sm:order-last",
                                                        o.isDecline ? "text-muted-foreground" : "font-extrabold",
                                                    )}
                                                >
                                                    {/* A refusal is not a $0 product. */}
                                                    {o.isDecline ? "—" : money(o.total)}
                                                </span>
                                            )}
                                        </div>
                                        {editing && (
                                            <div className="mt-1 space-y-1 border-l-2 border-border pl-2">
                                                {optLines.map((l, i) => (
                                                    <EstimateLineRow
                                                        key={l.id}
                                                        estimateId={est.id}
                                                        index={i}
                                                        line={l}
                                                        editing={editing}
                                                        canViewMoney={canViewMoney}
                                                        canViewMarkup={canViewMarkup}
                                                        rateCardActive={rateCardActive}
                                                        onSaved={onSaved}
                                                        onLastLineRefused={(message) =>
                                                            setEmptyGuard({ optionId: o.id, label: o.label, message })
                                                        }
                                                    />
                                                ))}
                                                {/* #1418 — until now the only picker on this
                                                    screen was the estimate-level one, so a line
                                                    the office added to fix a repair landed BESIDE
                                                    the menu instead of inside it. Fort Worth ISD
                                                    092678173: four lines deleted, the repair
                                                    re-added from the pricebook, $2,305.21 stranded
                                                    outside the option and the option itself left
                                                    empty at $0.00. The server has taken an
                                                    `option_id` since #380 and the comparative
                                                    branch has passed one all along; this branch
                                                    simply never did. */}
                                                {o.isDecline ? (
                                                    <p className="text-[10.5px] text-foreground/55">
                                                        Nothing can be added to a decline — it carries no lines by
                                                        design. That is what makes declining take the repair, its
                                                        re-test and the city fee off together.
                                                    </p>
                                                ) : (
                                                    <PricebookLinePicker
                                                        estimateId={est.id}
                                                        optionId={o.id}
                                                        addLabel={`Add to ${o.label}`}
                                                        destinationLabel={o.label}
                                                        onAdded={onSaved}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Why the menu is short, said plainly. 111 catalog
                                options load as `gap`, so a two-tier device is
                                ordinary — the office has to be able to tell that
                                from someone having deleted an option. */}
                            {!shapeOk ? (
                                <NotPricedYet labels={unpriced} />
                            ) : g.shape.notGuaranteed ? (
                                <p className="text-[11px] text-amber-700 dark:text-amber-300">
                                    This is the only repair we can offer for this model right now, and it isn&apos;t
                                    guaranteed — it only helps if the problem is debris. Source the parts or send it
                                    as-is.
                                </p>
                            ) : g.shape.tiersShort ? (
                                <p className="text-[11px] text-amber-700 dark:text-amber-300">
                                    {g.shape.realOptionCount} of the 3 repair tiers price for this model — the rest
                                    aren&apos;t sourced. That&apos;s the whole menu, not a missing option.
                                </p>
                            ) : null}

                            {externals.length > 0 && (
                                <div className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 dark:border-amber-800 dark:bg-amber-900/20">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] font-bold text-amber-800 dark:text-amber-200">
                                            Also needed — not a choice
                                        </span>
                                        {canViewMoney && (
                                            <span className="text-[12px] font-extrabold tabular-nums">
                                                {money(externalTotal)}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-0.5 text-[11px] text-amber-800/85 dark:text-amber-200/85">
                                        {externals
                                            .map((l) => l.description)
                                            .filter(Boolean)
                                            .join("; ")}
                                        {" — external part, added to whichever option they pick."}
                                    </p>
                                </div>
                            )}

                            <p className="text-[10.5px] text-foreground/55">
                                Re-test and city fee for this device sit inside every option, and come off with it if
                                it&apos;s declined.
                            </p>
                        </div>
                    </div>
                );
            })}

            <p className="border-t border-border pt-2 text-[11px] text-foreground/60">
                No estimate total — the customer&apos;s choices decide it.
            </p>
        </div>
    );
}

/**
 * Which repair-options world this estimate is in, when it ISN'T per-device
 * (#1348, artboard 4).
 *
 * Two states look identical on a flat estimate and mean opposite things, and
 * confusing them means sending the wrong document: the feature being switched
 * off (the default, and true of every estimate today), versus this customer
 * being deliberately set to one recommended repair. Renders nothing when the
 * feature is off AND the customer is on the menu — that is the ordinary world,
 * and a banner on every flat quote in the system would be noise.
 */
export function RepairOptionsWorld({
    mode,
    status = "draft",
}: {
    mode: NonNullable<StationEstimateData["repairOptions"]>;
    /** The estimate's status: only a draft can be rebuilt. */
    status?: string;
}) {
    if (mode.customerMode === "three" && !mode.featureEnabled) return null;
    const text =
        mode.customerMode === "hourly"
            ? "This customer is quoted by the hour, so they never get the repair menu — the quote is labor from their own rate card."
            : mode.customerMode === "single"
              ? "This customer is set to ONE recommended repair per device — no menu. Set on their rate card, not by billing."
              : // #1958: one plain next step instead of a paragraph about why.
                status === "draft"
                ? "Next step: press Rebuild this quote to get the repair choices for each device. If it still shows one line, those devices need a price added by hand."
                : "This quote already went out with one line per device. Edit the lines if something changed.";
    return (
        <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300">
            {text}
        </div>
    );
}

/**
 * The quote's title + scope of work, read and written from close-out.
 *
 * `notes` is the customer-facing SCOPE OF WORK block on the estimate PDF — the
 * paragraph explaining WHY the work is needed. Close-out is where the office
 * actually decides what the quote says, but the station rendered neither field:
 * the title was fetched and dropped, and the scope text wasn't in the payload
 * at all. So the answer to "what does this quote tell the customer?" was
 * always: open it in another tab.
 *
 * Saves through the existing `PATCH /api/estimates/:id` (office-gated, the same
 * route the estimate page uses) — no new endpoint, and the server's
 * edited-since-sent stamp still fires on a `sent` quote, so the "resend so they
 * see current numbers" nudge keeps working from here too.
 */
function CloseOutQuoteText({
    estimateId,
    title,
    notes,
    editable,
    onSaved,
}: {
    estimateId: string;
    title: string | null;
    notes: string | null;
    editable: boolean;
    onSaved: () => void;
}) {
    const [draft, setDraft] = useState<{ title: string; notes: string } | null>(null);
    const save = useMutation({
        mutationFn: (body: { title: string; notes: string }) => api.patch(`/api/estimates/${estimateId}`, body),
        onSuccess: () => {
            toast.success("Quote wording saved.");
            setDraft(null);
            onSaved();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save the wording."),
    });

    if (draft) {
        return (
            <div className="mb-2.5 rounded-lg border-l-[3px] border-sky-600 bg-sky-50/60 p-3 dark:bg-sky-900/15">
                <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                    What the customer reads
                </div>
                <div className="mt-2 space-y-2">
                    <div>
                        <label className="text-[11px] font-semibold">Title</label>
                        <Input
                            className="mt-0.5 h-8 text-[12.5px]"
                            value={draft.title}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold">Scope of work</label>
                        <Textarea
                            className="mt-0.5 text-[12.5px]"
                            rows={4}
                            value={draft.notes}
                            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                            placeholder="Why this work is needed, in the customer's language. Prints as SCOPE OF WORK on their quote."
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setDraft(null)} disabled={save.isPending}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => save.mutate({ title: draft.title, notes: draft.notes })}
                            disabled={save.isPending}
                        >
                            {save.isPending ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-2.5 rounded-lg border-l-[3px] border-sky-600 bg-sky-50/60 p-3 dark:bg-sky-900/15">
            <div className="flex items-start justify-between gap-2">
                <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                    What the customer reads
                </div>
                {editable && (
                    <button
                        type="button"
                        className="text-[11px] font-semibold text-sky-700 underline underline-offset-2 dark:text-sky-300"
                        onClick={() => setDraft({ title: title ?? "", notes: notes ?? "" })}
                    >
                        Edit wording
                    </button>
                )}
            </div>
            <p className="mt-1.5 text-[12.5px] font-bold">{title || "No title yet."}</p>
            {notes ? (
                <p className="mt-1 whitespace-pre-wrap text-[12.5px] text-foreground/85">{notes}</p>
            ) : (
                <p className="mt-1 text-[12px] text-foreground/55">
                    No scope of work — the quote goes out with no explanation of the work.
                </p>
            )}
        </div>
    );
}

function StationEstimate({
    jobId,
    ball,
}: {
    jobId: string;
    /** Who has the ball: undefined = switch off (the page is exactly as it was). */
    ball?: Ball | null;
}) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { isAdmin } = useAuth();
    const q = useQuery({
        queryKey: ["close-out-estimate", jobId],
        queryFn: () => api.get<StationEstimateData>(`/api/close-out/jobs/${jobId}/estimate`),
    });
    const data = q.data;
    const [editing, setEditing] = useState(false);
    // Second look (10/28): editing an APPROVED quote asks first, because a
    // real change sends it back for approval. null = not asking.
    const [confirmEditFor, setConfirmEditFor] = useState<string | null>(null);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["close-out-estimate", jobId] });
        refreshCloseOutQueue(qc, { jobId });
    };

    const sendToCorbin = useMutation({
        mutationFn: (estimateId: string) => api.post(`/api/close-out/estimates/${estimateId}/send-to-corbin`, {}),
        onSuccess: () => {
            toast.success("Sent to Corbin for approval.");
            refresh();
        },
        onError: () => toast.error("Couldn't send — try again."),
    });
    const approve = useMutation({
        mutationFn: (estimateId: string) =>
            api.post<{ quoteSend?: { queued: boolean; sendAfter: string | null } }>(
                `/api/close-out/estimates/${estimateId}/approve`,
                {},
            ),
        onSuccess: (res) => {
            toast.success(
                res?.quoteSend?.queued
                    ? "Approved. The quote emails to the customer by itself within the hour."
                    : "Estimate approved — it can now ride the packet.",
            );
            refresh();
        },
        // Say why (the quote check, a piece not priced yet, a device to OK).
        onError: (e: any) => toast.error(e?.body?.message ?? "Approve failed."),
    });
    // Start a quote right here off the field report — no need to bounce back to
    // Charges. Same find-or-create endpoint, so it's safe if one already exists.
    const startEstimate = useMutation({
        mutationFn: () => api.post<{ draftedFromTest?: boolean }>(`/api/close-out/jobs/${jobId}/estimate`, {}),
        onSuccess: (res) => {
            // A failed device drafts the quote from the test (10/28); only a
            // job with nothing to draft from starts blank.
            if (res?.draftedFromTest) {
                toast.success("Quote drafted from the test — check it below.");
                refresh();
                return;
            }
            toast.success("Estimate started — add the lines below.");
            refresh();
            setEditing(true);
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't start the estimate."),
    });
    // Turn the estimate into a comparative Repair-vs-Replacement quote (#380):
    // seeds its two options; the office then builds each side and the customer
    // picks exactly one. Idempotent server-side.
    const makeComparative = useMutation({
        mutationFn: (estimateId: string) => api.post(`/api/close-out/estimates/${estimateId}/make-comparative`, {}),
        onSuccess: () => {
            toast.success("Comparative quote — build a Repair option and a Replacement option below.");
            refresh();
            setEditing(true);
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't switch to a comparative quote."),
    });
    // "No estimate needed" rip cord — cancels the estimate so it stops blocking
    // closeout ("Estimate with Corbin"). Reversible via "Start the estimate".
    // Issue #517: capture WHY (required) and optionally share it with the
    // customer. State lives here; the dialog renders once below the cards.
    const [skipTarget, setSkipTarget] = useState<string | null>(null);
    const [skipReason, setSkipReason] = useState("");
    const [skipShare, setSkipShare] = useState(false);
    const openSkip = (estimateId: string) => {
        setSkipTarget(estimateId);
        setSkipReason("");
        setSkipShare(false);
    };
    const skipEstimate = useMutation({
        mutationFn: (vars: { estimateId: string; reason: string; shareWithCustomer: boolean }) =>
            api.post(`/api/close-out/estimates/${vars.estimateId}/skip`, {
                reason: vars.reason,
                shareWithCustomer: vars.shareWithCustomer,
            }),
        onSuccess: () => {
            toast.success("Marked no estimate needed — the job can move on.");
            setSkipTarget(null);
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't skip the estimate."),
    });

    // Hand the estimate off to an estimator to build (Issue #517). The handoff
    // is the gate that makes it theirs; `userId: null` clears it. Owner/office
    // keep full access.
    const assignEstimator = useMutation({
        mutationFn: (vars: { estimateId: string; userId: string | null }) =>
            api.post(`/api/close-out/estimates/${vars.estimateId}/assign`, { userId: vars.userId }),
        onSuccess: (_res, vars) => {
            toast.success(vars.userId ? "Handed off — it's theirs to build now." : "Handoff cleared.");
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't hand off the estimate."),
    });

    // "Rebuild this quote" (#1647): re-draft the repair quote from today's test
    // data, pricebook and customer settings. Estimate-only server-side — no
    // invoice is created, changed or deleted — and a failure puts the old draft
    // back. The confirm dialog is the design (artboard 4 of
    // threads/per-device-option-editing/mockup.html).
    const [rebuildTarget, setRebuildTarget] = useState<StationEstimateData["estimates"][number] | null>(null);
    const rebuildQuote = useMutation({
        mutationFn: (estimateId: string) =>
            api.post<{ displayNumber: string | null }>(`/api/close-out/estimates/${estimateId}/rebuild`, {}),
        onSuccess: (res) => {
            toast.success(
                `Quote rebuilt${res?.displayNumber ? ` — #${res.displayNumber}` : ""}. It needs approving again before it goes out.`,
            );
            setRebuildTarget(null);
            refresh();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't rebuild the quote — nothing was changed."),
    });

    // Approval is EXPLICIT — always (Issue #755, walkthrough-audit Tier 1).
    // There used to be a render-triggered effect here that silently
    // (re-)approved any priced 'none'/'delta' estimate the moment an admin
    // viewed this station, which meant an edited price could ride out with no
    // visible sign-off — defeating the "material edit revokes approval" rule
    // the approval snapshot exists to enforce. Viewing a page must never
    // mutate approval state: the "Approve & attach" / "Re-approve & attach"
    // button is the only path, for admins too.

    if (q.isLoading) {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading the estimate…
            </div>
        );
    }
    if (!data || data.estimates.length === 0) {
        const decision = data?.noQuoteDecision ?? null;
        return (
            <div className="rounded-xl border border-border bg-card p-4">
                {decision ? (
                    // Resolved: someone recorded "no estimate needed" with a
                    // reason (Issue #517). Read as done — not an empty prompt —
                    // so the quote step clears. Reversible via "Actually, quote it".
                    <div className="rounded-lg border-l-[3px] border-emerald-600 bg-emerald-50/70 p-3 dark:bg-emerald-900/15">
                        <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                            <FileCheck2 className="h-3.5 w-3.5" /> No quote needed
                        </div>
                        <p className="mt-1.5 text-[13px] text-foreground/85">
                            {decision.byName ? <b>{decision.byName} said:</b> : <b>Note:</b>} &ldquo;{decision.reason}
                            &rdquo;
                        </p>
                        <p className="mt-1 text-[11.5px] text-foreground/55">
                            {decision.shareWithCustomer
                                ? "The customer is told why in their closeout email."
                                : "Kept internal — not shared with the customer."}
                        </p>
                        <Button
                            size="sm"
                            variant="outline"
                            className="mt-2.5 h-7"
                            disabled={startEstimate.isPending}
                            onClick={() => startEstimate.mutate()}
                        >
                            {startEstimate.isPending ? (
                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Plus className="mr-1 h-3.5 w-3.5" />
                            )}
                            Actually, quote it
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="text-[12.5px] text-foreground/70">
                            No estimate on this job yet. When a failed test creates one (or the office builds one), it
                            shows here for Corbin&apos;s approval before anything rides a send.
                        </p>
                        <p className="mt-1 text-[12.5px] text-foreground/70">
                            Need to quote it now? You can price it straight off the report and photos below — start the
                            estimate and add the lines.
                        </p>
                        <Button
                            size="sm"
                            className="mt-2.5 h-7"
                            disabled={startEstimate.isPending}
                            onClick={() => startEstimate.mutate()}
                        >
                            {startEstimate.isPending ? (
                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Plus className="mr-1 h-3.5 w-3.5" />
                            )}
                            Start the estimate
                        </Button>
                    </>
                )}
                {/* The failed devices, tech notes, photos, and job documents the
                    office would review are shown here too, so a quote can be
                    built without waiting on that review. */}
                <div className="mt-3">
                    <TechPhotoPanel
                        techNotes={
                            data?.failed.map((f) => ({
                                asset_id: f.assetId,
                                device: [f.device, f.serial].filter(Boolean).join(" — ") || "device",
                                overall_result: "fail",
                                remarks: f.techSaid,
                            })) ?? []
                        }
                        photos={data?.photos ?? []}
                        documents={data?.documents ?? []}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {data.techAddedNeedsOk && (
                <TechAddedNeedsOkBanner jobId={jobId} data={data.techAddedNeedsOk} onSaved={refresh} />
            )}
            {data.estimates.map((est) => (
                <div key={est.id} className="rounded-xl border border-border bg-card p-3.5">
                    {/* What the customer will read, at the top of the quote the
                        office is building. The title was fetched here and never
                        rendered, and the scope text wasn't even in the payload —
                        so deciding what the quote SAYS meant opening the
                        estimate in another tab, every time. */}
                    {/* Trust audit Phase 1: every failed device is quoted or
                        says why not, before Approve & send works. */}
                    <QuoteCheckStrip
                        estimateId={est.id}
                        editable={est.status === "draft" || est.status === "sent"}
                        onChanged={refresh}
                    />
                    <CloseOutQuoteText
                        estimateId={est.id}
                        title={est.title}
                        notes={est.notes}
                        editable={est.status === "draft" || est.status === "sent"}
                        onSaved={refresh}
                    />
                    {/* #1958: a $0 piece nobody priced, on a one-line quote. The
                        per-device menu names its own, next to the device. */}
                    {!perDeviceGroupsOf(est) && est.lines.some((l) => notPricedLabel(l.description)) && (
                        <div className="mb-2">
                            <NotPricedYet
                                labels={est.lines
                                    .map((l) => notPricedLabel(l.description))
                                    .filter((x): x is string => !!x)}
                            />
                        </div>
                    )}
                    {/* Two columns: what failed → what we're asking. The tech's
                        verbatim notes used to sit in a middle column, but they
                        already render at the bottom (TechPhotoPanel), so they're
                        dropped here to cut the height (Issue #171). */}
                    <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
                        {/* What failed */}
                        <div className="rounded-lg border-l-[3px] border-red-600 bg-red-50/60 p-3 dark:bg-red-900/15">
                            <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-red-700 dark:text-red-300">
                                What failed
                            </div>
                            {data.failed.length === 0 && (
                                <p className="mt-1.5 text-[12px] text-foreground/60">No failed devices recorded.</p>
                            )}
                            {data.failed.map((f) => (
                                <div key={f.assetId} className="mt-1.5 text-[12.5px]">
                                    <b>
                                        {f.device || "device"}
                                        {f.serial ? ` — ${f.serial}` : ""}
                                    </b>
                                    {f.reasons.length > 0 && (
                                        <div className="text-foreground/60">{f.reasons.join(" · ")}</div>
                                    )}
                                    {/* #1958: the rule's answer, the same one the
                                        wording and the options were built from. */}
                                    {ruleWorthShowing(f.rule) && (
                                        <RepairRuleStrip
                                            jobId={jobId}
                                            assetId={f.assetId}
                                            rule={f.rule}
                                            editable={est.status === "draft"}
                                            onSaved={refresh}
                                        />
                                    )}
                                    <span
                                        className={cn(
                                            "mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold",
                                            f.reportReviewed
                                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
                                        )}
                                    >
                                        {f.reportReviewed ? "report reviewed ✓" : "report not reviewed"}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {/* What we're asking */}
                        <div className="rounded-lg border-l-[3px] border-teal-600 bg-teal-50/60 p-3 dark:bg-teal-900/15">
                            <div className="flex items-center justify-between">
                                <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-teal-700 dark:text-teal-300">
                                    What we&apos;re asking
                                    {est.selectionMode === "choose_one" ? " — customer picks one" : ""}
                                    {perDeviceGroupsOf(est) ? " — one choice per device" : ""}
                                </div>
                                {/* No total on a per-device quote: the customer's
                                    choices decide it, so a number here is one
                                    nobody has agreed to. */}
                                {data.canViewMoney && est.selectionMode !== "choose_one" && !perDeviceGroupsOf(est) && (
                                    <div className="text-[12.5px] font-extrabold">{money(est.total)}</div>
                                )}
                            </div>

                            {perDeviceGroupsOf(est) ? (
                                <PerDeviceQuote
                                    est={est}
                                    groups={perDeviceGroupsOf(est)!}
                                    failed={data.failed}
                                    editing={editing}
                                    canViewMoney={data.canViewMoney}
                                    canViewMarkup={isAdmin}
                                    rateCardActive={data.rateCardActive}
                                    onSaved={refresh}
                                />
                            ) : est.selectionMode === "choose_one" ? (
                                // Comparative Repair-vs-Replacement quote (#380): two option
                                // cards, each priced from the pricebook; the customer picks one.
                                <div className="mt-2 space-y-2.5">
                                    <div className="flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2 py-1.5 text-[11px] font-semibold text-violet-800 dark:border-violet-800 dark:bg-violet-900/20 dark:text-violet-200">
                                        <GitCompare className="h-3.5 w-3.5 flex-none" /> Comparative quote — the
                                        customer chooses one. Both options are pinned when you approve.
                                    </div>
                                    {/* #1958: the rule's answer, one plain line per device. */}
                                    {data.failed
                                        .filter((f) => f.rule?.shutoffWork)
                                        .map((f) => (
                                            <p
                                                key={f.assetId}
                                                className="text-[11.5px] font-bold text-violet-900 dark:text-violet-100"
                                                data-testid="comparative-rule-reason"
                                            >
                                                {f.rule!.reason}
                                            </p>
                                        ))}
                                    {est.options.map((opt) => {
                                        const optLines = est.lines.filter((l) => l.optionId === opt.id);
                                        const isPicked = est.selectedOptionId === opt.id;
                                        return (
                                            <div
                                                key={opt.id}
                                                className={cn(
                                                    "rounded-lg border bg-card p-2.5",
                                                    isPicked
                                                        ? "border-emerald-400 ring-1 ring-emerald-400/20"
                                                        : "border-border",
                                                )}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="text-[12px] font-bold">
                                                        {opt.label}
                                                        {isPicked && (
                                                            <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-bold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                                                                customer chose this
                                                            </span>
                                                        )}
                                                    </div>
                                                    {data.canViewMoney && (
                                                        <div className="text-[13px] font-extrabold">
                                                            {money(opt.total)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="mt-1.5 space-y-1">
                                                    {optLines.map((l, i) => (
                                                        <EstimateLineRow
                                                            key={l.id}
                                                            estimateId={est.id}
                                                            index={i}
                                                            line={l}
                                                            editing={editing}
                                                            canViewMoney={data.canViewMoney}
                                                            canViewMarkup={isAdmin}
                                                            rateCardActive={data.rateCardActive}
                                                            onSaved={refresh}
                                                        />
                                                    ))}
                                                    {optLines.length === 0 && (
                                                        <p className="text-[11.5px] text-foreground/55">
                                                            No lines yet — add the {opt.label.toLowerCase()} scope.
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="mt-1.5">
                                                    <PricebookLinePicker
                                                        estimateId={est.id}
                                                        optionId={opt.id}
                                                        addLabel={`Add to ${opt.label}`}
                                                        onAdded={refresh}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {est.lines.filter((l) => !l.optionId).length > 0 && (
                                        <div className="rounded-lg border border-dashed border-border p-2.5">
                                            <div className="text-[10.5px] font-bold uppercase tracking-wide text-foreground/55">
                                                Included with either option
                                            </div>
                                            <div className="mt-1.5 space-y-1">
                                                {est.lines
                                                    .filter((l) => !l.optionId)
                                                    .map((l, i) => (
                                                        <EstimateLineRow
                                                            key={l.id}
                                                            estimateId={est.id}
                                                            index={i}
                                                            line={l}
                                                            editing={editing}
                                                            canViewMoney={data.canViewMoney}
                                                            canViewMarkup={isAdmin}
                                                            rateCardActive={data.rateCardActive}
                                                            onSaved={refresh}
                                                        />
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                    {est.gaps.map((g) => (
                                        <div
                                            key={g}
                                            className="flex items-center justify-between text-[12px] text-amber-700 dark:text-amber-300"
                                        >
                                            <span className="font-semibold capitalize">{g}</span>
                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold dark:bg-amber-900/50">
                                                ⚠ not on estimate
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-1.5 space-y-1">
                                    {est.lines.map((l, i) => (
                                        <EstimateLineRow
                                            key={i}
                                            estimateId={est.id}
                                            index={i}
                                            line={l}
                                            editing={editing}
                                            canViewMoney={data.canViewMoney}
                                            canViewMarkup={isAdmin}
                                            rateCardActive={data.rateCardActive}
                                            onSaved={refresh}
                                        />
                                    ))}
                                    {est.lines.length === 0 &&
                                        (est.emptyWhy ? (
                                            <EmptyQuoteBanner
                                                why={est.emptyWhy}
                                                drafting={rebuildQuote.isPending}
                                                // Nothing to lose on an empty quote, so no
                                                // confirm step: one tap drafts it.
                                                onDraft={() => rebuildQuote.mutate(est.id)}
                                                onAddByHand={() => setEditing(true)}
                                            />
                                        ) : (
                                            <p className="text-[12px] text-foreground/60">
                                                No lines yet — add what we&apos;re asking.
                                            </p>
                                        ))}
                                    {est.gaps.map((g) => (
                                        <div
                                            key={g}
                                            className="flex items-center justify-between text-[12px] text-amber-700 dark:text-amber-300"
                                        >
                                            <span className="font-semibold capitalize">{g}</span>
                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold dark:bg-amber-900/50">
                                                ⚠ not on estimate
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Which repair-options world this is, when the quote
                                isn't per-device (#1348). Silent in the ordinary
                                case — feature off and customer on the menu. */}
                            {!perDeviceGroupsOf(est) && data.repairOptions && (
                                <RepairOptionsWorld mode={data.repairOptions} status={est.status} />
                            )}
                            {/* Docbar — Preview · Print · Edit inline (pricebook picker below) */}
                            <div className="mt-2.5 flex flex-wrap gap-1.5">
                                <Button size="sm" variant="outline" onClick={() => navigate(`/commercial/${est.id}`)}>
                                    <Eye className="mr-1 h-3.5 w-3.5" /> Preview
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/commercial/${est.id}?print=1`)}
                                >
                                    <Printer className="mr-1 h-3.5 w-3.5" /> Print
                                </Button>
                                <Button
                                    size="sm"
                                    variant={editing ? "default" : "outline"}
                                    onClick={() =>
                                        !editing && est.approvalState === "approved"
                                            ? setConfirmEditFor(est.approvedByName ?? "")
                                            : setEditing((e) => !e)
                                    }
                                    data-testid="edit-inline"
                                >
                                    {editing ? "Done editing" : "Edit inline"}
                                </Button>
                                {/* Turn a flat quote into a Repair-vs-Replacement comparison
                                    the customer picks one of (#380). Hidden once it's already
                                    comparative or the customer has acted on it. */}
                                {est.selectionMode !== "choose_one" &&
                                    !["accepted", "declined", "cancelled"].includes(est.status) && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-300 dark:hover:bg-violet-900/20"
                                            disabled={makeComparative.isPending}
                                            onClick={() => makeComparative.mutate(est.id)}
                                        >
                                            {makeComparative.isPending ? (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <GitCompare className="mr-1 h-3.5 w-3.5" />
                                            )}
                                            Repair vs Replacement
                                        </Button>
                                    )}
                                {/* Handoff + rip cord, set apart on the right. Hand the
                                    estimate to an estimator to build (Issue #517), or —
                                    if nobody should quote it — pull the "No estimate
                                    needed" cord (now captures why). Both hidden once the
                                    customer has acted (accepted/declined/cancelled). */}
                                {!["accepted", "declined", "cancelled"].includes(est.status) && (
                                    <div className="ml-auto flex flex-wrap items-center gap-1.5">
                                        <HandoffControl
                                            est={est}
                                            pending={assignEstimator.isPending}
                                            onAssign={(userId) =>
                                                assignEstimator.mutate({ estimateId: est.id, userId })
                                            }
                                        />
                                        {/* Only a draft can be rebuilt: a quote the
                                            customer has seen keeps the shape it was
                                            sent in (#1647). */}
                                        {est.status === "draft" && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                data-testid="closeout-rebuild-quote"
                                                className="text-[11px] text-foreground/65"
                                                disabled={rebuildQuote.isPending}
                                                onClick={() => setRebuildTarget(est)}
                                            >
                                                {rebuildQuote.isPending && rebuildTarget?.id === est.id ? (
                                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="mr-1 h-3.5 w-3.5" />
                                                )}
                                                Rebuild this quote
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            data-testid="closeout-skip-estimate"
                                            className="text-[11px] text-foreground/55 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                                            disabled={skipEstimate.isPending}
                                            onClick={() => openSkip(est.id)}
                                        >
                                            {skipEstimate.isPending && skipTarget === est.id ? (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <Trash2 className="mr-1 h-3.5 w-3.5" />
                                            )}
                                            No estimate needed
                                        </Button>
                                    </div>
                                )}
                            </div>
                            {/* Lines come from the pricebook — the picker computes
                                pricing live (labor + materials + per-customer rules)
                                and lands the quote's lines on this estimate. For a
                                comparative quote the per-option pickers above are used
                                instead, so this single picker is single-mode only. */}
                            {est.selectionMode !== "choose_one" && (
                                <div className="mt-1.5">
                                    {/* On a per-device quote this picker still has a real
                                        job — shared work that belongs to the quote and to
                                        no single option (a trip charge; an external part
                                        that rides with whichever repair they pick). But
                                        "Add from pricebook" beside a menu of options does
                                        not say which of them it means, and that ambiguity
                                        is how money ended up outside the option on Fort
                                        Worth ISD 092678173. Named explicitly here, and
                                        each option carries its own picker above (#1418). */}
                                    <PricebookLinePicker
                                        estimateId={est.id}
                                        addLabel={
                                            perDeviceGroupsOf(est) ? "Add to the whole quote — not a choice" : undefined
                                        }
                                        destinationLabel={
                                            perDeviceGroupsOf(est) ? "the whole quote, not one option" : undefined
                                        }
                                        onAdded={refresh}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Who has the ball: where the quote is, and one tap to send an
                        approved quote back for a new one. */}
                    {ball !== undefined && est.status === "draft" && (
                        <div className="mt-2.5 space-y-2">
                            <QuoteTrail
                                steps={quoteTrail({
                                    approvalState: est.approvalState,
                                    status: est.status,
                                    approvedByName: est.approvedByName?.split(" ")[0] ?? null,
                                    approvedAt: est.approvedAt,
                                    ball,
                                    customerSentAt: est.customerSentAt ?? null,
                                    autoSendAt:
                                        est.autoSend && ["pending", "sending"].includes(est.autoSend.status)
                                            ? est.autoSend.sendAfter
                                            : null,
                                })}
                            />
                            {est.approvalState === "approved" && (
                                <NeedsNewQuote
                                    estimateId={est.id}
                                    jobId={jobId}
                                    approverName={est.approvedByName?.split(" ")[0] || "the approver"}
                                    onDone={refresh}
                                />
                            )}
                            {isAdmin && est.approvalState !== "approved" && (
                                <div className="rounded-md border-l-[3px] border-teal-700 bg-teal-50 px-2.5 py-2 text-[12px] dark:bg-teal-950/30">
                                    After you approve, this job leaves your <b>Yours</b> and goes to the office as{" "}
                                    <b>Yours — send the quote</b>.
                                </div>
                            )}
                        </div>
                    )}

                    <QuoteSendChip customerSentAt={est.customerSentAt ?? null} autoSend={est.autoSend ?? null} />

                    {/* Second look (10/28): looking never sends it back for approval. */}
                    {est.status === "draft" && editing && est.approvalState === "approved" && (
                        <ApprovedEditWarning approverName={est.approvedByName} className="mt-2.5" />
                    )}
                    {est.status === "draft" && <SecondLookBar estimateId={est.id} className="mt-2.5" />}

                    {/* Corbin gate bar */}
                    <CorbinGateBar
                        est={est}
                        isAdmin={isAdmin}
                        canViewMoney={data.canViewMoney}
                        onSend={() => sendToCorbin.mutate(est.id)}
                        onApprove={() => approve.mutate(est.id)}
                        sending={sendToCorbin.isPending}
                        approving={approve.isPending}
                    />
                </div>
            ))}

            <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
                <TechPhotoPanel
                    techNotes={data.failed.map((f) => ({
                        asset_id: f.assetId,
                        device: [f.device, f.serial].filter(Boolean).join(" — ") || "device",
                        overall_result: "fail",
                        remarks: f.techSaid,
                    }))}
                    photos={data.photos ?? []}
                    documents={data.documents ?? []}
                />
            </div>

            <EditApprovedConfirm
                open={confirmEditFor !== null}
                approverName={confirmEditFor}
                onCancel={() => setConfirmEditFor(null)}
                onEdit={() => {
                    setConfirmEditFor(null);
                    setEditing(true);
                }}
            />

            <RebuildQuoteDialog
                est={rebuildTarget}
                repairOptions={data.repairOptions ?? null}
                canViewMoney={data.canViewMoney}
                pending={rebuildQuote.isPending}
                onCancel={() => setRebuildTarget(null)}
                onConfirm={(id) => rebuildQuote.mutate(id)}
            />

            {/* "No estimate needed" — capture WHY (required) and optionally share
                it with the customer (Issue #517). Rendered once for the station. */}
            <Dialog open={!!skipTarget} onClose={() => setSkipTarget(null)} title="No estimate needed?">
                <div className="space-y-3">
                    <p className="text-[12.5px] text-foreground/70">
                        This cancels the estimate so it stops holding up closeout. Say why you&apos;re not quoting —
                        it&apos;s saved to the job. You can start a fresh one later if a repair needs a quote.
                    </p>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-wide text-foreground/60">
                            Why aren&apos;t we quoting?
                        </label>
                        <Textarea
                            value={skipReason}
                            onChange={(e) => setSkipReason(e.target.value)}
                            rows={3}
                            autoFocus
                            placeholder="e.g. No repair needed — passed on retest. / Customer is handling repairs in-house."
                            className="mt-1"
                        />
                    </div>
                    <label className="flex items-start gap-2 text-[12.5px]">
                        <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 flex-none"
                            checked={skipShare}
                            onChange={(e) => setSkipShare(e.target.checked)}
                        />
                        <span>
                            <b>Tell the customer why.</b> Adds this to their closeout email so they know there&apos;s no
                            quote — you&apos;ll still review the email before it sends.
                        </span>
                    </label>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSkipTarget(null)}
                            disabled={skipEstimate.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            className="bg-red-600 text-white hover:bg-red-700"
                            disabled={!skipReason.trim() || skipEstimate.isPending}
                            onClick={() =>
                                skipTarget &&
                                skipEstimate.mutate({
                                    estimateId: skipTarget,
                                    reason: skipReason.trim(),
                                    shareWithCustomer: skipShare,
                                })
                            }
                        >
                            {skipEstimate.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                            No estimate needed
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

// "Rebuild this quote" confirm (#1647) — the approved artboard 4 of
// threads/per-device-option-editing/mockup.html. It says, before anything is
// written, what the rebuild keeps, what it rewrites, and that the invoice is
// not touched. The office's own scope paragraph is shown in full, because it
// is the one thing a rebuild genuinely loses.
function RebuildQuoteDialog({
    est,
    repairOptions,
    canViewMoney,
    pending,
    onCancel,
    onConfirm,
}: {
    est: StationEstimateData["estimates"][number] | null;
    repairOptions: StationEstimateData["repairOptions"] | null;
    canViewMoney: boolean;
    pending: boolean;
    onCancel: () => void;
    onConfirm: (estimateId: string) => void;
}) {
    const approved = est?.approvalState === "approved" || est?.approvalState === "delta";
    const menuLine =
        repairOptions?.customerMode === "single"
            ? "This customer is set to Recommended repair only, so each failed device gets one repair — the complete rebuild — with no menu."
            : repairOptions?.customerMode === "hourly"
              ? "This customer is quoted from their own labor hours, so each failed device gets one repair line."
              : repairOptions?.featureEnabled
                ? "This customer sees every repair we can price for each failed device, and picks one."
                : null;
    return (
        <Dialog open={!!est} onClose={onCancel} title="Rebuild this repair quote?">
            {est && (
                <div className="space-y-3 text-[12.5px]">
                    <p className="text-foreground/60">
                        {est.displayNumber ? `#${est.displayNumber} · ` : ""}draft · not sent
                        {canViewMoney ? ` · now ${money(est.total)}` : ""}
                    </p>
                    <p>
                        Re-drafts the quote from today&apos;s test results, today&apos;s pricebook and the
                        customer&apos;s current settings. Labor is written as hours × the crew&apos;s rate.
                        {menuLine ? ` ${menuLine}` : ""}
                    </p>
                    <ul className="space-y-1.5">
                        <li className="flex gap-2">
                            <span className="font-bold text-emerald-700 dark:text-emerald-300">✓</span>
                            <span>
                                <b>Kept</b> — every line you added or changed, exactly as you left it. Also the estimate
                                number, the site and devices it&apos;s for
                                {est.assignedEstimator
                                    ? `, and the handoff to ${est.assignedEstimator.name ?? "the estimator"}`
                                    : ""}
                                .
                            </span>
                        </li>
                        <li className="flex gap-2">
                            <span className="font-bold text-red-700 dark:text-red-300">✕</span>
                            <span>
                                <b>Rewritten</b> — the lines GWC added, and the scope paragraph goes back to the one
                                written from the test data.
                            </span>
                        </li>
                        <li className="flex gap-2">
                            <span className="font-bold text-foreground/60">↺</span>
                            <span>
                                {approved ? (
                                    <>
                                        <b>Approved{est.approvedByName ? ` by ${est.approvedByName}` : ""}</b> — a
                                        rebuild drops it back to pending, so it needs approving again.
                                    </>
                                ) : (
                                    <>
                                        <b>Not approved yet</b>, so nothing is undone.
                                    </>
                                )}
                            </span>
                        </li>
                    </ul>
                    <RemovedChoicesWarning choices={est.removedChoices} action="Rebuild" />
                    {est.notes?.trim() && (
                        <div>
                            <div className="text-[10.5px] font-bold uppercase tracking-wide text-foreground/55">
                                Scope paragraph that will be replaced
                            </div>
                            <p className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-2 text-[12px]">
                                {est.notes}
                            </p>
                        </div>
                    )}
                    <div className="rounded-md border-l-[3px] border-emerald-600 bg-emerald-50/70 p-2 dark:bg-emerald-900/15">
                        <b>Your invoice is not touched.</b> This rebuilds the repair quote only — no invoice is created,
                        changed or deleted. If the rebuild can&apos;t produce a quote, your quote stays exactly as it
                        is.
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                        <span className="mr-auto text-[11.5px] text-foreground/55">
                            Nothing is written until you press Rebuild.
                        </span>
                        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
                            Cancel
                        </Button>
                        <Button size="sm" disabled={pending} onClick={() => onConfirm(est.id)}>
                            {pending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                            Rebuild
                        </Button>
                    </div>
                </div>
            )}
        </Dialog>
    );
}

// Hand an estimate off to an estimator to build (Issue #517). Collapsed by
// default; opens a small estimator picker (fetched on open). When an estimate
// is already handed off it shows who's building it + a clear (×) button. The
// assignment is the gate that lets that person edit this one estimate.
function HandoffControl({
    est,
    onAssign,
    pending,
}: {
    est: StationEstimateData["estimates"][number];
    onAssign: (userId: string | null) => void;
    pending: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [pick, setPick] = useState("");
    const estimatorsQ = useQuery({
        queryKey: ["closeout-assignable-estimators"],
        queryFn: () =>
            api.get<{ estimators: Array<{ userId: string; name: string | null }> }>(
                `/api/close-out/assignable-estimators`,
            ),
        enabled: open,
    });
    const assigned = est.assignedEstimator;

    if (assigned) {
        return (
            <div className="flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-200">
                <UserRound className="h-3.5 w-3.5 flex-none" />
                Being built by {assigned.name ?? "an estimator"}
                <button
                    type="button"
                    title="Clear handoff"
                    className="ml-0.5 rounded p-0.5 text-indigo-500 hover:bg-indigo-100 hover:text-indigo-900 disabled:opacity-50 dark:hover:bg-indigo-800/40"
                    disabled={pending}
                    onClick={() => onAssign(null)}
                >
                    <X className="h-3 w-3" />
                </button>
            </div>
        );
    }

    if (!open) {
        return (
            <Button
                size="sm"
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/20"
                onClick={() => setOpen(true)}
            >
                <UserPlus className="mr-1 h-3.5 w-3.5" /> Hand to estimator
            </Button>
        );
    }

    const list = estimatorsQ.data?.estimators ?? [];
    return (
        <div className="flex items-center gap-1.5">
            <select
                className="h-8 rounded-md border border-border bg-card px-2 text-[12px]"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
            >
                <option value="">{estimatorsQ.isLoading ? "Loading…" : "Choose an estimator…"}</option>
                {list.map((u) => (
                    <option key={u.userId} value={u.userId}>
                        {u.name ?? "Unnamed"}
                    </option>
                ))}
            </select>
            <Button size="sm" disabled={!pick || pending} onClick={() => onAssign(pick)}>
                {pending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Hand off
            </Button>
            <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                    setOpen(false);
                    setPick("");
                }}
            >
                Cancel
            </Button>
        </div>
    );
}

// ─── Shared inline line editor: description · qty · cost · × mult · = price ──
// The math always reconciles: price = cost × multiplier, line total = qty ×
// price. The multiplier is never stored — it's derived (price ÷ cost) so the
// numbers can't drift. Editing cost or the multiplier recomputes price;
// editing price recomputes the multiplier. A line with a price but no cost
// shows cost = price / multiplier = 1 (never blank). When a rate card governs
// the customer, the multiplier is read-only (the agreed rate wins) — cost and
// price stay editable.
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const numOr = (s: string, fallback = 0) => {
    const n = Number(s);
    return Number.isFinite(n) ? n : fallback;
};
const fmtNum = (n: number) => {
    // Trim to at most 4 decimals without trailing-zero noise.
    const r = Math.round((n + Number.EPSILON) * 10000) / 10000;
    return String(r);
};

interface LineSavePayload {
    description?: string;
    qty?: number;
    unit_price?: number;
    unit_cost?: number | null;
    /** Labor line (#1823 S3): the server computes qty and unit from these. */
    labor_hours?: number;
    labor_techs?: number;
}

// No-empty rule (Corbin, 7/21): a line often has a price but no meaningful cost
// (a flat test fee, a labor line) — cost arrives null OR as a real 0. In every
// such case seed cost = price so the row is never blank and the math still
// reconciles (price = cost × 1). A positive stored cost is used as-is.
function seedCostMult(unitCost: number | null, unitPrice: number): { cost: number; mult: number } {
    const cost = unitCost != null && unitCost > 0 ? unitCost : unitPrice;
    const mult = cost > 0 ? round2(unitPrice / cost) : 1;
    return { cost, mult };
}

function LineEditForm({
    initialDescription,
    initialQty,
    initialUnitPrice,
    initialUnitCost,
    rateCardActive,
    canViewMarkup,
    saving,
    removing,
    onSave,
    onRemove,
    labor,
}: {
    initialDescription: string;
    initialQty: number;
    initialUnitPrice: number;
    initialUnitCost: number | null;
    rateCardActive: boolean;
    // Only admins (owner) see and edit the markup multiplier. For everyone
    // else the multiplier is hidden and the price auto-calculates from the
    // cost they enter (cost × the effective markup) — they look up the
    // material cost, the system figures the customer price.
    canViewMarkup: boolean;
    saving: boolean;
    removing: boolean;
    onSave: (payload: LineSavePayload) => void;
    onRemove: () => void;
    /** Labor line: the techs stepper replaces typing qty × price (#1823 S3). */
    labor?: LaborShape | null;
}) {
    // We only persist a cost if the user actually types one — a seeded
    // cost = price is a display convenience, never written back (so a real
    // test/labor line keeps "no cost on file" and margin math is undisturbed).
    const seeded = seedCostMult(initialUnitCost, initialUnitPrice);

    const [desc, setDesc] = useState(initialDescription);
    const [qty, setQty] = useState(fmtNum(initialQty));
    const [cost, setCost] = useState(fmtNum(seeded.cost));
    const [mult, setMult] = useState(fmtNum(seeded.mult));
    const [price, setPrice] = useState(fmtNum(initialUnitPrice));
    const [costTouched, setCostTouched] = useState(false);

    useEffect(() => {
        const s = seedCostMult(initialUnitCost, initialUnitPrice);
        setDesc(initialDescription);
        setQty(fmtNum(initialQty));
        setCost(fmtNum(s.cost));
        setMult(fmtNum(s.mult));
        setPrice(fmtNum(initialUnitPrice));
        setCostTouched(false);
    }, [initialDescription, initialQty, initialUnitPrice, initialUnitCost]);

    // Editing cost → price follows (cost × current multiplier).
    const onCost = (v: string) => {
        setCost(v);
        setCostTouched(true);
        const c = numOr(v);
        const m = numOr(mult, 1);
        setPrice(fmtNum(round2(c * m)));
    };
    // Editing multiplier → price follows. Disabled under a rate card.
    const onMult = (v: string) => {
        setMult(v);
        const c = numOr(cost);
        const m = numOr(v, 1);
        setPrice(fmtNum(round2(c * m)));
    };
    // Editing price → multiplier follows (price ÷ cost), so the identity holds.
    const onPrice = (v: string) => {
        setPrice(v);
        const c = numOr(cost);
        const p = numOr(v);
        setMult(c > 0 ? fmtNum(round2(p / c)) : "1");
    };

    const priceNum = numOr(price);
    const qtyNum = numOr(qty, 1);
    const lineTotal = round2(qtyNum * priceNum);

    const dirty =
        desc !== initialDescription ||
        numOr(qty, 1) !== initialQty ||
        priceNum !== initialUnitPrice ||
        (costTouched && numOr(cost) !== seeded.cost);

    const handleSave = () => {
        const payload: LineSavePayload = {
            description: desc,
            qty: numOr(qty, 1) || 1,
            unit_price: priceNum || 0,
        };
        // Only persist a cost when the user actually set one — otherwise leave
        // the stored cost untouched (don't fabricate a cost equal to price).
        if (costTouched) payload.unit_cost = numOr(cost);
        onSave(payload);
    };

    const numCls = "rounded border border-border bg-background px-1 py-1 text-right text-[12px] tabular-nums";
    return (
        <div className="rounded border border-border/70 bg-background/60 p-1.5">
            <textarea
                className="mb-1 w-full resize-y rounded border border-border bg-background px-1.5 py-1 text-[12px] leading-snug"
                rows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="description"
            />
            {labor && (
                <LaborCrewStepper
                    className="mb-1.5"
                    labor={labor}
                    pending={saving}
                    disabled={removing}
                    onChange={(next) => onSave(next)}
                />
            )}
            <div className="flex flex-wrap items-end gap-x-1.5 gap-y-1">
                <label className="flex flex-col text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                    qty
                    <input
                        className={cn(numCls, "w-12")}
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        inputMode="decimal"
                    />
                </label>
                <label className="flex flex-col text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                    cost
                    <input
                        className={cn(numCls, "w-16")}
                        value={cost}
                        onChange={(e) => onCost(e.target.value)}
                        inputMode="decimal"
                        placeholder="cost"
                    />
                </label>
                {canViewMarkup && (
                    <>
                        <span className="pb-1.5 text-[11px] text-muted-foreground">×</span>
                        <label className="flex flex-col text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                            mult{rateCardActive ? " 🔒" : ""}
                            <input
                                className={cn(numCls, "w-14", rateCardActive && "cursor-not-allowed opacity-60")}
                                value={mult}
                                onChange={(e) => onMult(e.target.value)}
                                inputMode="decimal"
                                readOnly={rateCardActive}
                                title={
                                    rateCardActive
                                        ? "Rate card in — the agreed markup governs. Adjust cost or price instead."
                                        : "Markup multiplier (price ÷ cost)"
                                }
                            />
                        </label>
                    </>
                )}
                <span className="pb-1.5 text-[11px] text-muted-foreground">=</span>
                <label className="flex flex-col text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                    price
                    <input
                        className={cn(numCls, "w-20", !canViewMarkup && "cursor-not-allowed opacity-60")}
                        value={price}
                        onChange={(e) => onPrice(e.target.value)}
                        inputMode="decimal"
                        placeholder="price"
                        readOnly={!canViewMarkup}
                        title={
                            canViewMarkup
                                ? "Customer price"
                                : "Auto-calculated from the cost you enter. Enter the material's cost and the price fills in."
                        }
                    />
                </label>
                <span className="ml-auto pb-1.5 text-[11px] font-semibold text-foreground/70">
                    = {money(lineTotal)}
                </span>
                <div className="flex items-center gap-1 pb-0.5">
                    <Button
                        size="sm"
                        variant={dirty ? "default" : "outline"}
                        className="h-7 px-2"
                        disabled={!dirty || saving || removing}
                        onClick={handleSave}
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 flex-shrink-0 p-0 text-foreground/50 hover:text-red-600"
                        title="Remove this line"
                        disabled={removing || saving}
                        onClick={onRemove}
                    >
                        {removing ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function EstimateLineRow({
    estimateId,
    line,
    editing,
    canViewMoney,
    canViewMarkup,
    rateCardActive,
    onSaved,
    onLastLineRefused,
}: {
    estimateId: string;
    index: number;
    line: EstimateAskLine;
    editing: boolean;
    canViewMoney: boolean;
    canViewMarkup: boolean;
    rateCardActive: boolean;
    onSaved: () => void;
    /** #1417: the server refused this delete because it was an option's last
     *  line. The parent owns the dialog, since it knows which option. */
    onLastLineRefused?: (message: string) => void;
}) {
    // Inline edit works against the shipped estimate-line endpoints; a save
    // that changes money/lines makes the estimate a delta at read time, so an
    // edited-after-approval estimate can't ride until Corbin re-approves.
    const save = useMutation({
        mutationFn: (payload: LineSavePayload) => api.patch(`/api/estimates/${estimateId}/lines/${line.id}`, payload),
        onSuccess: () => {
            toast.success("Line saved.");
            onSaved();
        },
        // Surface the server's real reason instead of a blank "couldn't save"
        // (the office was flying blind): a locked/approved estimate, a line
        // that went stale after the estimate regenerated, or a value out of
        // range each say what to do next.
        onError: (e: any) => {
            const code = e?.body?.error;
            const msg =
                code === "ESTIMATE_LOCKED"
                    ? "This estimate is approved/locked — reopen it before changing lines."
                    : code === "NOT_FOUND"
                      ? "This line is out of date — refresh the estimate and try again."
                      : code === "ASSET_NOT_LINKABLE"
                        ? (e?.body?.message ?? "That device isn't on this estimate.")
                        : code === "VALIDATION"
                          ? "Check the line values — a number is out of range."
                          : (e?.body?.message ?? e?.body?.error ?? "Couldn't save the line.");
            toast.error(msg);
        },
    });
    const remove = useMutation({
        mutationFn: () => api.delete(`/api/estimates/${estimateId}/lines/${line.id}`),
        onSuccess: () => {
            toast.success("Line removed.");
            onSaved();
        },
        onError: (e: any) => {
            // #1417 — the last line under a repair option is refused, because
            // deleting it would leave a $0.00 repair the customer could accept.
            // A toast would be a dead end; the parent opens the dialog that
            // names the two moves actually meant.
            if (e?.body?.error === "OPTION_WOULD_BE_EMPTY" && onLastLineRefused) {
                onLastLineRefused(e.body.message as string);
                return;
            }
            toast.error(
                e?.body?.error === "ESTIMATE_LOCKED"
                    ? "This estimate is locked — it can't take line changes."
                    : (e?.body?.message ?? "Couldn't remove the line."),
            );
        },
    });

    if (!editing) {
        return (
            <div className="flex items-center justify-between gap-2 text-[12.5px]">
                <span className="min-w-0 flex-1 truncate">{line.description || "line"}</span>
                {canViewMoney && (
                    <span className="whitespace-nowrap text-foreground/60">
                        {line.qty} × {money(line.unit_price)}
                    </span>
                )}
            </div>
        );
    }
    return (
        <LineEditForm
            initialDescription={line.description ?? ""}
            initialQty={line.qty}
            initialUnitPrice={line.unit_price}
            initialUnitCost={line.unit_cost}
            rateCardActive={rateCardActive}
            canViewMarkup={canViewMarkup}
            saving={save.isPending}
            removing={remove.isPending}
            onSave={(payload) => save.mutate(payload)}
            onRemove={() => remove.mutate()}
            labor={line.labor ?? null}
        />
    );
}

// Inline invoice-line editor for Station 2 (Charges). Mirrors EstimateLineRow
// but rides the invoice-line endpoints. The server hard-locks paid/void
// invoices (409 INVOICE_LOCKED), so `editing` is only ever true here for an
// invoice the office may still change — this component never has to re-check.
function InvoiceLineRow({
    invoiceId,
    line,
    editing,
    rateCardActive,
    canViewMarkup,
    onSaved,
}: {
    invoiceId: string;
    line: ChargesLine;
    editing: boolean;
    rateCardActive: boolean;
    canViewMarkup: boolean;
    onSaved: () => void;
}) {
    const lockedMsg = (e: any) =>
        e?.body?.error === "INVOICE_LOCKED"
            ? "This invoice is paid — reopen it before changing lines."
            : (e?.body?.message ?? "Couldn't save the line.");

    const save = useMutation({
        mutationFn: (payload: LineSavePayload) => api.patch(`/api/invoices/${invoiceId}/lines/${line.id}`, payload),
        onSuccess: () => {
            toast.success("Line saved.");
            onSaved();
        },
        onError: (e: any) => toast.error(lockedMsg(e)),
    });
    const remove = useMutation({
        mutationFn: () => api.delete(`/api/invoices/${invoiceId}/lines/${line.id}`),
        onSuccess: () => {
            toast.success("Line removed.");
            onSaved();
        },
        onError: (e: any) => toast.error(lockedMsg(e)),
    });

    const orphan = line.asset_id === null;
    if (!editing) {
        return (
            <div className="flex items-center gap-2 text-xs">
                <span className="min-w-0 flex-1 truncate">{line.description || "(line)"}</span>
                {orphan && (
                    <span
                        className="flex items-center gap-1 text-[10px] font-bold text-amber-700 dark:text-amber-400"
                        title="No work row pairs with this line"
                    >
                        <AlertTriangle className="h-3 w-3" /> no work row
                    </span>
                )}
                <span className="whitespace-nowrap text-foreground/60">
                    {line.qty} × ${line.unit_price.toFixed(2)}
                </span>
            </div>
        );
    }
    return (
        <LineEditForm
            initialDescription={line.description ?? ""}
            initialQty={line.qty}
            initialUnitPrice={line.unit_price}
            initialUnitCost={line.unit_cost}
            rateCardActive={rateCardActive}
            canViewMarkup={canViewMarkup}
            saving={save.isPending}
            removing={remove.isPending}
            onSave={(payload) => save.mutate(payload)}
            onRemove={() => remove.mutate()}
            labor={line.labor ?? null}
        />
    );
}

export function CorbinGateBar({
    est,
    isAdmin,
    canViewMoney,
    onSend,
    onApprove,
    sending,
    approving,
}: {
    est: StationEstimateData["estimates"][number];
    isAdmin: boolean;
    canViewMoney: boolean;
    onSend: () => void;
    onApprove: () => void;
    sending: boolean;
    approving: boolean;
}) {
    const state = est.approvalState;
    // #1417 — a repair priced at nothing never leaves the building.
    //
    // Derived from the option totals already on screen rather than a second
    // opinion from the server: the number the office reads and the number the
    // gate enforces must be the same one, or the button and the error disagree.
    // The server refuses this too (`OPTION_PRICES_AT_ZERO`) — this is so the
    // office is told BEFORE pressing, not by a toast afterwards. A decline is
    // exempt: carrying nothing is what it is for, which is why it renders as a
    // dash and never as $0.00.
    const zeroPriced = (est.deviceGroups ?? []).flatMap((g) =>
        g.options.filter((o) => !o.isDecline && o.total <= 0).map((o) => o.label),
    );
    const banner =
        zeroPriced.length > 0
            ? {
                  cls: "border-red-600 bg-red-50/70 dark:bg-red-900/15",
                  text:
                      zeroPriced.length === 1
                          ? `This quote can't be sent — “${zeroPriced[0]}” is priced at $0.00, and the customer could accept that repair and owe nothing. Put its lines back, or remove the option.`
                          : `This quote can't be sent — ${zeroPriced.length} repairs are priced at $0.00 (${zeroPriced.join(", ")}). A customer could accept any of them and owe nothing.`,
              }
            : state === "approved"
              ? {
                    cls: "border-emerald-600 bg-emerald-50/60 dark:bg-emerald-900/15",
                    text: isAdmin
                        ? "Approved & attached ✓ — rides the packet. Editing marks it 'Changed since approval' and holds it until you re-approve. Uncheck it in Send-off to hold it back."
                        : `Owner-approved ✓${est.approvedByName ? ` by ${est.approvedByName}` : ""} — this estimate can ride the packet.`,
                }
              : state === "delta"
                ? {
                      cls: "border-amber-600 bg-amber-50/60 dark:bg-amber-900/15",
                      text: canViewMoney
                          ? `Changed since approval — approved ${money(est.snapshotTotal ?? 0)} → now ${money(est.total)}. Review the change and Re-approve to unlock sending. Editing never re-approves silently.`
                          : "Changed since approval — needs a fresh approval before it can ride.",
                  }
                : state === "pending"
                  ? {
                        cls: "border-violet-500 bg-violet-50/60 dark:bg-violet-900/15",
                        // "Waiting on Corbin · N days" (10/28): where the ball is, and for how long.
                        text: <WaitingOnApproverText sentAt={est.sentAt} />,
                    }
                  : {
                        cls: "border-violet-500 bg-violet-50/60 dark:bg-violet-900/15",
                        text: "Every estimate needs owner approval before it can ride any send. Prepare it above, then send it up.",
                    };

    return (
        <div className={cn("mt-2.5 rounded-lg border-l-[3px] p-3", banner.cls)}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">
                    Owner approval
                </span>
                <span className="min-w-0 flex-1 text-[12.5px]">{banner.text}</span>
                {/* Office prepares + sends up; the owner (admin) approves. */}
                {!isAdmin && (state === "none" || state === "pending" || state === "delta") && (
                    <Button
                        size="sm"
                        className="bg-violet-600 text-white hover:bg-violet-700"
                        disabled={sending || zeroPriced.length > 0}
                        onClick={onSend}
                    >
                        {sending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                        {state === "delta" ? "Re-send for approval" : "Send for approval"}
                    </Button>
                )}
                {isAdmin && (state === "pending" || state === "delta" || state === "none") && (
                    <Button
                        size="sm"
                        className="bg-emerald-700 text-white hover:bg-emerald-800"
                        disabled={approving || zeroPriced.length > 0}
                        onClick={onApprove}
                    >
                        {approving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                        {state === "delta" ? "Re-approve & attach" : "Approve & attach"}
                    </Button>
                )}
            </div>
        </div>
    );
}

// ═══ STATION 1 — DOCUMENTS ══════════════════════════════════════════════

// ─── Fix-in-place field model (Issue #158) ──────────────────────────────
interface MappedField {
    id: string;
    pdf_field_name: string | null;
    label: string;
    field_type: string;
    page_number: number;
    mapping_type: string;
    data_source_path: string | null;
    format_pattern: string | null;
    static_value?: string | null;
    // Phase 2 (Issue #166) — live values for this job's device.
    checked?: boolean | null; // checkbox_match: does it currently get a ✓?
    resolved_value?: string | null; // the raw value data_source_path resolved to
    // Per-document override (this-doc-only edit) + repeating flag.
    override_value?: string | null; // office override for THIS document (null = untouched)
    is_repeating?: boolean; // per-device field
    // The field's box on the template (PDF points, bottom-left origin) — lets the
    // unified editor place a clickable edit box right over the value on the page.
    position_json?: { x: number; y: number; width?: number | null; height?: number | null } | null;
    // Per-device instance (Issue #355): a repeating field expanded to one entry
    // per device — `id` is `${templateFieldId}::${assetId}`, `device_label` names
    // the device for the box tooltip. Now editable on the document.
    device_asset_id?: string | null;
    device_index?: number | null;
    device_label?: string | null;
}
// Issue #184 — the concrete proposal the interpret endpoint returns.
interface CorrectionProposalResponse {
    proposal: {
        fieldId: string;
        action: "bind_checkbox" | "set_source" | "set_static_value";
        dataSourcePath: string | null;
        staticValue: string | null;
        explanation: string;
    };
    template: { id: string; name: string; form_code: string; version: string };
    field: {
        id: string;
        label: string;
        pdf_field_name: string | null;
        field_type: string;
        mapping_type: string;
        data_source_path: string | null;
    } | null;
}
const isCheckboxField = (f: MappedField) => f.mapping_type === "checkbox_match";
// The correction kinds a field can have, adapting to its type — checkmarks are
// first-class (the TCEQ 20700 is mostly checkboxes).
const FIX_KIND_OPTIONS: Record<string, Array<{ v: string; label: string }>> = {
    text: [
        { v: "value", label: "Value's wrong" },
        { v: "wrong_box", label: "Wrong box" },
        { v: "wrong_source", label: "Wrong source" },
        { v: "should_be_blank", label: "Should be blank" },
    ],
    number: [
        { v: "value", label: "Value's wrong" },
        { v: "wrong_box", label: "Wrong box" },
        { v: "wrong_source", label: "Wrong source" },
        { v: "should_be_blank", label: "Should be blank" },
    ],
    date: [
        { v: "value", label: "Value's wrong" },
        { v: "format", label: "Wrong format" },
        { v: "wrong_box", label: "Wrong box" },
        { v: "wrong_source", label: "Wrong source" },
        { v: "should_be_blank", label: "Should be blank" },
    ],
    checkbox: [
        { v: "wrong_box_checked", label: "Wrong box checked" },
        { v: "should_be_checked", label: "Should be checked" },
        { v: "should_be_unchecked", label: "Should be unchecked" },
        { v: "wrong_source", label: "Wrong source" },
    ],
    radio: [
        { v: "wrong_box_checked", label: "Wrong box checked" },
        { v: "should_be_checked", label: "Should be checked" },
        { v: "should_be_unchecked", label: "Should be unchecked" },
        { v: "wrong_source", label: "Wrong source" },
    ],
    signature: [
        { v: "missing_signature", label: "Missing signature" },
        { v: "wrong_signer", label: "Wrong signer" },
        { v: "wrong_box", label: "Wrong box" },
    ],
};
function kindOptions(fieldType: string): Array<{ v: string; label: string }> {
    return FIX_KIND_OPTIONS[fieldType] ?? FIX_KIND_OPTIONS.text;
}
// Where a kind routes — mirrors the server's correctionRouteType so the drawer
// can preview the destination before submit.
const FIX_DATA_KINDS = new Set(["value", "data"]);
const FIX_SIGNATURE_KINDS = new Set(["missing_signature", "wrong_signer"]);
function fixRouteType(kind: string): "mapping" | "data" | "signature" | "relink" {
    if (FIX_DATA_KINDS.has(kind)) return "data";
    if (FIX_SIGNATURE_KINDS.has(kind)) return "signature";
    if (kind === "relink") return "relink";
    return "mapping";
}
// One-line human description of an interpreted proposal's concrete change.
function describeProposal(p: CorrectionProposalResponse["proposal"]): string {
    if (p.action === "bind_checkbox") return `Tick when the value is "${p.staticValue ?? "—"}"`;
    if (p.action === "set_source") return `Pull from ${p.dataSourcePath ?? "—"}`;
    return `Always print "${p.staticValue ?? "—"}"`;
}
const FIELD_TYPE_BADGE: Record<string, string> = {
    checkbox: "checkmark",
    radio: "checkmark",
    date: "date",
    number: "number",
    signature: "signature",
    text: "text",
};

function StationDocuments({
    jobId,
    onStation,
    nextStation,
    nextStationTitle,
}: {
    jobId: string;
    onStation: (n: number) => void;
    /** The next station that actually needs work (skips done / not-needed). */
    nextStation: number;
    nextStationTitle: string;
}) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const docsQuery = useQuery({
        queryKey: ["close-out-documents", jobId],
        queryFn: () =>
            api.get<{
                documents: CloseOutDocument[];
                docFixes: CloseOutDocFix[];
                photos: CloseOutPhoto[];
                city: CityInfo | null;
                formMismatch: FormMismatch | null;
            }>(`/api/close-out/jobs/${jobId}/documents`),
    });
    const documents = docsQuery.data?.documents ?? [];
    const docFixes = docsQuery.data?.docFixes ?? [];
    const city = docsQuery.data?.city ?? null;
    const formMismatch = docsQuery.data?.formMismatch ?? null;

    // Reformat the report onto the site's correct jurisdiction form. Reuses the
    // office-side regenerate endpoint, which re-resolves the template from the
    // current provider and re-renders from the structured test data (no
    // re-testing). skipSideEffects is applied server-side — it's a re-render,
    // not a new submission.
    const reformat = useMutation({
        mutationFn: () => api.post(`/api/backflow/job/${jobId}/regenerate-report`),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            // #1537: the queue too. On the portal path the whole POINT is that
            // the card changes lane — archiving the paper copies drops
            // report_total to 0, so the job moves from "Waiting on our reports"
            // to "Waiting on portal reports" and keeps counting the readings
            // nobody has entered yet. Invalidating only the documents query left
            // the card on its old lane until a manual refresh, which reads as
            // the re-render having done nothing.
            await refreshCloseOutQueue(qc, { jobId });
            toast.success(
                formMismatch?.kind === "portal_paper"
                    ? `Re-rendered for ${formMismatch.platformLabel} — the paper copies are off the job and the cheat sheet is ready.`
                    : "Report reformatted onto the correct jurisdiction form",
            );
        },
        // The endpoint archives and re-renders together, so a failure leaves the
        // old rows live. Say so — "it failed" invites the office to wonder
        // whether the job is now carrying nothing at all.
        onError: (e: any) =>
            toast.error(
                `Re-render didn't finish — nothing was removed. ${e?.body?.message ?? e?.body?.error ?? "Unknown error"}`,
            ),
    });

    // A document is cleared when the office reviewed it OR it's an official
    // online-agency report the server auto-credited (photos live on Charges/
    // Estimate now, not here).
    const isCleared = (d: CloseOutDocument) => !!d.reviewed_at || !!d.autoReviewed;

    const reportDocs = documents.filter(
        (d) => d.category !== "work_order_uploaded" && d.category !== "work_order_filled",
    );
    const woDocs = documents.filter((d) => d.category === "work_order_uploaded" || d.category === "work_order_filled");
    // Uploaded paper WOs in chronological order (documents are created_at ASC)
    // → "Appointment N" numbering + the "Add another" affordance (Issue #295).
    const uploadedWoDocs = woDocs.filter((d) => d.category === "work_order_uploaded");
    const uploadedWoCount = uploadedWoDocs.length;
    const uploadedWoOrder = new Map(uploadedWoDocs.map((d, i) => [d.id, i]));
    const allCleared = documents.length > 0 && documents.every(isCleared);
    // How many staged documents still need a look — surfaced in the Documents
    // card header so it's obvious the queue holds more than the one on screen.
    const docsToReview = documents.filter((d) => !isCleared(d)).length;

    // Duplicate detection: two report attachments with the SAME file name AND
    // byte size are the same file attached twice (e.g. a regenerate that didn't
    // archive the prior copy). Flag every doc whose (name, size) collides with
    // an earlier one so the office sees it's a repeat instead of reviewing the
    // same report twice and wondering why nothing changed.
    const dupIds = useMemo(() => {
        const firstSeen = new Map<string, string>();
        const dups = new Set<string>();
        for (const d of reportDocs) {
            if (!d.file_name || d.file_size == null) continue;
            const key = `${d.file_name.trim().toLowerCase()}::${d.file_size}`;
            const prior = firstSeen.get(key);
            if (prior) dups.add(d.id);
            else firstSeen.set(key, d.id);
        }
        return dups;
    }, [reportDocs]);

    const [lane, setLane] = useState<ScannerLane>("reports");
    const [index, setIndex] = useState(0);
    const laneDocs: Array<{ id: string; label: string }> =
        lane === "wo"
            ? woDocs.map((d) => ({ id: d.id, label: d.file_name || "work-order.pdf" }))
            : reportDocs.map((d) => ({ id: d.id, label: d.file_name || "report.pdf" }));
    const current = laneDocs[Math.min(index, Math.max(0, laneDocs.length - 1))] ?? null;

    // Loud review progress for the current lane — how many still need a look —
    // so moving between documents (and finishing) reads at a glance instead of
    // a silent "N of M" tick, which was invisible when two reports looked alike.
    const laneClearedCount = lane === "wo" ? woDocs.filter(isCleared).length : reportDocs.filter(isCleared).length;
    const laneLeft = Math.max(0, laneDocs.length - laneClearedCount);
    const currentDoc = documents.find((d) => d.id === current?.id) ?? null;

    const go = useCallback(
        (delta: number) => {
            setIndex((i) => {
                const n = laneDocs.length;
                if (n === 0) return 0;
                return (i + delta + n) % n;
            });
        },
        [laneDocs.length],
    );

    // Arrow keys page the ACTIVE scanner; esc returns to the reports lane.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
            if (e.key === "ArrowLeft") go(-1);
            else if (e.key === "ArrowRight") go(1);
            else if (e.key === "Escape") {
                setLane("reports");
                setIndex(0);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [go]);

    const switchLane = (l: ScannerLane) => {
        setLane(l);
        setIndex(0);
    };

    // Whole-screen drop → the filed-reports upload endpoint, scoped to THIS
    // job: a report files here, and one that names no single device on the
    // job asks "Which device?" (2026-10-06).
    const [dragOver, setDragOver] = useState(false);
    const stationOfficial = useMakeOfficial(jobId, { onDone: () => refreshCloseOutQueue(qc, { jobId }) });
    const uploadDrop = useMutation({
        mutationFn: async (files: File[]) => {
            const fd = new FormData();
            for (const f of files) fd.append("files", f);
            fd.append("jobId", jobId);
            return api.postForm<{
                results: Array<{
                    fileName: string;
                    status?: string;
                    routed?: string;
                    needsDevice?: { attachmentId: string; candidates: ReportDeviceCandidate[] };
                }>;
            }>("/api/filed-reports/upload", fd);
        },
        onSuccess: (r) => {
            const ok = r.results?.filter((x) => x.status !== "error").length ?? 0;
            const filed = r.results?.filter((x) => x.routed === "filed").length ?? 0;
            toast.success(
                filed > 0
                    ? `${filed} official report${filed === 1 ? "" : "s"} filed to this job.`
                    : `${ok} file${ok === 1 ? "" : "s"} uploaded — serial auto-match ran.`,
            );
            const ask = r.results?.find((x) => x.needsDevice);
            if (ask?.needsDevice)
                stationOfficial.askFor(ask.needsDevice.attachmentId, ask.fileName, ask.needsDevice.candidates);
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Upload failed."),
    });

    // Always let the office attach a completed work order here. ADDITIVE
    // (Issue #295 — Cintas collects one signed WO per visit): each upload is
    // ADDED to the list, never replacing the last. Multiple photos in one
    // upload still merge into a single ordered PDF; a wrong one is removed
    // below. Rides the dedicated endpoint (releases a parked send).
    const woInputRef = useRef<HTMLInputElement>(null);
    const uploadWorkOrder = useMutation({
        mutationFn: async (files: File[]) => {
            const fd = new FormData();
            for (const f of files) fd.append("files", f);
            fd.append("jobId", jobId);
            fd.append("append", "true");
            return api.postForm(`/api/attachments/work-order-upload`, fd);
        },
        onSuccess: () => {
            toast.success("Work order added.");
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Work-order upload failed."),
    });

    // Remove one uploaded work order — undo a wrong upload without touching the
    // others. Soft-archive (kept on file), scoped server-side to WO uploads.
    const removeWorkOrder = useMutation({
        mutationFn: (attachmentId: string) => api.post(`/api/attachments/${attachmentId}/archive-work-order`, {}),
        onSuccess: () => {
            toast.success("Work order removed.");
            setIndex(0);
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't remove the work order."),
    });

    // Generated-WO repair actions (Issue #658): the completed work order is
    // fixable from HERE — regenerate re-fills pinned to the job's assigned
    // template (resolved server-side, never re-derived) and NEVER auto-sends;
    // "Fix mapping" opens the template mapping screen for the job's WO.
    const woFilledPresent = woDocs.some((d) => d.category === "work_order_filled");
    const woMappingQ = useQuery({
        queryKey: ["close-out-wo-mapping", jobId],
        enabled: woFilledPresent,
        queryFn: () => api.get<{ workOrder: { intake_item_id: string | null } | null }>(`/api/jobs/${jobId}`),
    });
    const woIntakeItemId = woMappingQ.data?.workOrder?.intake_item_id ?? null;
    // The regenerate itself is honest about being slow (Issue #1413): it runs
    // on a budget that matches the endpoint's real cost, says what it is doing
    // while it runs, and re-checks the job before ever reporting a failure —
    // aborting the fetch never cancelled the server, so a 30s cliff used to
    // report "Couldn't regenerate" over a refill that had already landed.
    const refreshAfterRegenerate = useCallback(() => {
        qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
        refreshCloseOutQueue(qc, { jobId });
    }, [qc, jobId]);
    const regenerateWo = useWoRegenerate({
        jobId,
        onLandedAfterTimeout: refreshAfterRegenerate,
        onSuccess: (r) => {
            refreshAfterRegenerate();
            const n = r.fillWarnings?.length ?? 0;
            // 200 with no PDF produced (no locked template) — don't call that
            // a regeneration (Issue #931).
            if (r.generated === false) {
                toast.error("No PDF was generated — nothing was re-filled.", {
                    description: r.fillWarnings?.[0] ?? "This job has no locked return-PDF template to fill onto yet.",
                });
            } else if (n > 0) {
                toast.warning(
                    `Work order re-generated with ${n} warning${n === 1 ? "" : "s"} — review before sending.`,
                    {
                        description: r.fillWarnings?.[0],
                    },
                );
            } else {
                toast.success("Work order re-generated — review it, then send.");
            }
        },
    });

    // Remove one report from the job — retire a stray or superseded per-device
    // report (e.g. a wrong-form copy left beside the reformatted one) so it stops
    // showing here AND stops riding the customer packet. Soft-archive (kept on
    // file, recoverable), scoped server-side to per-device report categories so
    // the merged city-filing report and work orders can never be removed here.
    const removeReport = useMutation({
        mutationFn: (attachmentId: string) => api.post(`/api/attachments/${attachmentId}/archive-report`, {}),
        onSuccess: () => {
            toast.success("Report removed from the job.");
            setIndex(0);
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["close-out-send-off", jobId] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't remove the report."),
    });

    const markReviewed = useMutation({
        mutationFn: (attachmentId: string) => api.post(`/api/close-out/attachments/${attachmentId}/reviewed`, {}),
        onSuccess: (_data, attachmentId) => {
            // Hand-off: name what's left and jump to the next UNREVIEWED report
            // (not just +1 — so we never land on a duplicate or already-cleared
            // page and look like nothing happened). On the last one, say so and
            // point at the next station.
            const remaining = reportDocs.filter((d) => !isCleared(d) && d.id !== attachmentId);
            if (remaining.length > 0) {
                toast.success(`Reviewed ✓ — ${remaining.length} report${remaining.length === 1 ? "" : "s"} left`);
                const nextIdx = reportDocs.findIndex((d) => d.id === remaining[0]!.id);
                setLane("reports");
                if (nextIdx >= 0) setIndex(nextIdx);
            } else {
                toast.success(`All reports reviewed ✓ — ready for ${nextStationTitle}`);
            }
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["close-out-step-credit", jobId] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not mark reviewed."),
    });

    const [flagOpen, setFlagOpen] = useState(false);
    const [flagNote, setFlagNote] = useState("");
    const flagDoc = useMutation({
        mutationFn: () =>
            api.post(`/api/close-out/jobs/${jobId}/flag`, {
                attachmentId: currentDoc?.id ?? null,
                note: flagNote,
            }),
        onSuccess: () => {
            toast.success("Flagged to Corbin with your note.");
            setFlagOpen(false);
            setFlagNote("");
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "A brief why-note is required."),
    });

    // ─── Clear a "Corbin fix" flag in place (Issue #270) ────────────────
    // The counterpart to "Something's wrong…" — closes the open doc-fix flag
    // that pins station 1 on "Report fix — with Corbin", so a resolved (or
    // mistaken) flag stops blocking without a trip to the legacy panel.
    const resolveFix = useMutation({
        mutationFn: (itemId: string) => api.post(`/api/close-out/jobs/${jobId}/doc-fixes/${itemId}/resolve`, {}),
        onSuccess: () => {
            toast.success("Flag cleared — documents can go green.");
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["close-out-step-credit", jobId] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not clear the flag."),
    });

    // ─── Fix-in-place (Issue #158): correct a mapped field in review ────
    // Only reports GWC generated/mapped have a mapping to fix; agency-portal
    // PDFs (autoReviewed) don't, so the affordance is hidden for them. Mapping
    // edits land in the Form Mapper, which is admin-gated → Corbin-only for v1.
    const { isAdmin } = useAuth();
    const canFix = isAdmin && !!currentDoc && !currentDoc.autoReviewed;
    // The unified "Edit document" editor (per-doc field edits + free-form marks)
    // opens in a modal; declared here so the fields query can load for it.
    const [markupOpen, setMarkupOpen] = useState(false);
    // Expand the doc stage to fill the width by hiding the verify column (lg+).
    // Small screens always stack, so the sidebar is never trapped off-screen.
    const [stageExpanded, setStageExpanded] = useState(false);
    // The two admin-only mapper tools (Fix future forms + learning digest) are
    // tucked behind a collapsed-by-default disclosure so the Documents / Review /
    // City review flow dominates (the v4 3-card sidebar). Nothing removed.
    const [adminToolsOpen, setAdminToolsOpen] = useState(false);
    const [fixOpen, setFixOpen] = useState(false);
    const [fixFieldId, setFixFieldId] = useState<string | null>(null);
    const [fixKind, setFixKind] = useState<string>("");
    const [fixNote, setFixNote] = useState("");
    const fieldsQuery = useQuery({
        queryKey: ["close-out-doc-fields", jobId, currentDoc?.id],
        queryFn: () =>
            api.get<{
                autoReviewed: boolean;
                template: { id: string; name: string; form_code: string; version: string } | null;
                fields: MappedField[];
            }>(`/api/close-out/jobs/${jobId}/documents/${currentDoc!.id}/fields`),
        // Load fields when the unified editor is open (it renders clickable field
        // boxes) or when Corbin opens the admin "fix on future forms" mapper card.
        enabled: (markupOpen || (fixOpen && canFix)) && !!currentDoc,
    });
    const fixTemplate = fieldsQuery.data?.template ?? null;
    const fixFields = fieldsQuery.data?.fields ?? [];
    const fixField = fixFields.find((f) => f.id === fixFieldId) ?? null;
    const selectFixField = (f: MappedField) => {
        setFixFieldId(f.id);
        setFixKind(kindOptions(f.field_type)[0]?.v ?? "value");
        setFixNote("");
        // For a checkbox, pre-select the box they tapped as the one to check.
        setPickBoxId(isCheckboxField(f) ? f.id : null);
    };
    const fileCorrection = useMutation({
        mutationFn: () =>
            api.post<{ correctionId: string; route: { routeType: string; mapperUrl?: string } }>(
                `/api/close-out/jobs/${jobId}/field-correction`,
                {
                    attachmentId: currentDoc?.id ?? null,
                    templateId: fixTemplate?.id ?? null,
                    templateFieldId: fixField?.id ?? null,
                    pdfFieldName: fixField?.pdf_field_name ?? fixField?.label ?? null,
                    fieldType: fixField?.field_type ?? null,
                    kind: fixKind,
                    note: fixNote,
                },
            ),
        onSuccess: (r) => {
            refreshCloseOutQueue(qc, { jobId });
            const url = r?.route?.mapperUrl;
            if (url) {
                toast.success("Saved — opening the mapper on that field.");
                window.open(url, "_blank", "noopener");
            } else {
                toast.success("Correction saved to Corbin's queue.");
            }
            setFixFieldId(null);
            setFixKind("");
            setFixNote("");
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not save the correction."),
    });

    // Phase 2 (Issue #166; work orders Issue #177): the on-page checkbox fix.
    // For a checkbox field we show its sibling group (boxes sharing a data
    // source), the live value the form recorded, and which box currently checks
    // — then Corbin picks the box that SHOULD check and Apply binds the mapping
    // to that value. Works for both test reports and generated work orders.
    const [pickBoxId, setPickBoxId] = useState<string | null>(null);
    const fixGroup =
        fixField && isCheckboxField(fixField)
            ? fixFields.filter(
                  (f) => !f.device_asset_id && isCheckboxField(f) && f.data_source_path === fixField.data_source_path,
              )
            : [];
    const groupLiveValue = fixGroup.find((f) => f.resolved_value != null)?.resolved_value ?? null;
    const bindCheckbox = useMutation({
        mutationFn: (fieldId: string) =>
            api.post<{ ok: boolean; reason?: string; message?: string; boundValue?: string; fields?: MappedField[] }>(
                `/api/close-out/jobs/${jobId}/documents/${currentDoc!.id}/bind-checkbox`,
                { templateId: fixTemplate?.id, fieldId },
            ),
        onSuccess: (r) => {
            if (r?.ok === false) {
                toast.error(r.message ?? "Nothing to bind — that's a data fix.");
                return;
            }
            toast.success(`Bound "${r?.boundValue}" — it checks now and on future forms.`);
            qc.invalidateQueries({ queryKey: ["close-out-doc-fields", jobId, currentDoc?.id] });
            refreshCloseOutQueue(qc, { jobId });
            setPickBoxId(null);
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not bind the checkbox."),
    });

    // ─── Fix a field in plain English (Issue #184) ─────────────────────────
    // Corbin types what's wrong; Claude interprets it against this form's real
    // fields + real data sources and proposes ONE concrete change for a one-tap
    // Apply. bind_checkbox reuses the existing bind mutation; set_source /
    // set_static_value go to the new single-field PATCH. {ok:false} degrades to a
    // "try rephrasing, or pick the field manually" hint. Admin-only.
    const [askText, setAskText] = useState("");
    const [proposal, setProposal] = useState<CorrectionProposalResponse | null>(null);
    const [askError, setAskError] = useState<string | null>(null);
    const interpretMut = useMutation({
        mutationFn: (instruction: string) =>
            api.post<CorrectionProposalResponse & { ok?: boolean; reason?: string }>(
                `/api/close-out/jobs/${jobId}/documents/${currentDoc!.id}/interpret-correction`,
                { instruction },
            ),
        onSuccess: (r) => {
            if (r?.ok === false || !r?.proposal) {
                setProposal(null);
                setAskError(r?.reason ?? "Couldn't interpret that — try rephrasing, or pick the field manually.");
                return;
            }
            setAskError(null);
            setProposal(r);
        },
        onError: (e: any) => {
            setProposal(null);
            setAskError(e?.body?.message ?? "Couldn't interpret that — try rephrasing, or pick the field manually.");
        },
    });
    const applyMappingPatch = useMutation({
        mutationFn: (p: CorrectionProposalResponse) =>
            api.patch(`/api/form-templates/${p.template.id}/fields/${p.proposal.fieldId}`, {
                dataSourcePath: p.proposal.action === "set_source" ? p.proposal.dataSourcePath : undefined,
                staticValue: p.proposal.action === "set_static_value" ? p.proposal.staticValue : undefined,
                fromCloseout: true,
            }),
        onSuccess: () => {
            toast.success("Applied — the field is fixed on this form and future ones.");
            qc.invalidateQueries({ queryKey: ["close-out-doc-fields", jobId, currentDoc?.id] });
            refreshCloseOutQueue(qc, { jobId });
            setProposal(null);
            setAskText("");
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Could not apply the fix."),
    });
    const applyProposal = (p: CorrectionProposalResponse) => {
        if (p.proposal.action === "bind_checkbox") {
            bindCheckbox.mutate(p.proposal.fieldId, {
                onSuccess: () => {
                    setProposal(null);
                    setAskText("");
                },
            });
        } else {
            applyMappingPatch.mutate(p);
        }
    };
    const applyingProposal = applyMappingPatch.isPending || bindCheckbox.isPending;

    const laneStyle = LANE_STYLE[lane];
    const isImage = (currentDoc?.mime_type ?? "").startsWith("image/");
    // The unified "Edit document" editor (field value edits + free-form marks in
    // one Save). Open to any reviewer; not for agency PDFs or images (no GWC
    // mapping to edit, and images can't be baked here). markupOpen is declared
    // above so the fields query can preload for it.
    const canMarkup = !!currentDoc && !currentDoc.autoReviewed && !isImage;

    return (
        <div
            className="flex h-full min-h-0 flex-col gap-2"
            onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const files = Array.from(e.dataTransfer?.files ?? []);
                if (files.length > 0) uploadDrop.mutate(files);
            }}
        >
            {stationOfficial.element}
            {/* Wrong-form banner (Issue #260) — the report was generated on a form
                that no longer matches this site's water provider. One click reruns
                generation onto the correct jurisdiction form from the same test
                data. Only shows when the server detects a real mismatch. */}
            <FormVerdictBanner
                verdict={formMismatch}
                cityName={city?.name ?? null}
                pending={reformat.isPending}
                onFix={() => reformat.mutate()}
            />
            {/* Report problems with a one-tap fix per device (2026-10-09). */}
            <ReportProblemsBanner jobId={jobId} />
            {/* Ask once where a paper/email city wants reports emailed (10/17). */}
            <CityEmailPrompt jobId={jobId} />
            <div className="flex min-h-0 flex-1 flex-col gap-2 lg:flex-row">
                {/* Doc stage — a flex column that fills all available height so the
                PDF is as tall as the viewport allows; bar sits tight on top,
                the page fills the rest with no letterbox gaps. */}
                <div
                    className={cn(
                        "relative flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-xl bg-[#26241f] lg:min-h-0",
                        dragOver && "ring-4 ring-teal-500/60",
                    )}
                >
                    <div
                        className={cn(
                            "flex shrink-0 items-center gap-2.5 rounded-t-xl px-3 py-1.5 text-xs font-bold text-white",
                            laneStyle.bar,
                        )}
                    >
                        {laneStyle.title}
                        <span className="text-[11px] font-semibold opacity-85">
                            {current
                                ? `${current.label} — ${Math.min(index + 1, laneDocs.length)} of ${laneDocs.length}`
                                : "no documents yet"}
                        </span>
                        {current && (
                            <span
                                className={cn(
                                    "rounded px-1.5 py-px text-[10.5px] font-bold",
                                    laneLeft > 0
                                        ? "bg-amber-400/25 text-amber-50"
                                        : "bg-emerald-400/25 text-emerald-50",
                                )}
                            >
                                {laneLeft > 0 ? `${laneLeft} left to review` : "all reviewed ✓"}
                            </span>
                        )}
                        {current && dupIds.has(current.id) && (
                            <span className="rounded bg-orange-500/30 px-1.5 py-px text-[10.5px] font-bold text-orange-50">
                                possible duplicate
                            </span>
                        )}
                        <div className="ml-auto flex items-center gap-2.5">
                            <span className="hidden text-[11px] opacity-80 xl:block">
                                review any order · skip freely · esc back to reports
                            </span>
                            {current && canMarkup && (
                                <button
                                    onClick={() => setMarkupOpen(true)}
                                    className="flex items-center gap-1 rounded bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-white/25"
                                    title="Click a field to correct it, or type / check / circle anywhere on the document"
                                >
                                    <Pencil className="h-3 w-3" /> Edit document
                                </button>
                            )}
                            <button
                                onClick={() => setStageExpanded((v) => !v)}
                                className="hidden items-center gap-1 rounded bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-white/25 lg:flex"
                                title={
                                    stageExpanded
                                        ? "Show the review sidebar"
                                        : "Hide the sidebar — fill the width with the document"
                                }
                                aria-label={stageExpanded ? "Collapse document" : "Expand document"}
                            >
                                {stageExpanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
                                {stageExpanded ? "Exit full width" : "Expand"}
                            </button>
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-1 items-stretch gap-2 p-2">
                        <button
                            onClick={() => go(-1)}
                            disabled={laneDocs.length === 0}
                            aria-label="Previous document"
                            className="flex h-9 w-9 flex-none items-center justify-center self-center rounded-full bg-white/10 text-white"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <div className="flex min-h-0 w-full flex-1 items-stretch justify-center overflow-hidden rounded bg-white shadow-2xl">
                            {current && currentDoc ? (
                                <CloseoutDocViewer
                                    attachmentId={currentDoc.id}
                                    label={current.label}
                                    mime={currentDoc.mime_type}
                                    expanded={stageExpanded}
                                />
                            ) : (
                                <span className="m-auto px-4 text-center text-xs text-zinc-400">
                                    {lane === "photos"
                                        ? "No photos"
                                        : lane === "wo"
                                          ? "No work order yet"
                                          : "No reports yet — drop them anywhere on this screen"}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => go(1)}
                            disabled={laneDocs.length === 0}
                            aria-label="Next document"
                            className="flex h-9 w-9 flex-none items-center justify-center self-center rounded-full bg-white/10 text-white"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                    {/* Review actions docked on the document — the primary task
                    (read → clear or flag) stays under your eyes instead of
                    tracking to the sidebar. Same handlers as the Review card, so
                    allCleared / auto-advance / the flag dialog all keep working. */}
                    <div className="flex shrink-0 items-center gap-2 border-t border-white/10 px-2.5 py-1.5">
                        {current && currentDoc ? (
                            <>
                                <Button
                                    size="sm"
                                    className="h-7 bg-teal-700 text-[11px] text-white hover:bg-teal-800"
                                    disabled={isCleared(currentDoc) || markReviewed.isPending}
                                    title={
                                        currentDoc.autoReviewed
                                            ? "Auto-cleared — official online-agency report"
                                            : currentDoc.reviewed_at
                                              ? "Already reviewed"
                                              : undefined
                                    }
                                    onClick={() => markReviewed.mutate(currentDoc.id)}
                                >
                                    {markReviewed.isPending ? (
                                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Check className="mr-1 h-3.5 w-3.5" />
                                    )}
                                    {isCleared(currentDoc) ? "Reviewed ✓" : "Reviewed — looks right"}
                                </Button>
                                <button
                                    onClick={() => setFlagOpen(true)}
                                    className="rounded px-2 py-1 text-[11px] font-semibold text-red-200 hover:bg-white/10 hover:text-red-100"
                                >
                                    Something&apos;s wrong…
                                </button>
                            </>
                        ) : (
                            <span className="text-[11px] text-white/45">Drop reports anywhere on this screen</span>
                        )}
                        <span className="ml-auto text-[11px] text-white/45">⤓ whole screen is the drop zone</span>
                    </div>
                </div>

                {/* Verify column — stretches to the same height as the doc stage
                (no dead space beneath); the Documents list takes the slack and
                scrolls, Review + City stay compact. */}
                <div
                    className={cn(
                        "flex w-full flex-none flex-col gap-2 sm:flex-row sm:flex-wrap lg:h-full lg:min-h-0 lg:w-[248px] lg:flex-col",
                        stageExpanded && "lg:hidden",
                    )}
                >
                    <div className="order-1 flex min-w-[220px] flex-1 flex-col rounded-xl border border-border bg-card p-3 lg:min-h-0">
                        <div className="mb-1.5 flex items-baseline justify-between gap-2 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                            <span>Documents</span>
                            {documents.length > 0 && (
                                <span className="font-bold normal-case tracking-normal text-muted-foreground">
                                    {documents.length} staged
                                    {docsToReview > 0 && (
                                        <span className="text-amber-600 dark:text-amber-500">
                                            {" "}
                                            · {docsToReview} to review
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        <div className="min-h-[7.5rem] flex-1 overflow-y-auto text-xs [scrollbar-gutter:stable]">
                            {reportDocs.map((d, i) => {
                                // A stray/superseded per-device report can be removed
                                // from the job (soft-archive); the merged city-filing
                                // report has no remove here (server-scoped too).
                                const canRemove = d.category === "test_report" || d.category === "test_report_official";
                                const active = lane === "reports" && laneDocs[index]?.id === d.id;
                                return (
                                    <div
                                        key={d.id}
                                        className={cn(
                                            "flex w-full items-center gap-1.5 rounded-md px-1 py-1",
                                            active && "bg-primary/10 ring-1 ring-inset ring-primary/30",
                                        )}
                                    >
                                        <button
                                            onClick={() => {
                                                switchLane("reports");
                                                setIndex(i);
                                            }}
                                            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                                        >
                                            {isCleared(d) ? (
                                                <span
                                                    className="flex-none font-extrabold text-emerald-600"
                                                    title={
                                                        d.autoReviewed ? "Auto — from the reporting agency" : "Reviewed"
                                                    }
                                                >
                                                    ✓
                                                </span>
                                            ) : (
                                                <span className="flex-none font-extrabold text-amber-600">●</span>
                                            )}
                                            <span className={cn("truncate", active && "font-bold")}>
                                                {deviceLabel(d)}
                                            </span>
                                            {dupIds.has(d.id) && (
                                                <span
                                                    className="flex-none rounded bg-orange-100 px-1 py-px text-[9px] font-bold text-orange-700 dark:bg-orange-900/50 dark:text-orange-300"
                                                    title="Same file as another report on this job — likely a duplicate you can remove."
                                                >
                                                    dup
                                                </span>
                                            )}
                                            {d.autoReviewed && (
                                                <span className="ml-auto flex-none rounded bg-emerald-100 px-1 py-px text-[9px] font-bold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                                                    auto
                                                </span>
                                            )}
                                        </button>
                                        {canRemove && (
                                            <button
                                                onClick={async () => {
                                                    if (
                                                        await confirmDialog({
                                                            title: "Remove this report from the job?",
                                                            message: `${deviceLabel(d)} stays on file and can be restored — it just won't show on the job or ride the customer packet.`,
                                                            confirmLabel: "Remove",
                                                            destructive: true,
                                                            focusCancel: true,
                                                        })
                                                    )
                                                        removeReport.mutate(d.id);
                                                }}
                                                disabled={removeReport.isPending}
                                                title="Remove this report from the job"
                                                aria-label={`Remove ${deviceLabel(d)}`}
                                                className="flex-none rounded p-0.5 text-muted-foreground hover:text-red-600"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {woDocs.map((d, i) => {
                                // "Appointment N" once 2+ paper WOs are on the
                                // job (Issue #295); a single WO reads as before.
                                const label =
                                    d.category === "work_order_uploaded"
                                        ? uploadedWoCount > 1
                                            ? `Appointment ${(uploadedWoOrder.get(d.id) ?? 0) + 1}`
                                            : "WO — paper"
                                        : "WO — generated";
                                const active = lane === "wo" && laneDocs[index]?.id === d.id;
                                // The generated WO carries the regenerate's own
                                // status line (Issue #1413) — a running one says
                                // how long it has been going instead of turning
                                // red at 30s, and a settled one says how it went.
                                const woStatus =
                                    d.category === "work_order_filled"
                                        ? (regenerateWo.progress ?? regenerateWo.lastOutcome)
                                        : null;
                                return (
                                    <div key={d.id} className="w-full">
                                        <div
                                            className={cn(
                                                "flex w-full items-center gap-1.5 rounded-md px-1 py-1",
                                                active && "bg-primary/10 ring-1 ring-inset ring-primary/30",
                                            )}
                                        >
                                            <button
                                                onClick={() => {
                                                    switchLane("wo");
                                                    setIndex(i);
                                                }}
                                                className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                                            >
                                                {isCleared(d) ? (
                                                    <span className="font-extrabold text-emerald-600">✓</span>
                                                ) : (
                                                    <span className="font-extrabold text-amber-600">●</span>
                                                )}
                                                <span className={cn("truncate", active && "font-bold")}>{label}</span>
                                            </button>
                                            {d.category === "work_order_uploaded" && (
                                                <button
                                                    onClick={async () => {
                                                        if (
                                                            await confirmDialog({
                                                                title: "Remove this work order?",
                                                                message: `${label} stays on file — it just won't ride the customer packet.`,
                                                                confirmLabel: "Remove",
                                                                destructive: true,
                                                                focusCancel: true,
                                                            })
                                                        )
                                                            removeWorkOrder.mutate(d.id);
                                                    }}
                                                    disabled={removeWorkOrder.isPending}
                                                    title="Remove this work order"
                                                    aria-label={`Remove ${label}`}
                                                    className="flex-none rounded p-0.5 text-muted-foreground hover:text-red-600"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {d.category === "work_order_filled" && (
                                                <>
                                                    {woIntakeItemId && (
                                                        <button
                                                            onClick={() =>
                                                                navigate(`/jobs/work-orders/review/${woIntakeItemId}`)
                                                            }
                                                            title="Fix template mapping — adjust where values print on the vendor's form"
                                                            aria-label="Fix template mapping"
                                                            className="flex-none rounded px-1 py-0.5 text-[10.5px] font-medium text-foreground/50 hover:text-foreground"
                                                        >
                                                            Fix
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => void regenerateWo.start()}
                                                        disabled={regenerateWo.isRunning}
                                                        title="Regenerate the completed WO PDF (re-fills on the job's template; nothing sends automatically)"
                                                        aria-label="Regenerate completed work order PDF"
                                                        className="flex-none rounded p-0.5 text-foreground/40 hover:text-foreground disabled:opacity-50"
                                                    >
                                                        <RefreshCw
                                                            className={cn(
                                                                "h-3.5 w-3.5",
                                                                regenerateWo.isRunning && "animate-spin",
                                                            )}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                        <WoRegenerateStatusLine status={woStatus} />
                                    </div>
                                );
                            })}
                            {documents.length === 0 && <p className="py-1 text-muted-foreground">Nothing here yet.</p>}
                        </div>
                        <p className="mt-1.5 flex-none border-t border-dashed border-border pt-1.5 text-[10.5px] text-muted-foreground">
                            Photos moved to Charges/Estimate — review them against the invoice there.
                        </p>
                        {/* WO upload — ADD one, then add another for each visit
                        (Issue #295). Each upload is kept; remove a wrong one
                        with the trash icon above. A single upload can be several
                        photos of one WO — they merge into one PDF. */}
                        <div className="mt-1.5 flex-none">
                            <input
                                ref={woInputRef}
                                type="file"
                                multiple
                                accept="application/pdf,image/*"
                                className="hidden"
                                onChange={(e) => {
                                    const files = Array.from(e.target.files ?? []);
                                    if (files.length > 0) uploadWorkOrder.mutate(files);
                                    e.target.value = "";
                                }}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full text-[11px]"
                                disabled={uploadWorkOrder.isPending}
                                onClick={() => woInputRef.current?.click()}
                            >
                                {uploadWorkOrder.isPending ? (
                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                ) : uploadedWoCount > 0 ? (
                                    <Plus className="mr-1 h-3.5 w-3.5" />
                                ) : (
                                    <Upload className="mr-1 h-3.5 w-3.5" />
                                )}
                                {uploadedWoCount > 0 ? "Add another work order" : "Upload completed work order"}
                            </Button>
                            <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                                {uploadedWoCount > 0
                                    ? `${uploadedWoCount} work order${uploadedWoCount === 1 ? "" : "s"} on this job — add one for each visit. Removing keeps it on file.`
                                    : "Drop the tech's signed work order — PDF or photos. Add another for each visit."}
                            </p>
                        </div>
                    </div>

                    {/* Admin / mapper tools — one slim disclosure (order-5) that
                    demotes the two later-added Corbin-only cards below the real
                    3-card review flow. Collapsed by default; opening reveals the
                    Fix-future-forms mapper (order-6) and the learning digest
                    (order-7) untouched. */}
                    {isAdmin && (
                        <button
                            onClick={() => setAdminToolsOpen((o) => !o)}
                            aria-expanded={adminToolsOpen}
                            className="order-5 flex min-w-[220px] flex-1 items-center justify-between rounded-xl border border-border bg-card px-3 py-2 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/50 hover:text-foreground/75 lg:flex-none"
                        >
                            <span className="flex items-center gap-1.5">
                                <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                                Admin / mapper tools
                            </span>
                            {adminToolsOpen ? (
                                <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                                <ChevronDown className="h-3.5 w-3.5" />
                            )}
                        </button>
                    )}

                    {/* Teach the mapper (Issue #158) — the ADMIN path that corrects
                    the template mapping so ALL FUTURE forms fill right. Distinct
                    from "Edit document", which fixes just this one PDF. Only for
                    GWC-mapped reports (agency PDFs have no mapping); Corbin-only. */}
                    {canFix && adminToolsOpen && (
                        <div className="order-6 min-w-[220px] flex-1 rounded-xl border border-violet-300/70 bg-card p-3 dark:border-violet-800/60 lg:flex-none">
                            <div className="mb-1.5 flex items-center justify-between">
                                <span className="text-[10.5px] font-extrabold uppercase tracking-[.07em] text-violet-700 dark:text-violet-300">
                                    Fix future forms
                                </span>
                                <button
                                    className="text-[11px] font-semibold text-violet-700 dark:text-violet-300"
                                    onClick={() => {
                                        setFixOpen((o) => !o);
                                        setFixFieldId(null);
                                    }}
                                >
                                    {fixOpen ? "Close" : "Open"}
                                </button>
                            </div>
                            {!fixOpen && (
                                <p className="text-[11px] leading-snug text-foreground/55">
                                    To fix just <b>this</b> PDF, use <b>Edit document</b> above. This teaches the
                                    mapping so every future form of this type fills right. Corbin-only.
                                </p>
                            )}
                            {fixOpen && fieldsQuery.isLoading && (
                                <p className="text-[11px] text-muted-foreground">Loading the form&apos;s fields…</p>
                            )}
                            {fixOpen && !fieldsQuery.isLoading && !fixTemplate && (
                                <p className="text-[11px] leading-snug text-foreground/55">
                                    No mapped template resolves for this jurisdiction — nothing to edit here. Use
                                    &quot;Something&apos;s wrong…&quot; to flag the whole document.
                                </p>
                            )}
                            {fixOpen && fixTemplate && !fixField && (
                                <div className="mb-2 rounded-lg border border-violet-300/60 bg-violet-50/40 p-2 dark:border-violet-800/50 dark:bg-violet-950/20">
                                    <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[.06em] text-violet-700 dark:text-violet-300">
                                        Tell it what&apos;s wrong — I&apos;ll figure out the fix
                                    </div>
                                    <textarea
                                        value={askText}
                                        onChange={(e) => setAskText(e.target.value)}
                                        placeholder={'e.g. "this should tick for a double-check valve"'}
                                        className="h-14 w-full rounded-md border border-border bg-background p-2 text-[12px]"
                                    />
                                    <div className="mt-1 flex items-center gap-2">
                                        <Button
                                            size="sm"
                                            className="bg-violet-600 text-white hover:bg-violet-700"
                                            disabled={askText.trim().length < 3 || interpretMut.isPending}
                                            onClick={() => interpretMut.mutate(askText.trim())}
                                        >
                                            {interpretMut.isPending && (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            )}
                                            Ask
                                        </Button>
                                        {(proposal || askError) && (
                                            <button
                                                className="text-[11px] text-foreground/55"
                                                onClick={() => {
                                                    setProposal(null);
                                                    setAskError(null);
                                                }}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                    {askError && (
                                        <p className="mt-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                                            {askError}
                                        </p>
                                    )}
                                    {proposal && (
                                        <div className="mt-2 rounded-md border border-violet-300 bg-card p-2 text-[11.5px] dark:border-violet-800">
                                            <div className="font-semibold text-violet-700 dark:text-violet-300">
                                                Proposed fix
                                            </div>
                                            <p className="mt-0.5 leading-snug">{proposal.proposal.explanation}</p>
                                            <div className="mt-1 rounded bg-muted/40 px-2 py-1 text-[11px]">
                                                <div className="flex justify-between gap-2">
                                                    <span className="text-foreground/55">Field</span>
                                                    <b className="truncate">
                                                        {proposal.field?.label ?? proposal.proposal.fieldId}
                                                    </b>
                                                </div>
                                                <div className="flex justify-between gap-2">
                                                    <span className="flex-none text-foreground/55">Change</span>
                                                    <b className="truncate text-right">
                                                        {describeProposal(proposal.proposal)}
                                                    </b>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                className="mt-1.5 w-full bg-violet-600 text-white hover:bg-violet-700"
                                                disabled={applyingProposal}
                                                onClick={() => applyProposal(proposal)}
                                            >
                                                {applyingProposal && (
                                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                                )}
                                                Apply
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {fixOpen && fixTemplate && !fixField && (
                                <div>
                                    <p className="mb-1 text-[10.5px] text-foreground/55">
                                        {fixTemplate.name} — or tap the field that&apos;s wrong.
                                    </p>
                                    <div className="max-h-52 overflow-y-auto rounded-lg border border-border">
                                        {fixFields
                                            .filter((f) => !f.device_asset_id)
                                            .map((f) => (
                                                <button
                                                    key={f.id}
                                                    onClick={() => selectFixField(f)}
                                                    className="flex w-full items-center gap-1.5 border-b border-border/60 px-2 py-1.5 text-left text-[11.5px] last:border-b-0 hover:bg-violet-50 dark:hover:bg-violet-950/40"
                                                >
                                                    <span className="truncate font-medium">
                                                        {f.label || f.pdf_field_name || "field"}
                                                    </span>
                                                    <span className="ml-auto flex-none rounded bg-violet-100 px-1 py-px text-[9px] font-bold text-violet-700 dark:bg-violet-900/50 dark:text-violet-300">
                                                        {FIELD_TYPE_BADGE[f.field_type] ?? f.field_type}
                                                    </span>
                                                </button>
                                            ))}
                                        {fixFields.length === 0 && (
                                            <p className="px-2 py-1.5 text-[11px] text-muted-foreground">
                                                This template has no mapped fields yet.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {fixOpen && fixField && (
                                <div className="flex flex-col gap-2">
                                    <button
                                        className="self-start text-[11px] text-foreground/55"
                                        onClick={() => setFixFieldId(null)}
                                    >
                                        ← all fields
                                    </button>
                                    <div className="rounded-lg border border-border bg-muted/30 px-2 py-1.5 text-[11px]">
                                        <div className="flex justify-between gap-2">
                                            <span className="text-foreground/55">Field</span>
                                            <b className="truncate">{fixField.label || fixField.pdf_field_name}</b>
                                        </div>
                                        <div className="flex justify-between gap-2">
                                            <span className="text-foreground/55">Type</span>
                                            <b>{FIELD_TYPE_BADGE[fixField.field_type] ?? fixField.field_type}</b>
                                        </div>
                                        {fixField.data_source_path && (
                                            <div className="flex justify-between gap-2">
                                                <span className="text-foreground/55">Source</span>
                                                <b className="truncate">{fixField.data_source_path}</b>
                                            </div>
                                        )}
                                    </div>
                                    {isCheckboxField(fixField) ? (
                                        <div className="flex flex-col gap-2">
                                            <div className="rounded-md bg-muted/40 px-2 py-1.5 text-[11px]">
                                                This form got: <b>{groupLiveValue ?? "— nothing recorded"}</b>
                                                {groupLiveValue == null && (
                                                    <span className="text-amber-700 dark:text-amber-300">
                                                        {" "}
                                                        — that&apos;s a data fix, not a mapping bind.
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] font-extrabold uppercase tracking-[.06em] text-foreground/55">
                                                Which box should be checked?
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                {fixGroup.map((b) => (
                                                    <button
                                                        key={b.id}
                                                        onClick={() => setPickBoxId(b.id)}
                                                        className={cn(
                                                            "flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-[12px]",
                                                            pickBoxId === b.id
                                                                ? "border-violet-500 bg-violet-50 dark:bg-violet-950/40"
                                                                : "border-border",
                                                        )}
                                                    >
                                                        <span
                                                            className={cn(
                                                                "flex h-4 w-4 flex-none items-center justify-center rounded border text-[10px] font-bold",
                                                                pickBoxId === b.id
                                                                    ? "border-violet-600 bg-violet-600 text-white"
                                                                    : b.checked
                                                                      ? "border-emerald-500 bg-emerald-700 text-white"
                                                                      : "border-foreground/30",
                                                            )}
                                                        >
                                                            {pickBoxId === b.id || b.checked ? "✓" : ""}
                                                        </span>
                                                        <span className="truncate">{b.label || b.pdf_field_name}</span>
                                                        {b.checked && pickBoxId !== b.id && (
                                                            <span className="ml-auto flex-none text-[9px] font-bold text-emerald-600">
                                                                checks now
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="flex gap-1.5">
                                                <Button
                                                    size="sm"
                                                    className="flex-1 bg-violet-600 text-white hover:bg-violet-700"
                                                    disabled={
                                                        !pickBoxId || groupLiveValue == null || bindCheckbox.isPending
                                                    }
                                                    onClick={() => pickBoxId && bindCheckbox.mutate(pickBoxId)}
                                                >
                                                    {bindCheckbox.isPending && (
                                                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                                    )}
                                                    Apply — bind &amp; fill this way
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setFixFieldId(null);
                                                        setPickBoxId(null);
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                            {groupLiveValue == null ? (
                                                <p className="text-[10px] text-amber-700 dark:text-amber-300">
                                                    No live value to bind — fix the underlying data, or use
                                                    &quot;Something&apos;s wrong…&quot; to flag it.
                                                </p>
                                            ) : (
                                                <p className="text-[10px] text-muted-foreground">
                                                    Binds &quot;{groupLiveValue}&quot; to the box you pick so it checks
                                                    on this form and every future one of this type. Corbin-only.
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[.06em] text-foreground/55">
                                                    What&apos;s wrong?
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                    {kindOptions(fixField.field_type).map((k) => (
                                                        <button
                                                            key={k.v}
                                                            onClick={() => setFixKind(k.v)}
                                                            className={cn(
                                                                "rounded-full border px-2 py-0.5 text-[11px] font-medium",
                                                                fixKind === k.v
                                                                    ? "border-violet-500 bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200"
                                                                    : "border-border text-foreground/70",
                                                            )}
                                                        >
                                                            {k.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[.06em] text-foreground/55">
                                                    What should change?{" "}
                                                    <span className="text-violet-600">(required)</span>
                                                </div>
                                                <textarea
                                                    value={fixNote}
                                                    onChange={(e) => setFixNote(e.target.value)}
                                                    placeholder={'e.g. "PASS should be checked — passed on retest"'}
                                                    className="h-16 w-full rounded-md border border-border bg-background p-2 text-[12px]"
                                                />
                                            </div>
                                            <div
                                                className={cn(
                                                    "rounded-md px-2 py-1.5 text-[11px]",
                                                    fixRouteType(fixKind) === "mapping"
                                                        ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200"
                                                        : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
                                                )}
                                            >
                                                {fixRouteType(fixKind) === "mapping" ? (
                                                    <>
                                                        → Opens the Form Mapper on <b>{fixTemplate?.name}</b>, field{" "}
                                                        <b>{fixField.label}</b> — note attached.
                                                    </>
                                                ) : fixRouteType(fixKind) === "data" ? (
                                                    <>
                                                        → Saves a data fix to Corbin&apos;s queue (the value lives on
                                                        the record).
                                                    </>
                                                ) : fixRouteType(fixKind) === "signature" ? (
                                                    <>→ Saves a signature fix to Corbin&apos;s queue.</>
                                                ) : (
                                                    <>→ Saves a re-link request to Corbin&apos;s queue.</>
                                                )}
                                            </div>
                                            <div className="flex gap-1.5">
                                                <Button
                                                    size="sm"
                                                    className="flex-1 bg-violet-600 text-white hover:bg-violet-700"
                                                    disabled={fixNote.trim().length < 3 || fileCorrection.isPending}
                                                    onClick={() => fileCorrection.mutate()}
                                                >
                                                    {fileCorrection.isPending && (
                                                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                                    )}
                                                    {fixRouteType(fixKind) === "mapping"
                                                        ? "Save + open mapper"
                                                        : "Save fix"}
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => setFixFieldId(null)}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                    <p className="text-[10px] text-muted-foreground">
                                        Mapping edits are Corbin-only for now.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Per-document field edits now live inside the unified "Edit
                    document" modal (click a field on the page to correct it),
                    alongside the free-form marks — one Save regenerates + bakes. */}

                    {/* Phase C glance — what those edits taught the mapper (admin). */}
                    {isAdmin && adminToolsOpen && <CloseoutLearningDigest className="order-7" />}

                    <div className="order-2 min-w-[220px] flex-1 rounded-xl border border-border bg-card p-3 lg:flex-none">
                        <div className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                            Review
                        </div>
                        {/* Open "Corbin fix" flags — the reason station 1 shows
                        "Report fix — with Corbin" even when every report is
                        reviewed. Clear one here once it's handled (or if it was
                        raised by mistake); no trip to the legacy panel. */}
                        {docFixes.length > 0 && (
                            <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-2 dark:border-amber-900 dark:bg-amber-950/40">
                                <div className="mb-1 text-[11px] font-bold text-amber-800 dark:text-amber-300">
                                    Held for a fix — {docFixes.length} open flag{docFixes.length === 1 ? "" : "s"}
                                </div>
                                <div className="space-y-1.5">
                                    {docFixes.map((f) => (
                                        <div key={f.id} className="rounded bg-white/70 p-1.5 dark:bg-black/20">
                                            <p className="text-[11px] leading-snug text-foreground/80">
                                                {f.title.replace(/^Corbin fix:\s*/i, "")}
                                            </p>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="mt-1 h-7 w-full border-emerald-300 text-[11px] text-emerald-700 dark:border-emerald-900 dark:text-emerald-400"
                                                disabled={resolveFix.isPending}
                                                onClick={() => resolveFix.mutate(f.id)}
                                            >
                                                {resolveFix.isPending ? (
                                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <Check className="mr-1 h-3.5 w-3.5" />
                                                )}
                                                Mark fixed — clears the flag
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col gap-1.5">
                            <Button
                                size="sm"
                                className="w-full bg-teal-700 text-white hover:bg-teal-800"
                                disabled={
                                    !currentDoc || (currentDoc && isCleared(currentDoc)) || markReviewed.isPending
                                }
                                title={
                                    !currentDoc
                                        ? "Open a document first"
                                        : currentDoc.autoReviewed
                                          ? "Auto-cleared — official online-agency report"
                                          : currentDoc.reviewed_at
                                            ? "Already reviewed"
                                            : undefined
                                }
                                onClick={() => currentDoc && markReviewed.mutate(currentDoc.id)}
                            >
                                {markReviewed.isPending ? (
                                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Check className="mr-1 h-3.5 w-3.5" />
                                )}
                                Reviewed — looks right
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full border-red-200 text-red-700 dark:border-red-900 dark:text-red-400"
                                onClick={() => setFlagOpen(true)}
                            >
                                Something's wrong… (why required)
                            </Button>
                        </div>
                        {currentDoc?.autoReviewed ? (
                            <p className="mt-1.5 text-[11px] text-emerald-700 dark:text-emerald-300">
                                Auto-cleared — official report from the online reporting agency, matched to a tested
                                device.
                            </p>
                        ) : (
                            currentDoc?.reviewed_at && (
                                <p className="mt-1.5 text-[11px] text-muted-foreground">
                                    Reviewed{currentDoc.reviewed_by_name ? ` by ${currentDoc.reviewed_by_name}` : ""}.
                                </p>
                            )
                        )}
                        {currentDoc && dupIds.has(currentDoc.id) && (
                            <p className="mt-1.5 rounded bg-orange-50 px-2 py-1 text-[11px] text-orange-700 dark:bg-orange-950/40 dark:text-orange-300">
                                This looks like a duplicate of another report on this job (same file). Review just one —
                                the extra can be archived.
                            </p>
                        )}
                        {/* Forward motion — jumps to the next station that actually needs
                        work (skips done / not-needed), and says so plainly. */}
                        {allCleared && (
                            <p className="mt-2 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                                All reports reviewed ✓
                            </p>
                        )}
                        <Button
                            size="sm"
                            variant={allCleared ? "default" : "outline"}
                            className={cn(
                                "mt-1 w-full",
                                allCleared && "bg-emerald-700 text-white hover:bg-emerald-800",
                            )}
                            onClick={() => onStation(nextStation)}
                        >
                            Next — {nextStationTitle} <ChevronRight className="ml-1 h-3.5 w-3.5" />
                        </Button>
                        {!allCleared && documents.length > 0 && (
                            <p className="mt-1 text-[10.5px] text-muted-foreground">
                                Some documents still need a review — you can move on, but the job stays flagged until
                                they&apos;re cleared.
                            </p>
                        )}
                    </div>

                    <div className="order-3 min-w-[220px] flex-1 rounded-xl border border-border bg-card p-3 lg:flex-none">
                        <div className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                            City {city ? `— ${city.portal_only ? "portal" : "paper"} jurisdiction` : ""}
                        </div>
                        {city ? (
                            <div className="overflow-hidden rounded-lg border border-border text-xs">
                                <div className="border-b border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-bold">
                                    SEND PREVIEW — {city.name}
                                </div>
                                {/* M7 (#834): "no email on file" and "0 reports" are filing
                                    blockers — say so with warn styling and point at the fix
                                    instead of rendering them like normal preview lines. */}
                                <div
                                    className={cn(
                                        "flex justify-between gap-2 px-2.5 py-1.5",
                                        !city.submission_email &&
                                            !city.portal_only &&
                                            "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
                                    )}
                                >
                                    <span className="truncate">
                                        To:{" "}
                                        {city.submission_email ||
                                            (city.portal_only
                                                ? "filed on the portal, not emailed"
                                                : "⚠ no email on file")}
                                    </span>
                                    <span className="whitespace-nowrap text-[11px] font-semibold text-teal-700">
                                        one-time · new default
                                    </span>
                                </div>
                                {!city.submission_email && !city.portal_only && (
                                    <div className="border-t border-amber-200/60 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 dark:border-amber-800/40 dark:bg-amber-950/40 dark:text-amber-300">
                                        The filing can't be emailed until the city has an address. Save it in the yellow
                                        box at the top and it's used for every {city.name} job.
                                    </div>
                                )}
                                {(() => {
                                    // Count what the filing will actually carry: cleared =
                                    // human-reviewed OR auto-cleared official reports — the
                                    // same predicate as "All reports reviewed ✓" above.
                                    const clearedCount = reportDocs.filter(
                                        (d) => d.reviewed_at || d.autoReviewed,
                                    ).length;
                                    return (
                                        <div
                                            className={cn(
                                                "border-t border-border/60 px-2.5 py-1.5",
                                                clearedCount === 0 &&
                                                    "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
                                            )}
                                        >
                                            Attaching: {clearedCount} cleared report{clearedCount === 1 ? "" : "s"}
                                            {clearedCount === 0 && (
                                                <span className="block text-[11px]">
                                                    ⚠ Nothing to file yet — review the reports on this station first.
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        ) : (
                            <p className="text-xs text-muted-foreground">No jurisdiction on file.</p>
                        )}
                        <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                            Previewed here. After the packet goes out, this city's reports wait in City Filing's Ready
                            tray until someone presses Send. Nothing goes to the city or the customer from here.
                        </p>
                    </div>

                    {/* Report input — office puts the online portal entry on a
                        tester (starts the 36h-from-flag nudge) or parks it on a
                        city-registration hold. Portal jurisdictions only; filing
                        is licensed so it never routes to the office. */}
                    {city?.portal_only && (
                        <div className="order-4 min-w-[220px] flex-1 rounded-xl border border-border bg-card p-3 lg:flex-none">
                            <div className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                                Report input
                            </div>
                            <p className="text-[11px] leading-relaxed text-foreground/60">
                                Who&apos;s entering this in the {city.name} portal? Filing is licensed — it can only go
                                to a tester, never the office.
                            </p>
                            <ReportInputFlag jobId={jobId} isPortal={city.portal_only} />
                        </div>
                    )}
                </div>
            </div>

            {/* "Something's wrong…" — the why-note is REQUIRED by design. */}
            <Dialog open={flagOpen} onClose={() => setFlagOpen(false)} title="Something's wrong — tell Corbin why">
                <div className="space-y-3">
                    <p className="text-sm text-foreground/80">
                        {currentDoc ? (
                            <>
                                Flagging <b>{currentDoc.file_name}</b> to Corbin's queue.
                            </>
                        ) : (
                            "Flagging this job's documents to Corbin's queue."
                        )}{" "}
                        A brief why-note is required.
                    </p>
                    <textarea
                        value={flagNote}
                        onChange={(e) => setFlagNote(e.target.value)}
                        placeholder={'e.g. "date printed wrong — shows 2025"'}
                        className="h-20 w-full rounded-md border border-border bg-background p-2 text-sm"
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setFlagOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="bg-violet-600 text-white hover:bg-violet-700"
                            disabled={flagNote.trim().length < 3 || flagDoc.isPending}
                            onClick={() => flagDoc.mutate()}
                        >
                            {flagDoc.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Send to Corbin
                        </Button>
                    </div>
                </div>
            </Dialog>

            {markupOpen && currentDoc && (
                <CloseoutPdfMarkup
                    jobId={jobId}
                    attachmentId={currentDoc.id}
                    pdfUrl={attachmentDownloadUrl(currentDoc.id, { disposition: "inline" })}
                    title={currentDoc.file_name ?? current?.label}
                    fields={fixFields}
                    onClose={() => setMarkupOpen(false)}
                    onSaved={() => {
                        qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
                        refreshCloseOutQueue(qc, { jobId });
                        qc.invalidateQueries({ queryKey: ["close-out-doc-fields", jobId, currentDoc.id] });
                    }}
                />
            )}
        </div>
    );
}

// The tech's field findings + photos, surfaced on Charges/Estimate so the
// office can check what the tech reported is covered by what's being billed.
function docCategoryLabel(category: string): string {
    if (category === "work_order_uploaded") return "Work order";
    if (category === "test_report_official") return "Official report";
    if (category === "test_report") return "Test report";
    return "Document";
}

/** One photo, big, with its device label and a Save button (saves under "SN-40613G Tag 2026-10-26.jpg"). */
function PhotoWithSave({ id, caption }: { id: string; caption: string }) {
    return (
        <div className="space-y-2">
            <img
                src={attachmentDownloadUrl(id, { disposition: "inline" })}
                alt={caption}
                className="max-h-[70vh] w-full object-contain"
            />
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="min-w-0 truncate font-semibold">{caption}</span>
                <a
                    href={attachmentDownloadUrl(id, { disposition: "attachment" })}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 font-semibold hover:bg-muted"
                >
                    <Download className="h-3 w-3" /> Save photo
                </a>
            </div>
        </div>
    );
}

function TechPhotoPanel({
    techNotes,
    photos,
    documents = [],
}: {
    techNotes: TechRemarkGroup[];
    photos: CloseOutPhoto[];
    documents?: CloseOutDocument[];
}) {
    const [lightbox, setLightbox] = useState<string | null>(null);
    const [previewDoc, setPreviewDoc] = useState<CloseOutDocument | null>(null);
    if (techNotes.length === 0 && photos.length === 0 && documents.length === 0) return null;
    return (
        <div className="rounded-xl border border-border bg-card lg:col-span-2">
            <div className="border-b border-border px-3.5 py-2 text-[13px] font-bold">
                What the tech reported — verify it&apos;s covered by the charges
            </div>
            <div className="grid gap-3 p-3 md:grid-cols-2">
                <div className="space-y-2">
                    <div className="text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                        Field notes
                    </div>
                    {techNotes.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No tech notes on this job.</p>
                    ) : (
                        techNotes.map((g) => (
                            <div key={g.asset_id ?? g.device} className="rounded-lg border border-border p-2 text-xs">
                                <div className="mb-1 flex items-center gap-1.5 font-bold">
                                    {g.device}
                                    {g.overall_result && (
                                        <span
                                            className={cn(
                                                "rounded-full px-1.5 text-[9px] font-extrabold",
                                                RESULT_PILL[testStateTone(g.overall_result)],
                                            )}
                                        >
                                            {testStateLabel(g.overall_result).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <ul className="list-disc space-y-0.5 pl-4 text-foreground/75">
                                    {g.remarks.map((r, i) => (
                                        <li key={i}>{r}</li>
                                    ))}
                                </ul>
                            </div>
                        ))
                    )}
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                        <Camera className="h-3 w-3" /> Photos ({photos.length})
                    </div>
                    {photos.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No photos on this job.</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {photos.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setLightbox(p.id)}
                                    className="group w-28 text-left"
                                    title={photoCaption(p)}
                                >
                                    <span className="block h-20 w-28 overflow-hidden rounded-lg border border-border">
                                        <img
                                            src={attachmentDownloadUrl(p.id, { variant: "thumb" })}
                                            alt={photoCaption(p)}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                        />
                                    </span>
                                    <span className="mt-0.5 line-clamp-2 block text-[10px] leading-tight text-foreground/75">
                                        {photoCaption(p)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {documents.length > 0 && (
                <div className="border-t border-border px-3 py-3">
                    <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                        <Eye className="h-3 w-3" /> Documents on this job — click to preview
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {documents.map((d) => (
                            <button
                                key={d.id}
                                onClick={() => setPreviewDoc(d)}
                                className="group flex max-w-[240px] items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-left text-xs hover:border-foreground/40 hover:bg-muted"
                                title={d.file_name || docCategoryLabel(d.category)}
                            >
                                <Printer className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
                                <span className="min-w-0">
                                    <span className="block truncate font-semibold">
                                        {d.file_name || docCategoryLabel(d.category)}
                                    </span>
                                    <span className="block text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                                        {docCategoryLabel(d.category)}
                                        {d.autoReviewed ? " · auto — from the reporting agency" : ""}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <Dialog
                open={!!lightbox}
                onClose={() => setLightbox(null)}
                title={photoCaption(photos.find((p) => p.id === lightbox) ?? {})}
            >
                {lightbox && (
                    <PhotoWithSave id={lightbox} caption={photoCaption(photos.find((p) => p.id === lightbox) ?? {})} />
                )}
            </Dialog>
            <AttachmentPreviewDialog
                open={!!previewDoc}
                onClose={() => setPreviewDoc(null)}
                attachmentId={previewDoc?.id ?? null}
                fileName={previewDoc?.file_name ?? null}
            />
        </div>
    );
}

// ═══ STATION 2 — CHARGES ════════════════════════════════════════════════

function StationCharges({ jobId, onStation }: { jobId: string; onStation: (n: number) => void }) {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { isAdmin } = useAuth();
    const charges = useQuery({
        queryKey: ["close-out-charges", jobId],
        queryFn: () =>
            api.get<{
                work: ChargesWorkRow[];
                invoices: ChargesInvoice[];
                lines: ChargesLine[];
                techNotes: TechRemarkGroup[];
                photos: CloseOutPhoto[];
                documents: CloseOutDocument[];
                customerId: string | null;
                rateCardActive: boolean;
                reportsWithoutTests?: ReportsWithoutTests;
            }>(`/api/close-out/jobs/${jobId}/charges`),
    });
    const work = charges.data?.work ?? [];
    const invoices = charges.data?.invoices ?? [];
    const lines = charges.data?.lines ?? [];
    const techNotes = charges.data?.techNotes ?? [];
    const photos = charges.data?.photos ?? [];
    const documents = charges.data?.documents ?? [];
    const customerId = charges.data?.customerId ?? null;
    const rateCardActive = charges.data?.rateCardActive ?? false;
    // Close Out guard (11/2): reports but 0 tests is never "$0, nothing to bill".
    const reportsNoTests = charges.data?.reportsWithoutTests?.blocked ? charges.data.reportsWithoutTests : null;

    // Hover pairing: hovering a work row lights its invoice lines and back.
    const [hoverAsset, setHoverAsset] = useState<string | null>(null);
    const billedAssets = useMemo(() => new Set(lines.map((l) => l.asset_id).filter(Boolean)), [lines]);

    const [previewInvoiceId, setPreviewInvoiceId] = useState<string | null>(null);
    // "Don't bill" a surprise (tech-added) device — the reason is optional but
    // audited; declining voids the tech's provisional points and drops the
    // device from billing.
    const [declineAsset, setDeclineAsset] = useState<ChargesWorkRow | null>(null);
    const [declineReason, setDeclineReason] = useState("");

    // Rule on a tech-added device. "bill it" (approved) pulls it onto the draft
    // invoice at the right price and reinstates the tech's points; "don't bill"
    // (declined) drops it and voids the points. Both actually clear the amber
    // unbilled warning — unlike the old no-op "No charge" button.
    const deviceDecision = useMutation({
        mutationFn: (vars: { assetId: string; decision: "approved" | "declined"; reason?: string }) =>
            api.post<{ billing?: { kept?: boolean } | null }>(`/api/close-out/jobs/${jobId}/device-decision`, vars),
        onSuccess: (r, vars) => {
            toast.success(
                vars.decision === "approved"
                    ? r?.billing?.kept
                        ? "Device OK'd. It stays on the invoice as priced."
                        : "Device billed — added to the invoice."
                    : "Device left off — no charge recorded (audited).",
            );
            setDeclineAsset(null);
            setDeclineReason("");
            qc.invalidateQueries({ queryKey: ["close-out-charges", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't record the device decision."),
    });

    // Start an invoice from a nothing job — the system couldn't auto-build one
    // (e.g. every device was held), so give the office a way to create the
    // draft and then add lines inline. Reuses the standard invoice-create API.
    const createInvoice = useMutation({
        mutationFn: () =>
            api.post<{ invoice: { id: string } }>(`/api/invoices`, {
                customerId,
                jobId,
                status: "draft",
            }),
        onSuccess: () => {
            toast.success("Invoice started — add lines from the pricebook.");
            qc.invalidateQueries({ queryKey: ["close-out-charges", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't start an invoice."),
    });

    // "Add estimate" — most jobs never need one, so the Estimate station stays
    // hidden. When a job DOES need a repair quote the office can add a blank
    // draft right here; the server is find-or-create (a repeat click won't
    // duplicate). On success we reveal + jump to the Estimate station.
    const addEstimate = useMutation({
        mutationFn: () => api.post<{ estimate: { id: string } }>(`/api/close-out/jobs/${jobId}/estimate`, {}),
        onSuccess: () => {
            toast.success("Estimate added — price it and send for approval.");
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["close-out-estimate", jobId] });
            onStation(3);
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't add an estimate."),
    });

    const primaryInvoice = invoices[0] ?? null;

    // ── Inline invoice editing (no more bouncing to the Money page) ─────────
    // The office edits summary + lines right here. Two truths from the server
    // shape the gate: a paid/void invoice can't take line edits at all
    // (INVOICE_LOCKED), and any collected money means "acknowledge first".
    const refreshCharges = () => qc.invalidateQueries({ queryKey: ["close-out-charges", jobId] });
    const paidNum = Number(primaryInvoice?.amount_paid ?? 0);
    const hasPayment =
        !!primaryInvoice && (paidNum > 0.005 || ["partially_paid", "paid"].includes(primaryInvoice.status));
    const linesLocked = !primaryInvoice || ["paid", "void"].includes(primaryInvoice.status); // server truth

    const [editingInvoice, setEditingInvoice] = useState(false);
    const [ackedPayment, setAckedPayment] = useState(false);
    const [showPaidAck, setShowPaidAck] = useState(false);
    // Pricebook picker open-state, lifted so an unbilled work row can pop it.
    const [pickerOpen, setPickerOpen] = useState(false);
    // Summary (title + notes) editor — seeded from the invoice, saved on demand.
    const [summaryTitle, setSummaryTitle] = useState("");
    const [summaryNotes, setSummaryNotes] = useState("");
    useEffect(() => {
        setSummaryTitle(primaryInvoice?.title ?? "");
        setSummaryNotes(primaryInvoice?.notes ?? "");
    }, [primaryInvoice?.id, primaryInvoice?.title, primaryInvoice?.notes]);

    const beginEdit = () => {
        if (editingInvoice) {
            setEditingInvoice(false);
            return;
        }
        if (hasPayment && !ackedPayment) {
            setShowPaidAck(true);
            return;
        }
        setEditingInvoice(true);
    };

    // ── Finalize (draft → open, NO email) ──────────────────────────────────
    // The office's way out of the draft once the charges are reviewed: flips
    // the invoice to `open` (issue-time tax snapshot, net-terms due date,
    // pay-link token — same side effects as the send flow) WITHOUT emailing the
    // customer, so the Payment station unlocks and the card can be charged.
    // Charging stays a separate, explicit click on Payment. A 409
    // TAX_STATUS_UNKNOWN swaps in the amber taxable/exempt set-and-continue
    // interstitial, mirroring ChargeCardPanel / SendCustomerDialog.
    const [taxPrompt, setTaxPrompt] = useState<{ customerId: string } | null>(null);
    const [taxResolving, setTaxResolving] = useState<"taxable" | "exempt" | null>(null);
    // Out-of-scope tests (a device tested that wasn't on the job's asset list)
    // raise a "Verify before invoicing:" gate that finalize/send 409s on
    // (REVIEW_REQUIRED). Office Review cleared it on Approve; closeout had no
    // equivalent, so Finalize dead-ended "Approve them in Office Review." Resolve
    // it here with an explicit sign-off, then retry finalize.
    //
    // The same 409 also covers a second hold: tech-added devices the office
    // hasn't OK'd. The server says which one (`gate`) and names the devices,
    // and "Approve & finalize" now clears both. If finalize STILL comes back
    // held right after an approve, show the server's plain message instead of
    // the same prompt again — that loop is what stranded Park Lane
    // Periodontist (invoice #102678706, 10/28).
    const [outOfScopePrompt, setOutOfScopePrompt] = useState<ReviewPrompt | null>(null);
    const [approvingOutOfScope, setApprovingOutOfScope] = useState(false);
    const finalizeAfterApproveRef = useRef(false);
    const finalize = useMutation({
        mutationFn: (invoiceId: string) =>
            api.post<{ invoice: { status: string } }>(`/api/invoices/${invoiceId}/finalize`, {}),
        onSettled: () => {
            finalizeAfterApproveRef.current = false;
        },
        onSuccess: () => {
            setTaxPrompt(null);
            setOutOfScopePrompt(null);
            toast.success("Invoice finalized — it's now open. No email was sent.");
            qc.invalidateQueries({ queryKey: ["close-out-charges", jobId] });
            qc.invalidateQueries({ queryKey: ["close-out-payment", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => {
            const body = e instanceof ApiError ? (e.body as Record<string, unknown> | null) : null;
            if (e instanceof ApiError && e.status === 409 && body?.error === "TAX_STATUS_UNKNOWN") {
                const cid = typeof body.customerId === "string" ? body.customerId : customerId;
                if (cid) {
                    setTaxPrompt({ customerId: cid });
                    return;
                }
            }
            // Out-of-scope tests pending office sign-off — offer to approve them
            // here instead of dead-ending with "Approve them in Office Review."
            if (
                e instanceof ApiError &&
                e.status === 409 &&
                body?.error === "REVIEW_REQUIRED" &&
                !finalizeAfterApproveRef.current
            ) {
                setOutOfScopePrompt(reviewPromptFromBody(body));
                return;
            }
            setOutOfScopePrompt(null);
            toast.error(
                body && typeof body.message === "string" ? body.message : "The invoice could not be finalized.",
            );
        },
    });
    // One-click set-and-continue from the amber interstitial: persist the
    // customer's tax status (the server re-taxes their draft docs), then retry.
    const resolveTaxAndFinalize = async (status: "taxable" | "exempt") => {
        if (!taxPrompt || !primaryInvoice) return;
        setTaxResolving(status);
        try {
            await api.patch(`/api/customers/${taxPrompt.customerId}`, { taxStatus: status });
            setTaxPrompt(null);
            finalize.mutate(primaryInvoice.id);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not update the customer's tax status.");
        } finally {
            setTaxResolving(null);
        }
    };
    // Sign off on the out-of-scope tests (mirrors office-review Approve, which
    // cleared the same gate), then retry finalize — one flow from the prompt.
    const approveOutOfScopeAndFinalize = async () => {
        if (!primaryInvoice) return;
        setApprovingOutOfScope(true);
        try {
            await api.post(`/api/close-out/jobs/${jobId}/approve-out-of-scope`, {});
            setOutOfScopePrompt(null);
            qc.invalidateQueries({ queryKey: ["close-out-charges", jobId] });
            finalizeAfterApproveRef.current = true;
            finalize.mutate(primaryInvoice.id);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not approve. Try again.");
        } finally {
            setApprovingOutOfScope(false);
        }
    };

    const summaryDirty =
        summaryTitle !== (primaryInvoice?.title ?? "") || summaryNotes !== (primaryInvoice?.notes ?? "");
    const saveSummary = useMutation({
        mutationFn: (invoiceId: string) =>
            api.patch(`/api/invoices/${invoiceId}`, {
                title: summaryTitle.trim() || null,
                notes: summaryNotes.trim() || null,
            }),
        onSuccess: () => {
            toast.success("Invoice summary saved.");
            refreshCharges();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save the summary."),
    });

    // ── "No invoice needed" rip cord ────────────────────────────────────────
    // A job with nothing to bill auto-builds a $0 draft invoice, which then
    // blocks the send (approve-and-send 422s a $0 invoice). Voiding it is the
    // escape hatch: the job flows straight to send-off with no bill. Only shown
    // for a $0 draft with no payment; the server enforces the same.
    const zeroDraft =
        !!primaryInvoice &&
        primaryInvoice.status === "draft" &&
        Number(primaryInvoice.total_amount) <= 0.005 &&
        Number(primaryInvoice.amount_paid ?? 0) <= 0.005;
    const skipInvoice = useMutation({
        mutationFn: () => api.post(`/api/close-out/jobs/${jobId}/skip-invoice`, {}),
        onSuccess: () => {
            toast.success("Marked no invoice needed — the job can go straight to send-off.");
            qc.invalidateQueries({ queryKey: ["close-out-charges", jobId] });
            qc.invalidateQueries({ queryKey: ["close-out-payment", jobId] });
            qc.invalidateQueries({ queryKey: ["close-out-send-off", jobId] });
            refreshCloseOutQueue(qc, { jobId });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't skip the invoice."),
    });
    const confirmSkipInvoice = async () => {
        const ok = await confirmDialog({
            title: "No invoice needed?",
            message:
                "This voids the $0 invoice on this job so the packet can go out with no bill. You can start a fresh invoice later if you need one.",
            confirmLabel: "No invoice needed",
            destructive: true,
            focusCancel: true,
        });
        if (ok) skipInvoice.mutate();
    };

    return (
        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
            {/* Work rows — one per tested device. */}
            <div className="rounded-xl border border-border bg-card">
                <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-3.5 py-2">
                    <span className="text-[13px] font-bold">Work done — one row per device</span>
                    {/* Repairs are quoted, not invoiced. This button is ALWAYS
                        here — even if no failed test pre-decided a quote — so the
                        office can build one on the spot. Clicking it turns on the
                        Estimate step and attaches the quote to this closeout. */}
                    <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto border-violet-400/70 text-violet-700 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-300 dark:hover:bg-violet-950/40"
                        title="Build a repair estimate for this job — turns on the Estimate step and attaches the quote to this closeout."
                        disabled={addEstimate.isPending}
                        onClick={() => addEstimate.mutate()}
                    >
                        {addEstimate.isPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Plus className="mr-1 h-3.5 w-3.5" />
                        )}
                        Add estimate
                    </Button>
                </div>
                <div className="p-2">
                    {work.map((w) => {
                        const unbilled = !billedAssets.has(w.asset_id);
                        // A surprise device the tech found that wasn't on the
                        // original work order. Billing it approves it; "don't
                        // bill" declines it. Office-scheduled devices just warn.
                        const techAdded = w.added_by === "tech";
                        const declined = w.office_review_decision === "denied";
                        // A tech-added device nobody has OK'd holds Finalize and
                        // Send. Its buttons used to show only while it was
                        // unbilled, so one already priced on the invoice could
                        // never be OK'd (Park Lane Periodontist, 10/28). Now
                        // a pending one always shows its chip and buttons.
                        const waitingOnOk = techAdded && w.office_review_decision === "pending";
                        return (
                            <div
                                key={w.asset_id}
                                onMouseEnter={() => setHoverAsset(w.asset_id)}
                                onMouseLeave={() => setHoverAsset(null)}
                                className={cn(
                                    "mb-1 flex flex-wrap items-center gap-2 rounded-lg border px-2.5 py-2 text-xs transition-colors",
                                    hoverAsset === w.asset_id
                                        ? "border-teal-600 bg-yellow-50 dark:bg-yellow-900/20"
                                        : unbilled
                                          ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20"
                                          : "border-border",
                                )}
                            >
                                <span className="font-bold">{deviceLabel(w)}</span>
                                <span
                                    className={cn(
                                        "rounded-full px-1.5 text-[10px] font-extrabold",
                                        RESULT_PILL[testStateTone(w.overall_result)],
                                    )}
                                >
                                    {testStateLabel(w.overall_result).toUpperCase()}
                                </span>
                                <span className="text-muted-foreground">
                                    report {w.report_reviewed ? "reviewed ✓" : "not reviewed"}
                                </span>
                                {techAdded && (
                                    <span
                                        className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                                        title="This device wasn't on the original work order — the tech added it in the field."
                                    >
                                        tech-added
                                    </span>
                                )}
                                {waitingOnOk && (
                                    <span
                                        data-testid="closeout-device-waiting-ok"
                                        className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                                        title="The tech added this device. It holds the invoice until you OK it or leave it off."
                                    >
                                        Waiting on your OK
                                    </span>
                                )}
                                {waitingOnOk && !unbilled && (
                                    /* Already priced on the invoice but never OK'd —
                                       Approve keeps the lines as they are. */
                                    <span className="ml-auto flex flex-wrap items-center gap-1.5">
                                        <Button
                                            size="sm"
                                            data-testid="closeout-device-approve"
                                            className="h-6 bg-teal-700 px-2 text-[11px] text-white hover:bg-teal-800"
                                            disabled={deviceDecision.isPending}
                                            title="OK this device. It stays on the invoice as priced."
                                            onClick={() =>
                                                deviceDecision.mutate({
                                                    assetId: w.asset_id,
                                                    decision: "approved",
                                                })
                                            }
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-2 text-[11px]"
                                            disabled={deviceDecision.isPending}
                                            onClick={() => setDeclineAsset(w)}
                                        >
                                            Don&apos;t bill
                                        </Button>
                                    </span>
                                )}
                                {unbilled && (
                                    <span className="ml-auto flex flex-wrap items-center gap-1.5">
                                        {declined ? (
                                            /* Already declined — left off the invoice. Offer a reversal. */
                                            <>
                                                <span className="text-muted-foreground">declined — not billed</span>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-6 px-2 text-[11px]"
                                                    disabled={deviceDecision.isPending}
                                                    onClick={() =>
                                                        deviceDecision.mutate({
                                                            assetId: w.asset_id,
                                                            decision: "approved",
                                                        })
                                                    }
                                                >
                                                    Bill it after all
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="flex items-center gap-1 font-bold text-amber-700 dark:text-amber-400">
                                                    <AlertTriangle className="h-3 w-3" /> unbilled
                                                </span>
                                                {techAdded ? (
                                                    /* Surprise device — inline editing IS the ruling:
                                                       bill it (approve → priced onto the invoice) or
                                                       don't (decline → left off, points voided). */
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            className="h-6 bg-teal-700 px-2 text-[11px] text-white hover:bg-teal-800"
                                                            disabled={deviceDecision.isPending}
                                                            title="Add this device to the invoice at the right price"
                                                            onClick={() =>
                                                                deviceDecision.mutate({
                                                                    assetId: w.asset_id,
                                                                    decision: "approved",
                                                                })
                                                            }
                                                        >
                                                            Bill it
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-6 px-2 text-[11px]"
                                                            disabled={deviceDecision.isPending}
                                                            onClick={() => setDeclineAsset(w)}
                                                        >
                                                            Don&apos;t bill
                                                        </Button>
                                                    </>
                                                ) : primaryInvoice && !linesLocked ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-6 px-2 text-[11px]"
                                                        title="Add a line from the pricebook, right here"
                                                        onClick={() => {
                                                            setPickerOpen(true);
                                                            document
                                                                .getElementById("closeout-invoice-picker")
                                                                ?.scrollIntoView({
                                                                    behavior: "smooth",
                                                                    block: "center",
                                                                });
                                                        }}
                                                    >
                                                        Add line from pricebook
                                                    </Button>
                                                ) : !primaryInvoice ? (
                                                    /* Nothing-job: the system never built an invoice.
                                                       Start one here, then add lines inline. */
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-6 px-2 text-[11px]"
                                                        disabled={!customerId || createInvoice.isPending}
                                                        title="Create an invoice for this job, then add lines"
                                                        onClick={() => createInvoice.mutate()}
                                                    >
                                                        {createInvoice.isPending && (
                                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                        )}
                                                        Start an invoice
                                                    </Button>
                                                ) : null}
                                            </>
                                        )}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                    {work.length === 0 && !charges.isLoading && (
                        <p className="p-2 text-xs text-muted-foreground">No tested devices on this job.</p>
                    )}
                </div>
            </div>

            {/* Invoice lines. */}
            <div className="rounded-xl border border-border bg-card">
                <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-3.5 py-2">
                    <span className="text-[13px] font-bold">
                        Invoice {primaryInvoice?.invoice_number ? `#${primaryInvoice.invoice_number}` : ""}{" "}
                        {primaryInvoice ? `— $${Number(primaryInvoice.total_amount).toFixed(2)}` : ""}
                    </span>
                    <span className="ml-auto flex gap-1">
                        {!primaryInvoice ? (
                            /* Nothing-job escape hatch: the system never built an
                               invoice (every device held, or an intake the system
                               couldn't handle). Create the draft here, then add
                               lines inline. */
                            <Button
                                size="sm"
                                className="h-6 bg-teal-700 px-2 text-[11px] text-white hover:bg-teal-800"
                                disabled={!customerId || createInvoice.isPending}
                                onClick={() => createInvoice.mutate()}
                            >
                                {createInvoice.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                <Plus className="mr-1 h-3 w-3" /> Start an invoice
                            </Button>
                        ) : (
                            <>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-2 text-[11px]"
                                    onClick={() => setPreviewInvoiceId(primaryInvoice.id)}
                                >
                                    <Eye className="mr-1 h-3 w-3" /> Preview
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-2 text-[11px]"
                                    onClick={() => setPreviewInvoiceId(primaryInvoice.id)}
                                >
                                    <Printer className="mr-1 h-3 w-3" /> Print
                                </Button>
                                <Button
                                    size="sm"
                                    variant={editingInvoice ? "default" : "outline"}
                                    className="h-6 px-2 text-[11px]"
                                    onClick={beginEdit}
                                >
                                    {editingInvoice ? "Done editing" : "Edit inline"}
                                </Button>
                            </>
                        )}
                    </span>
                </div>
                <div className="p-2">
                    {/* Summary (title + notes) — read-only unless editing. */}
                    {editingInvoice ? (
                        <div className="mb-2 rounded-lg border border-border bg-background/40 p-2">
                            <div className="mb-1 text-[10.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
                                Summary
                            </div>
                            <Input
                                className="mb-1.5 h-7 text-[12px]"
                                value={summaryTitle}
                                onChange={(e) => setSummaryTitle(e.target.value)}
                                placeholder="Title (optional)"
                            />
                            <Textarea
                                className="min-h-[52px] text-[12px]"
                                value={summaryNotes}
                                onChange={(e) => setSummaryNotes(e.target.value)}
                                placeholder="Notes shown on the invoice (optional)"
                            />
                            <div className="mt-1.5 flex justify-end">
                                <Button
                                    size="sm"
                                    className="h-7"
                                    variant={summaryDirty ? "default" : "outline"}
                                    disabled={!primaryInvoice || !summaryDirty || saveSummary.isPending}
                                    onClick={() => primaryInvoice && saveSummary.mutate(primaryInvoice.id)}
                                >
                                    {saveSummary.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                                    Save summary
                                </Button>
                            </div>
                        </div>
                    ) : (
                        (primaryInvoice?.title || primaryInvoice?.notes) && (
                            <div className="mb-2 rounded-lg border border-border/70 bg-background/30 px-2.5 py-1.5 text-xs">
                                {primaryInvoice?.title && <div className="font-semibold">{primaryInvoice.title}</div>}
                                {primaryInvoice?.notes && (
                                    <div className="whitespace-pre-wrap text-foreground/70">{primaryInvoice.notes}</div>
                                )}
                            </div>
                        )
                    )}

                    {/* Paid-in-full: lines are server-locked. Be honest — point
                        at Reopen (which reverses the payment) rather than pretend. */}
                    {editingInvoice && linesLocked && primaryInvoice && (
                        <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                            <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <div>
                                This invoice is paid in full, so its lines are locked. To change a line, open the
                                invoice and <b>Reopen</b> it — that reverses the recorded payment first.
                                <button
                                    className="ml-1 font-semibold underline"
                                    onClick={() => navigate(`/invoices/${primaryInvoice.id}`)}
                                >
                                    Open invoice
                                </button>
                            </div>
                        </div>
                    )}

                    {lines.map((l) =>
                        editingInvoice && !linesLocked ? (
                            <div
                                key={l.id}
                                onMouseEnter={() => l.asset_id && setHoverAsset(l.asset_id)}
                                onMouseLeave={() => setHoverAsset(null)}
                                className="mb-1"
                            >
                                <InvoiceLineRow
                                    invoiceId={l.invoice_id}
                                    line={l}
                                    editing
                                    rateCardActive={rateCardActive}
                                    canViewMarkup={isAdmin}
                                    onSaved={refreshCharges}
                                />
                            </div>
                        ) : (
                            <div
                                key={l.id}
                                onMouseEnter={() => l.asset_id && setHoverAsset(l.asset_id)}
                                onMouseLeave={() => setHoverAsset(null)}
                                className={cn(
                                    "mb-1 flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors",
                                    l.asset_id && hoverAsset === l.asset_id
                                        ? "border-teal-600 bg-yellow-50 dark:bg-yellow-900/20"
                                        : "border-border",
                                )}
                            >
                                <span className="min-w-0 flex-1 truncate">{l.description || "(line)"}</span>
                                {l.asset_id === null && (
                                    <span
                                        className="flex items-center gap-1 text-[10px] font-bold text-amber-700 dark:text-amber-400"
                                        title="No work row pairs with this line"
                                    >
                                        <AlertTriangle className="h-3 w-3" /> no work row
                                    </span>
                                )}
                                <span className="whitespace-nowrap text-foreground/60">
                                    {l.qty} × ${l.unit_price.toFixed(2)}
                                </span>
                            </div>
                        ),
                    )}
                    {lines.length === 0 && !charges.isLoading && (
                        <p className="p-2 text-xs text-muted-foreground">No invoice lines yet.</p>
                    )}

                    {/* Lines come from the pricebook — the picker adds a line at the
                        catalog price without leaving Closeout. Hidden once the
                        invoice is paid/void (server-locked against line changes). */}
                    {primaryInvoice && !linesLocked && (
                        <div id="closeout-invoice-picker" className="mt-2 border-t border-dashed border-border pt-2">
                            <PricebookLinePicker
                                invoiceId={primaryInvoice.id}
                                onAdded={refreshCharges}
                                open={pickerOpen}
                                onOpenChange={setPickerOpen}
                            />
                        </div>
                    )}

                    {/* Finalize — the office's path out of draft once the charges
                        are reviewed. Draft→open, NO email; unlocks Payment so the
                        card can be charged (charging stays a separate click). */}
                    {primaryInvoice && primaryInvoice.status === "draft" && (
                        <div className="mt-2 border-t border-border pt-2">
                            {taxPrompt ? (
                                <div
                                    data-testid="closeout-finalize-tax-prompt"
                                    className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-2.5 dark:border-amber-500/50 dark:bg-amber-950/40"
                                >
                                    <p className="flex items-start gap-1.5 text-[12px] font-semibold text-amber-900 dark:text-amber-200">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        Tax status not set — is this customer taxable or exempt?
                                    </p>
                                    <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90">
                                        Picking one updates the customer, recalculates this invoice&apos;s tax, and
                                        finalizes it in one step.
                                    </p>
                                    <div className="flex flex-wrap gap-2 pt-0.5">
                                        <Button
                                            size="sm"
                                            className="h-7 bg-amber-700 px-2 text-[11px] text-white hover:bg-amber-800"
                                            disabled={taxResolving !== null || finalize.isPending}
                                            onClick={() => void resolveTaxAndFinalize("taxable")}
                                        >
                                            {taxResolving === "taxable" && (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            )}
                                            Taxable — apply tax &amp; finalize
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 border-amber-500/60 px-2 text-[11px]"
                                            disabled={taxResolving !== null || finalize.isPending}
                                            onClick={() => void resolveTaxAndFinalize("exempt")}
                                        >
                                            {taxResolving === "exempt" && (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            )}
                                            Tax exempt — finalize as-is
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-[11px]"
                                            disabled={taxResolving !== null || finalize.isPending}
                                            onClick={() => setTaxPrompt(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : outOfScopePrompt ? (
                                <div
                                    data-testid="closeout-finalize-oos-prompt"
                                    className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-2.5 dark:border-amber-500/50 dark:bg-amber-950/40"
                                >
                                    <p className="flex items-start gap-1.5 text-[12px] font-semibold text-amber-900 dark:text-amber-200">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        {outOfScopePrompt.gate === "tech_added_devices"
                                            ? "The tech added devices that weren't on the job. OK to bill them?"
                                            : "Tests were done outside this job's scope. Approve to bill them?"}
                                    </p>
                                    {outOfScopePrompt.gate === "tech_added_devices" ? (
                                        <ul
                                            data-testid="closeout-finalize-review-devices"
                                            className="list-disc space-y-0.5 pl-5 text-[11px] text-amber-800/90 dark:text-amber-300/90"
                                        >
                                            {outOfScopePrompt.devices.map((d) => (
                                                <li key={d}>{d}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90">
                                            {outOfScopePrompt.title
                                                ? outOfScopePrompt.title.replace(/^Verify before invoicing:\s*/i, "")
                                                : "A device was tested that wasn't on the original job."}
                                        </p>
                                    )}
                                    <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90">
                                        Approving says this work is real and OK to bill, and gives the tech credit for
                                        it. To leave a device off instead, press Don&apos;t bill next to it above.
                                    </p>
                                    <div className="flex flex-wrap gap-2 pt-0.5">
                                        <Button
                                            size="sm"
                                            className="h-7 bg-amber-700 px-2 text-[11px] text-white hover:bg-amber-800"
                                            disabled={approvingOutOfScope || finalize.isPending}
                                            onClick={() => void approveOutOfScopeAndFinalize()}
                                        >
                                            {(approvingOutOfScope || finalize.isPending) && (
                                                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                            )}
                                            Approve &amp; finalize
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-[11px]"
                                            disabled={approvingOutOfScope || finalize.isPending}
                                            onClick={() => setOutOfScopePrompt(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : reportsNoTests ? (
                                <ReportsWithoutTestsGuard jobId={jobId} reports={reportsNoTests.reports} />
                            ) : zeroDraft ? (
                                /* Rip cord: a $0 draft has nothing to bill and
                                   would only block the send — offer to skip it. */
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="text-[11px] text-foreground/60">
                                        $0 — nothing to bill. Skip the invoice and send the packet with no charge.
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid="closeout-skip-invoice"
                                        className="h-7 px-2.5 text-[11px] text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/20"
                                        disabled={skipInvoice.isPending}
                                        onClick={confirmSkipInvoice}
                                    >
                                        {skipInvoice.isPending ? (
                                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                                        )}
                                        No invoice needed
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="text-[11px] text-foreground/60">
                                        Charges look right? Finalize opens the invoice (no email) so it can be paid.
                                    </span>
                                    <Button
                                        size="sm"
                                        data-testid="closeout-finalize-invoice"
                                        className="h-7 bg-teal-700 px-2.5 text-[11px] text-white hover:bg-teal-800"
                                        disabled={finalize.isPending}
                                        onClick={() => finalize.mutate(primaryInvoice.id)}
                                    >
                                        {finalize.isPending ? (
                                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <FileCheck2 className="mr-1 h-3.5 w-3.5" />
                                        )}
                                        Finalize invoice
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <TechPhotoPanel techNotes={techNotes} photos={photos} documents={documents} />

            <DocumentPreviewDialog
                open={!!previewInvoiceId}
                onClose={() => setPreviewInvoiceId(null)}
                kind="invoice"
                documentId={previewInvoiceId}
                label={primaryInvoice?.invoice_number ?? null}
                suggestedFilename={primaryInvoice ? `invoice-${primaryInvoice.invoice_number ?? "draft"}.pdf` : null}
            />

            <Dialog
                open={!!declineAsset}
                onClose={() => setDeclineAsset(null)}
                title="Don't bill this device (audited)"
            >
                <div className="space-y-3">
                    <p className="text-sm text-foreground/80">
                        {declineAsset ? deviceLabel(declineAsset) : ""} was added by the tech and isn&apos;t on the
                        original job. Declining takes it off the invoice and voids the tech&apos;s points for it. The
                        reason (optional) is recorded in the audit trail.
                    </p>
                    <textarea
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder={'e.g. "duplicate of the DC already billed" or "not our device"'}
                        className="h-20 w-full rounded-md border border-border bg-background p-2 text-sm"
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setDeclineAsset(null)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={deviceDecision.isPending}
                            onClick={() =>
                                declineAsset &&
                                deviceDecision.mutate({
                                    assetId: declineAsset.asset_id,
                                    decision: "declined",
                                    reason: declineReason.trim() || undefined,
                                })
                            }
                        >
                            {deviceDecision.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Don&apos;t bill it
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Acknowledge before editing an invoice money has landed on. */}
            <Dialog open={showPaidAck} onClose={() => setShowPaidAck(false)} title="Payment already collected">
                <div className="space-y-3">
                    <p className="text-sm text-foreground/80">
                        {paidNum > 0.005
                            ? `${money(paidNum)} has already been collected on this invoice.`
                            : "Payment has already been collected on this invoice."}{" "}
                        Editing it now changes an invoice the customer has paid against.
                        {linesLocked
                            ? " It's paid in full, so the line items are locked — you can still edit the summary here, but changing a line means reopening the invoice (which reverses the payment)."
                            : " Make sure the change is intended."}
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowPaidAck(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setAckedPayment(true);
                                setShowPaidAck(false);
                                setEditingInvoice(true);
                            }}
                        >
                            I understand — edit
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

/**
 * "Card on file ending 4242 · $95.00 · Charge card" — one plain line at
 * Close Out, after the job is done and before the city filing goes out
 * (Book My Test, 10/17). It uses the existing card-on-file charge. A decline
 * reads in plain words with the next step.
 *
 * #1984 one try per card: once this card is declined on the invoice, the
 * Charge card button goes away and the red box offers Send a new card link /
 * Collect on site / Customer says the card is fixed (one more try).
 */
export function ChargeCardStep({
    last4,
    amount,
    invoiceStatus,
    pending,
    decline,
    customerName,
    customerId = null,
    guard = null,
    hold = null,
    onChanged,
    onCharge,
}: {
    last4: string | null;
    amount: number | null;
    invoiceStatus: string;
    pending: boolean;
    decline: { reason: string } | null;
    customerName: string | null;
    customerId?: string | null;
    guard?: CardGuard | null;
    /** Card hold (#2037, step 5): a live hold → "Charge $X from hold". */
    hold?: ChargeHold | null;
    onChanged?: () => void;
    onCharge: () => void;
}) {
    const card = last4 ? `Card on file ending ${last4}` : "Card on file";
    const draft = invoiceStatus === "draft";
    const liveHold = hold?.state === "held";
    // Taking money from a live hold isn't a new try on the card: the bank
    // already said yes. The one-try box still shows for the card itself.
    const held = guard && guard.state !== "clear" && !liveHold ? guard : null;
    const words = holdChargeWords(amount == null ? null : Math.round(amount * 100), hold);
    const money =
        amount == null
            ? null
            : `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
        <div className="mt-3 space-y-2" data-testid="charge-card-step">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-background px-3 py-2 text-sm">
                <span className="font-semibold">{card}</span>
                {hold && <HoldChip state={hold.state === "held" ? "held" : "expired"} amountCents={hold.amountCents} />}
                {money && (
                    <>
                        <span className="text-foreground/50">·</span>
                        <span className="font-semibold tabular-nums">{money}</span>
                    </>
                )}
                <span className="text-foreground/50">·</span>
                {draft ? (
                    <span className="text-foreground/70">Finish the charges first, then charge the card.</span>
                ) : held?.state === "blocked" ? (
                    <span className="text-muted-foreground" data-testid="charge-card-stopped">
                        Not charging this card again
                    </span>
                ) : held?.state === "one_more_try" ? (
                    <span className="text-muted-foreground">One more try below</span>
                ) : (
                    <Button
                        size="sm"
                        className="bg-teal-700 text-white hover:bg-teal-800"
                        disabled={pending}
                        onClick={onCharge}
                        data-testid="charge-card-button"
                    >
                        {pending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                        {words.button ?? "Charge card"}
                    </Button>
                )}
            </div>
            {words.note && !draft && (
                <p
                    className="flex items-center gap-2 text-[12.5px] text-muted-foreground"
                    data-testid="charge-hold-note"
                >
                    {words.note}
                    <CardHoldHowItWorks />
                </p>
            )}
            {held && (
                <DeclinedCardNextSteps
                    guard={held}
                    last4={last4}
                    customerId={customerId}
                    onChanged={onChanged}
                    onTryOnceMore={onCharge}
                    tryPending={pending}
                />
            )}
            {decline && !held && (
                <div
                    className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                    role="alert"
                    data-testid="charge-card-declined"
                >
                    <p className="font-semibold">
                        The {last4 ? `card ending ${last4}` : "card"} was declined. Nothing was charged.
                    </p>
                    <p className="mt-0.5">{decline.reason}</p>
                    <p className="mt-1">
                        What to do: call {customerName ?? "the customer"} for another card and add it below, then press
                        Charge card again. Or email them a payment link.
                    </p>
                </div>
            )}
        </div>
    );
}
